/* Russian UI catalogue.
 *
 * Authored, not translated. Russian is first-class here (FR-58),
 * so these strings use the trade's own vocabulary:
 *
 * The whole vocabulary is the owner's glossary (2026-10-08, QW-2), published
 * to readers at /glossary from the `glossary` group below; `glossary.test.ts`
 * fails on a retired word in any value. The load-bearing entries:
 *
 *   Работа        the central entity — the word already on their Master File
 *                 sheet tab. NOT «кейс», NOT «сделка», NOT «дело». The UX design
 *                 fixes the UI term and overrides the RU brief, which says
 *                 «сделка».
 *                 «перевозка» is the MOVEMENT and the client's word for it
 *                 (mail and the offer say it); the record on screen is
 *                 «Работа» (S-13, issue #71's rule).
 *   Котировка     the RECORD a quote is: its list, page, team, status, chat.
 *   Маршрут /     the cargo's PATH — from → to in lists, the chain of points
 *   Вариант       on a leg («Точки маршрута»). A quote's priced alternative is
 *   доставки      «вариант доставки», never «вариант маршрута» (S-13, owner
 *                 2026-10-14); the offer PDF prints «ВАРИАНТЫ ДОСТАВКИ» too.
 *   КП            the DOCUMENT the client receives — «Отправить КП», the
 *                 preview, what «печатается в КП».
 *   Ставка        the approved client rate on a case. The carrier's side is
 *                 «ставка перевозчика» wherever it is prose; the quote's
 *                 computed amount is «цена клиенту».
 *   Расчётные /   planned vs actual costs — the Master File's own headings.
 *   Фактические затраты
 *   Плечо         one leg of a multimodal shipment (neuter: «новое плечо»,
 *                 «плеча», «плечи», «плеч», «на плече», «по плечу»).
 *   Шаблон        a Settings catalogue entry a leg's points are COPIED from.
 *   маршрута      Issue #35 had renamed the leg «Направление» so that the
 *                 catalogue («Направления») and what it fills shared one word.
 *                 The owner reversed that on 2026-10-08: a template is copied
 *                 onto a leg, not linked, so two different names is correct —
 *                 and the walkthrough (D-1) found «направление» meaning three
 *                 things at once (a leg, a catalogue entry, a payment's in/out).
 *                 Only the payment's direction keeps the word.
 *   Команда       colleagues on a record (`team`). The case's and the quote's
 *                 own lists of outside parties («Участники») are gone (S-22a).
 *   Контрагент    every company or person that provides us a service, one
 *                 record with a category (S-22b) — «Перевозчик», «Экспедитор»,
 *                 «Таможенный брокер», «Водитель», «Агент», «Прочее». Also the
 *                 accounting counterparty in finance. «Перевозчик» stays the
 *                 ROLE word on a leg and a truck.
 *
 * Every IDENTIFIER stays as it was — `leg`/`legs`, `directions`, `participants`
 * — the table, the endpoint, the DTO and the keys below, because renaming a
 * contract to match a label is how the two stop being traceable to each other.
 *
 * Russian plurals take three forms and i18next's CLDR suffixes carry them:
 * `_one` (1, 21, 31…), `_few` (2–4, 22–24…), `_many` (0, 5–20, 25…). A day
 * counter is the visible case: «4 дня» but «5 дней».
 *
 * Length: Russian runs roughly 15–30% longer than English. Nothing here may
 * assume the shorter one — no layout in the product is sized to the English
 * string — and table headers in particular are kept short
 * because a column header that wraps changes the row height of a dense grid.
 */

export const ru = {
  common: {
    appName: 'ASA Gateway',
    empty: '—',
    actions: {
      retry: 'Повторить',
      retryNow: 'Повторить сейчас',
      dismiss: 'Скрыть',
      undo: 'Отменить',
      cancel: 'Отмена',
      save: 'Сохранить',
      edit: 'Изменить',
      add: 'Добавить',
      open: 'Открыть',
      close: 'Закрыть',
      /** The scrim behind a drawer: names WHICH panel a click there closes. */
      closeNamed: 'Закрыть «{{title}}»',
      delete: 'Удалить',
      upload: 'Загрузить',
      /** Название колонки действий, заголовок которой визуально пуст. */
      label: 'Действия',
      back: 'На дашборд',
    },
    /** Переключатель таблица/доска на списках. */
    view: {
      label: 'Вид',
      table: 'Таблица',
      board: 'Доска',
    },
    /* Две собственные настройки плотной таблицы (UX-DR-4). Обе — привычка
       рабочего места, своя у каждого пользователя и у каждого экрана, поэтому
       текст говорит про ЭТОТ ЭКРАН, а не про настройки профиля. */
    columns: {
      label: 'Колонки',
      description: 'Какие колонки показывает этот экран',
      reset: 'Показать все колонки',
      hiddenCount_one: 'Скрыта {{count}} колонка',
      hiddenCount_few: 'Скрыто {{count}} колонки',
      hiddenCount_many: 'Скрыто {{count}} колонок',
    },
    filters: {
      label: 'Фильтр',
      status: 'Статус',
      all: 'Все',
      clear: 'Сбросить фильтр',
      /* Множественное число и отдельный ключ: на финансовом экране одновременно
         стоят период, фасет и поиск, и кнопка «Сбросить фильтр» заставляет
         гадать, какой из трёх она уберёт. */
      clearAll: 'Сбросить фильтры',
      activeCount_one: '{{count}} фильтр',
      activeCount_few: '{{count}} фильтра',
      activeCount_many: '{{count}} фильтров',
      emptyTitle: 'Под фильтр ничего не подходит',
      emptyDescription: 'Сбросьте фильтр, чтобы снова увидеть весь список.',
    },
    /* Два пустых состояния плотной таблицы (UX-DR-17). Здесь — «ещё ничего
       нет»; «ничего не подходит» берётся из `filters.emptyTitle` выше, чтобы
       два прочтения пустой таблицы оставались одним решением. Текст намеренно
       общий: это запасной вариант, а экран, который может сказать, чем он
       наполняется, передаёт свой собственный. */
    table: {
      emptyTitle: 'Здесь пока пусто',
      emptyDescription: 'Строки появятся здесь по мере их создания.',
    },
    /* Подвал таблицы со страницами. «Строк на странице» — единственное место,
       где продукт говорит «строки», а не называет запись: это факт о таблице,
       а не о котировках в ней. Счётчик рядом («6 котировок») приходит с
       экрана, который знает, что именно считает. */
    pagination: {
      label: 'Страницы',
      rowsPerPage: 'Строк на странице:',
      previous: 'Предыдущая страница',
      next: 'Следующая страница',
      page: 'Страница {{page}}',
      position: '{{page}} / {{total}}',
    },
    language: {
      label: 'Язык интерфейса',
      en: 'EN',
      ru: 'RU',
      english: 'English',
      russian: 'Русский',
    },
    search: {
      label: 'Поиск',
      placeholder: 'Поиск по работам, котировкам, клиентам',
      loading: 'Ищем…',
      emptyTitle: 'Ничего не нашлось',
      emptyDescription: 'Проверьте номер или попробуйте часть названия клиента.',
      resultCount_one: '{{count}} совпадение',
      resultCount_few: '{{count}} совпадения',
      resultCount_many: '{{count}} совпадений',
      hintNavigate: 'выбрать',
      hintOpen: 'открыть',
      hintClose: 'закрыть',
      kind: {
        case: 'Работа',
        quote: 'Котировка',
        client: 'Клиент',
      },
    },
    notifications: {
      label: 'Уведомления',
      labelWithCount_one: 'Уведомления, {{count}} непрочитанное',
      labelWithCount_few: 'Уведомления, {{count}} непрочитанных',
      labelWithCount_many: 'Уведомления, {{count}} непрочитанных',
      emptyTitle: 'Ничего нового',
      emptyDescription:
        'Здесь появятся ответы в чате, смены статуса, согласования и платежи по вашим котировкам и работам.',
  loading: 'Загрузка уведомлений',
      markAllRead: 'Отметить все',
      unreadItem: 'Не прочитано — {{kind}}',
      /* Заголовки, которые сервер пишет по-английски. Переводятся, только пока
         и тема, и текст в точности совпадают с таблицей
         features/notifications/notificationTitles.ts; иначе — как прислано. */
      titles: {
        teamAssigned: 'Вас добавили в команду котировки',
        pricingRequested: 'По этой котировке ждут вашу цену',
        pricingReady: 'Расчёт по котировке готов',
        pricingReopened: 'Котировка изменилась — нужен новый расчёт',
        caseAssigned: 'Вас назначили на эту работу',
        statusSent: 'Статус отправлен клиенту',
        rateChangeRequested: 'Запрошено изменение ставки',
        rateChangeApproved: 'Изменение ставки согласовано',
        expenseRecorded: 'Добавлена статья затрат',
        expenseAmended: 'Статья затрат изменена',
        expenseRemoved: 'Статья затрат удалена',
        paymentAwaitsConfirmation: 'Платёж ждёт подтверждения',
      },
      /* Код сообщения строки уведомлений словами, из её параметров
         (features/notifications/notificationMessages.ts). Вложено по точкам
         самого кода: `case.status.moved` — это `messages.case.status.moved`.
         У кода, чья детализация — данные (фрагмент чата, маршрут, имя
         клиента), `detail` здесь нет: сохранённая печатается как есть.
         `_bare` — детализация для строки без статьи или основания. */
      messages: {
        case: {
          status: {
            moved: { title: 'Статус работы изменён: {{from}} → {{to}}' },
          },
          assigned: { title: 'Вас назначили на эту работу' },
          'status-sent': {
            title: 'Статус отправлен клиенту',
            detail: '{{status}} → {{email}}',
          },
          message: { title: '{{author}}: новое сообщение в чате работы' },
          'expense-changed': {
            recorded: {
              title: 'Добавлена статья затрат',
              detail: '{{item}} — {{amount}}',
              detail_bare: '{{amount}}',
            },
            amended: {
              title: 'Статья затрат изменена',
              detail: '{{item}} — {{amount}}',
              detail_bare: '{{amount}}',
            },
            removed: {
              title: 'Статья затрат удалена',
              detail: '{{item}} — {{amount}}',
              detail_bare: '{{amount}}',
            },
          },
        },
        quote: {
          message: { title: '{{author}}: новое сообщение в чате котировки' },
          sent: {
            title: 'Котировка отправлена',
            detail_zero: 'Версия {{revision}}, письмо не отправлялось',
            detail_one: 'Версия {{revision}}, {{count}} получатель',
            detail_few: 'Версия {{revision}}, {{count}} получателя',
            detail_many: 'Версия {{revision}}, {{count}} получателей',
          },
          answered: {
            rejected: {
              title: 'Клиент отклонил котировку',
              detail: 'Ответ записан {{date}}',
            },
          },
          'participant-added': {
            title: 'В вашу котировку добавлен участник',
            detail: 'Роль: {{role}}',
          },
          'team-assigned': { title: 'Вас добавили в команду котировки' },
          'pricing-requested': { title: 'По этой котировке ждут вашу цену' },
          'pricing-ready': { title: 'Расчёт по котировке готов' },
          'pricing-reopened': { title: 'Котировка изменилась — нужен новый расчёт' },
          converted: {
            title: 'По котировке создана работа',
            detail: 'Открыта работа {{caseNumber}}',
          },
        },
        finance: {
          'rate-change-requested': {
            title: 'Запрошено изменение ставки',
            detail: '{{current}} → {{requested}}',
          },
          'rate-change-decided': {
            approved: {
              title: 'Изменение ставки согласовано',
              detail: '{{current}} → {{requested}}',
            },
            rejected: {
              title: 'Изменение ставки отклонено',
              detail: 'Отказано: {{requested}}; ставка остаётся {{current}}',
            },
          },
          'payment-awaits-confirmation': {
            title: 'Платёж ждёт подтверждения',
            detail: '{{reference}} — {{amount}}',
            detail_bare: '{{amount}}',
          },
        },
      },
    },
    /** Column and field labels shared by more than one table. */
    fields: {
      name: 'Название',
      country: 'Страна',
      contact: 'Контакт',
      email: 'Email',
      phone: 'Телефон',
      owner: 'Ответственный',
      type: 'Тип',
      status: 'Статус',
      amount: 'Сумма',
      number: 'Номер',
      total: 'Итого',
      date: 'Дата',
    },
    form: {
      saving: 'Сохранение…',
      required: 'Обязательное поле',
      /* Коды полей, которые сервер присылает стабильными токенами (AD-27). */
      invalid: 'Значение не принято',
      tooLong: 'Слишком длинное значение',
      malformed: 'Не удалось прочитать значение',
      /* Денежные коды полей (QW-9, E-1): ноль или минус в сумме и слишком
         точное число. Это коды ПОЛЕЙ под `errors`, а не типы проблем. */
      notPositive: 'Должно быть больше нуля',
      negative: 'Не может быть отрицательным',
      tooPrecise: 'Слишком много знаков после запятой',
      /* Что видит роль с полным доступом, не меняется (история B3): её флаг
         открывает все записи, что бы ни было записано. Код ПОЛЯ `scopes`. */
      superuserScopesFixed: 'Роль с полным доступом всегда видит все записи — это не меняется.',
      /* Коды ПОЛЕЙ `role` и `capabilities` учётной записи и роли (S-7, O7/O8):
         роль или право шире полномочий того, кто их выдаёт, и право, которое
         есть только у супер-админа. */
      exceedsAuthority: 'Больше прав, чем у вас самих, — выдать это вы не можете',
      reserved: 'Это право есть только у супер-админа',
      /* Коды ПОЛЕЙ редактора ролей (S-13): ключ роли уже занят другой ролью, и
         право или раздел, которых сервер не знает, — вкладка открыта до
         обновления сервера. */
      taken: 'Уже используется',
      unknownCapability: 'Такого права нет — обновите страницу',
      unknownSurface: 'Такого раздела нет — обновите страницу',
      /* Коды ПОЛЕЙ оплаты перевозчику несколькими строками (S-18a): строка
         с затратой и долгом сразу, одна и та же единица дважды, больше 101
         строк, сумма платежа не равна сумме строк, строки разных
         перевозчиков. */
      exclusive: 'Укажите что-то одно',
      duplicate: 'Эта строка уже есть в оплате',
      tooMany: 'Слишком много строк в одной оплате — не больше 101',
      sumMismatch: 'Сумма оплаты не равна сумме строк',
      carrierMismatch: 'Одной оплатой платят только одному перевозчику из справочника',
      /* S-22d: долг по фуре или плечу, чей перевозчик вписан текстом, а не
         выбран из справочника, — код `items.<i>.debt`. Исправляют на плече. */
      carrierNotInDirectory: 'Перевозчик не из справочника — выберите его из справочника на плече',
      /* S-22d: фактическая затрата и оплата перевозчику называют контрагента
         из справочника — коды `vendorId` и `payment.counterpartyId`: не выбран,
         или такого в справочнике нет (удалён или объединён, пока форма была
         открыта). */
      counterpartyPick: 'Выберите контрагента из справочника',
      counterpartyUnknown: 'Контрагент не найден в справочнике',
      /* Сторона платежа (finance/payment_party.go): входящий платёж с
         контрагентом из справочника и платёж, названный и клиентом, и
         контрагентом сразу. Форма такого не отправляет — только старая
         вкладка. */
      partyDirection:
        'Входящий платёж не бывает от контрагента из справочника — выберите клиента или другого плательщика',
      withCounterparty: 'Укажите кого-то одного: клиента или контрагента',
      /* Коды ПОЛЕЙ единиц плеча (S-18b): ключ, который сервер больше не читает,
         — его шлёт только вкладка, открытая до обновления сервера; и поле,
         которого нет у этого вида единицы (`units.<i>.<поле>`). */
      retired: 'Страница устарела — обновите её',
      notForKind: 'Это поле не относится к такому виду единицы',
      /* finance/debitnote_service.go (S-22a): новая дебет-нота выставляется
         только клиенту этой работы — на `counterparty`. */
      clientOnly: 'Дебет-нота выставляется только клиенту этой работы',
      /* directory (S-22b): объединить можно только двух контрагентов одной
         категории — код ПОЛЯ `category`. */
      mismatched: 'Не совпадает',
      /* Код ПОЛЯ `planned` на складной записи затраты (S-4.1): у внеплановой
         затраты план не появляется задним числом — именно такой план прятал
         перерасход. Фраза говорит, что делать вместо этого. */
      unplannedLine:
        'У внеплановой затраты нельзя задать план — удалите её и внесите заново с планом',
      invalidNumber: 'Введите число',
      /* Число само по себе верное, но точнее, чем колонка, которая его хранит.
         Отдельная строка: «Введите число» над 3.607 отправит читателя искать
         ошибку, которой нет. */
      tooManyDecimals: 'Не более 2 знаков после запятой',
      fixFields: 'Проверьте поля — без этого сохранить нельзя.',
      active: 'Активен',
      inactive: 'Неактивен',
      optional: 'Необязательно',
      /* Подпись на каждом раскрывающемся блоке, который прячет остаток формы.
         Одно слово на всех, чтобы тот, кто нашёл редкие поля в одной форме,
         искал их там же в следующей. Число рядом — от компонента. */
      more: 'Ещё',
      selectPlaceholder: 'Выберите…',
      searchPlaceholder: 'Поиск…',
      noMatches: 'Ничего не найдено',
      noMatchesHint: 'Попробуйте запрос короче',
      /* Выбор страны (ISO 3166-1). Закрытый элемент теперь кнопка, поэтому
         подсказка называет сам выбор; о том, что можно ввести, говорит поле
         поиска в открытом списке — строка ниже. */
      countryPlaceholder: 'Выберите страну…',
      /* Поиск внутри списка стран. Ищет и по названию, и по кодам, поэтому
         подсказка называет оба: по «Поиск…» не догадаться, что «RUS» или
         «398» тоже находят страну. */
      countrySearch: 'Страна, код…',
      /* Две половины поля телефона: кнопка кода страны и номер рядом с ней.
         Сама кнопка показывает «+7» и не говорит, что это, — существительное
         даёт подпись. */
      phoneCode: 'Телефонный код страны',
      phoneSearch: 'Страна, +код…',
      phonePlaceholder: 'Номер телефона',
      /* Для сохранённого значения, которого нет среди 249 кодов, — порта или
         города, введённого до появления выбора. Остаётся доступным: запись не
         ошибочна, она старше самого поля. */
      countryCarriedOver: 'как введено',
      /* Русский требует три формы: 1 показан, 2–4 показаны, 5+ показано.
         i18next выбирает ветку по `count`, поэтому текст согласуется с числом
         совпадений, а не с общим количеством. */
      matchCount_one: 'Показан {{count}} из {{total}}',
      matchCount_few: 'Показаны {{count}} из {{total}}',
      matchCount_many: 'Показано {{count}} из {{total}}',
      clear: 'Очистить',
      // The mask the Russian DatePicker shows and reads: day first, dots. The
      // value stays ISO; lib/dateInput also accepts ISO, `/`, `,`, a space and
      // eight bare digits, so the mask is the suggested form, not the only one.
      datePlaceholder: 'ДД.ММ.ГГГГ',
      openCalendar: 'Открыть календарь',
      previousMonth: 'Предыдущий месяц',
      nextMonth: 'Следующий месяц',
      today: 'Сегодня',
    },
    /* Предупреждение о несохранённых изменениях в форме.
     *
     * Вопрос, а не предупреждение, и в нём названо, чем рискуют: «вы уверены?»
     * без указания потери заставляет догадываться, с чем соглашаешься.
     * Основное действие здесь — ОТМЕНИТЬ закрытие, безопасное, чтобы
     * машинальный Enter сохранял черновик, а не удалял его. */
    unsaved: {
      title: 'Отменить изменения?',
      body: 'В форме есть несохранённые правки. Если её закрыть, они пропадут.',
      keep: 'Вернуться к правке',
      discard: 'Отменить изменения',
    },
    menu: {
      open: 'Открыть меню',
      close: 'Закрыть меню',
    },
    loading: 'Загрузка',
    /* Один словарь периодов на все финансовые экраны и дашборд
       (components/data/DateRangeControl.tsx). `1W` убран вместе со вторым
       словарём, который его объявлял: неделя дебиторки — это шум вокруг одного
       счёта, а два набора периодов позволяют двум экранам отвечать на разные
       вопросы, выглядя одинаково. */
    dateRange: {
      label: 'Период: {{label}}',
      shared: 'Период',
      // Range chips stay Latin: they are read as symbols, they must fit a
      // narrow chip, and «1Н / 1М / 3М / 1Г» reads as a typo at 11.5px.
      '1M': '1M',
      '3M': '3M',
      '1Y': '1Y',
      all: 'Всё',
    },
    /* Suppressed fields (AD-6): the UI names the suppression rather than
     * rendering an empty cell, so it is never misread as missing data. */
    suppressed: {
      agent: 'Агент скрыт',
      carrier: 'Перевозчик скрыт',
      vendor: 'Поставщик скрыт',
      contact: 'Контакты ограничены',
      generic: 'Скрыто',
      reason: 'Ваша роль не видит это поле',
    },
    relativeTime: {
      now: 'сейчас',
      minutes: '{{count}} мин',
      hours: '{{count}} ч',
      days: '{{count}} д',
    },
  },

  nav: {
    label: 'Основное',
    sections: {
      main: 'Основное',
      company: 'Компания',
    },
    dashboard: 'Дашборд',
    myTasks: 'Мои задачи',
    quotes: 'Котировки',
    cases: 'Работы',
    chat: 'Чат',
    finance: 'Финансы',
    clients: 'Клиенты',
    counterparties: 'Контрагенты',
    directories: 'Справочники',
    settings: 'Настройки',
    profile: 'Профиль',
  },

  roles: {
    sales: 'Продажи',
    pricing: 'Расчёт',
    operations: 'Операции',
    finance: 'Финансы',
    'super-admin': 'Суперадмин',
  },

  /* Для чего встроенная роль — пока её описание в базе совпадает с тем, что
     записала миграция 0021. Описание, переписанное администратором, показывается
     как есть, на любом языке. */
  roleDescriptions: {
    sales: 'Ведёт отношения с клиентом и воронку котировок.',
    pricing: 'Считает себестоимость в котировках и ведёт агентов.',
    operations: 'Ведёт перевозку, когда котировка становится работой.',
    finance: 'Счета, оплаты, дебиторка и сводные отчёты по всем работам.',
    'super-admin': 'Полный доступ, управление пользователями и самими ролями.',
  },

  modes: {
    sea: 'Море',
    rail: 'Ж/д',
    road: 'Авто',
    air: 'Авиа',
    multimodal: 'Мультимодал',
  },

  caseStatus: {
    draft: 'Черновик',
    'in-progress': 'В работе',
    stuck: 'Застряла',
    delivered: 'Доставлена',
    // Двухэтапное закрытие, по которому работает компания (FR-25): сначала
    // операционное, потом финансовое. Обе половины НАЗЫВАЮТ себя: просто
    // «Закрыта» рядом с «Закрыта опер.» заставляла читателя догадываться, что
    // неуточнённая — это про деньги, а название статуса существует ровно для
    // того, чтобы догадываться не приходилось.
    'operationally-closed': 'Закрыта опер.',
    'financially-closed': 'Закрыта фин.',
    cancelled: 'Отменена',
  },

  quoteStatus: {
    draft: 'Черновик',
    sent: 'Отправлена',
    rejected: 'Отклонена',
    expired: 'Истекла',
    converted: 'Конвертирована',
  },

  docStatus: {
    draft: 'Черновик',
    issued: 'Выставлен',
    'part-paid': 'Частично оплачен',
    paid: 'Оплачен',
    /* Значения `void` здесь нет, и это намеренно. AD-24 делает переход
       «черновик → выставлен» односторонним, и вернуть документ обратно в
       системе нечем: исправление оформляется новым документом — ровно так,
       как это делается на бумаге. Плашка состояния, которое никто не может
       создать, обещает то, чего не бывает. `part-paid` и `paid` тоже нигде не
       хранятся — они выводятся на клиенте из присланных сумм, — но это
       настоящие состояния настоящего документа, поэтому они остались, а
       `void` ушёл. */
  },

  documentKind: {
    invoice: 'Счёт',
    'debit-note': 'Дебет-нота',
    // КП — как это и называют вслух.
    offer: 'КП',
    /* PDF статуса для клиента, который отправка подшивала к работе до S-15.
       Теперь запись об отправке — журнал «Отправленные статусы», но старые
       документы по-прежнему видны в списке бумаг работы. */
    'status-update': 'Статус для клиента',
  },

  /* Что такое точка маршрута (миграция 0062) — общее для вариантов маршрута
     в котировке, точек направления в работе и шаблонов направлений. */
  routePoints: {
    kind: 'Тип точки',
    kindN: 'Тип точки {{n}}',
    kinds: {
      border_crossing: 'Погранпереход',
      customs_terminal: 'Таможенный терминал',
      waypoint: 'Пункт',
    },
    transshipment: 'Перегруз',
    transshipmentYes: 'Есть',
  },
  legStatus: {
    planned: 'Запланировано',
    'in-transit': 'В пути',
    'waiting-border': 'Ожидает на границе',
    arrived: 'Прибыло',
    completed: 'Завершено',
  },

  /* Категория контрагента (S-22b). Сервер присылает КОД, слова — здесь. */
  counterpartyCategory: {
    carrier: 'Перевозчик',
    forwarder: 'Экспедитор',
    'customs-broker': 'Таможенный брокер',
    driver: 'Водитель',
    agent: 'Агент',
    other: 'Прочее',
  },

  /* Типы участника справочника до S-22b. Остались только для старых строк
     колокольчика, которые называют роль этими словами. */
  participantRole: {
    shipper: 'Грузоотправитель',
    consignee: 'Грузополучатель',
    agent: 'Агент',
    'counter-agent': 'Контрагент',
    'vehicle-owner': 'Владелец транспорта',
    dispatcher: 'Диспетчер',
    driver: 'Водитель',
  },

  notificationKind: {
    message: 'Сообщение',
    status: 'Статус',
    approval: 'Согласование',
    payment: 'Платёж',
    expense: 'Затраты',
    pricing: 'Расчёт цены',
    document: 'Документ',
    file: 'Файл',
    assignment: 'Назначение',
    stuck: 'Застряла',
  },

  recordKind: {
    case: 'Работа',
    quote: 'Котировка',
    invoice: 'Счёт',
  },

  /* «Мои задачи» (S-2b.1, маршрут /tasks; финансовые разделы — S-2b.2).
     Разделы и их порядок присылает сервер; заголовки и подписи — здесь, по
     ключу раздела. «С …» перед датой говорит, с какого события запись ждёт. */
  myTasks: {
    title: 'Мои задачи',
    subtitle: 'Котировки, работы и деньги, которые ждут вашего действия.',
    eyebrow: '{{date}} · Данные на {{time}}',
    /* Карточка раздела без записей: раздел ваш, но сейчас в нём пусто. */
    nothingWaiting: 'Ничего не ждёт',
    /* Сервер присылает не больше 50 записей и настоящее число. Ссылки «Показать
       все» нет: списки котировок и работ не умеют отбирать по этим правилам. */
    more: 'и ещё {{count}}',
    /* Полоса задач над дашбордом показывает до пяти записей раздела; остальное —
       на странице «Мои задачи». */
    allTasks: 'Все задачи',
    /* Одна строка для финансов: сумма к оплате — та же цифра, что на дашборде,
       и ссылка туда, где с этим работают. Ссылка не обещает список, который
       сходится с цифрой. */
    toPay: {
      label: 'К оплате',
      open: 'Открыть балансы',
    },
    /* Ни одного раздела: роль не может сделать ничего, к чему они ведут. */
    noSections: {
      title: 'Для вашей роли здесь нет разделов',
      description:
        'Здесь собирается то, что ждёт вашего действия: расчёт, отправка КП, плечи, фуры, фактические затраты, согласование ставок, подтверждение платежей. Ваша роль ничего из этого не делает.',
    },
    sections: {
      'pricing-requested': {
        title: 'Ждут вашего расчёта',
        hint: 'Вас попросили назвать цену.',
        since: 'Запрошен {{date}}',
      },
      /* Дата — более поздняя из двух: когда расчёт готов и когда КП последний
         раз отправляли. Переоткрытое КП остаётся здесь, поэтому подпись верна
         при любой из них. */
      'pricing-ready': {
        title: 'Расчёт готов, КП не отправлено',
        hint: 'Цена посчитана — КП можно отправлять клиенту.',
        since: 'Ждёт отправки с {{date}}',
      },
      'awaiting-client': {
        title: 'Ждут ответа клиента',
        hint: 'КП отправлено несколько дней назад и ещё действует, а ответ клиента не внесён.',
        since: 'Отправлено {{date}}',
      },
      /* Отправленные КП, срок действия которых кончается в ближайшие дни
         (порог — в настройках). Дата в строке — последний день действия. */
      'expiring-soon': {
        title: 'Срок КП скоро истекает',
        hint: 'КП отправлено, ответ клиента не внесён, а срок его действия заканчивается.',
        until: 'до {{date}}',
      },
      'case-no-legs': {
        title: 'Работы без плеч',
        hint: 'В работе ещё нет ни одного плеча.',
        since: 'Открыта {{date}}',
      },
      'case-no-vehicle': {
        title: 'Плечи без фуры',
        hint: 'Автомобильное плечо, груз ещё не доставлен, а фура не добавлена.',
        since: 'Плечо создано {{date}}',
      },
      'case-no-actuals': {
        title: 'Работы без фактических затрат',
        hint: 'Работа идёт или закрыта операционно, а фактических затрат в ней нет.',
        since: 'Открыта {{date}}',
      },
      /* S-12: ставки фур плеча не детализируют строку плана и не отмечены
         «не детализирует», а в плане работы есть строка, которую никто не
         детализирует, — перевозка в прогнозе может считаться дважды. Строка
         ведёт на первое такое плечо. */
      'case-truck-double-count': {
        title: 'Двойной счёт фур',
        hint: 'Ставки фур плеча не привязаны к строке плана, а в плане есть строка перевозки — она может считаться дважды.',
        since: 'С {{date}}',
      },
      /* Тот же список, что во вкладке «Согласования ставок»: строка — номер
         работы, кто запросил и на сколько меняется ставка. Причину запроса
         показывает только сама вкладка. */
      'rate-approvals': {
        title: 'Согласования ставок',
        hint: 'Запрошено изменение ставки — нужно ваше решение.',
      },
      /* Только число: с сервера не приходит ни плательщик, ни сумма. Фразу
         собираем здесь, потому что «платёж» требует трёх форм. */
      'payments-to-confirm': {
        title: 'Платежи к подтверждению',
        hint: 'Платёж записан, но ещё не подтверждён.',
        awaiting_one: '{{count}} платёж ждёт подтверждения',
        awaiting_few: '{{count}} платежа ждут подтверждения',
        awaiting_many: '{{count}} платежей ждут подтверждения',
      },
    },
  },

  dashboard: {
    /* Russian splits the day differently from English: «Добрый день» covers
     * roughly 11:00–17:00 and there is no idiomatic equivalent of a broad
     * "Good afternoon". The boundaries live in the component; both catalogues
     * simply supply three greetings and the component picks by hour. */
    greeting: {
      morning: 'Доброе утро, {{name}}',
      afternoon: 'Добрый день, {{name}}',
      evening: 'Добрый вечер, {{name}}',
    },
    eyebrow: '{{date}} · Данные на {{time}}',
    /* Число — строки «Требует решения» плюс задачи из полосы над дашбордом. */
    subtitle_one: '{{count}} вопрос ждёт вашего решения или действия.',
    subtitle_few: '{{count}} вопроса ждут вашего решения или действия.',
    subtitle_many: '{{count}} вопросов ждут вашего решения или действия.',
    loading: 'Загрузка дашборда',

    counters: {
      activeCases: 'Активные работы',
      shipmentsThisMonth: 'Отгрузки за месяц',
      toPay: 'К оплате',
    },

    /* Денежные плитки. Подпись здесь не обещает, что цифра есть: фигуру,
       которую сервер не посчитал, он объявляет в `pending`, и плитка сама
       говорит о своём отсутствии, а не рисует пустую карточку.

       Оборот (Story B7) — сколько выставлено клиентам за текущий календарный
       месяц: выпущенные счета и дебет-ноты клиенту, в долларах по курсу
       проводки, БЕЗ НДС. Поэтому с «К получению» (там НДС включён) он не
       сходится и не должен. «За месяц» в подписи — потому что переключателя
       периода у плитки нет. */
    tiles: {
      turnover: 'Оборот за месяц',
      /* «Прибыль», а не «Прогноз прибыли»: по FR-51 AC 2 это одна цифра на двух
         основаниях — сколько работа заработала фактически, если она финансово
         закрыта, и сколько прогнозируется по всем остальным. Название по одному
         из двух неверно описывало бы вторую половину той же суммы. */
      profit: 'Прибыль',
      /* «К получению» и «К оплате» — слова самого основателя, и каждое сразу
         говорит, что с этим делать. */
      toCollect: 'К получению',
      toPay: 'К оплате',
      /* Переход к списку за цифрой (UX-DR-10). Одна подпись на все плитки:
         заголовок плитки уже называет цифру, и «Смотреть дебиторку» рядом с
         «К получению» сказало бы это дважды. */
      drillThrough: 'Открыть список',
    },

    /* Плитка, которую сервер объявил `pending`. Фраза ничего не обещает: плитка
       не построена, а номер истории — признак для кода, а не новость для
       читателя (QW-4). Сервер по-прежнему присылает `story`; на экран он не
       попадает. */
    pending: {
      description: 'Пока не считается.',
    },

    attention: {
      title: 'Требует решения',
      /* Когда всё в порядке, так и сказать — без подбадривания. Здесь только
         убыточные и застрявшие работы: согласования ставок и платежи к
         подтверждению переехали в «Мои задачи» над дашбордом (S-2b.2). */
      emptyTitle: 'Нет убыточных и застрявших работ',
      /* Порог приходит с сервера. Три формы, потому что счёт по-русски требует
         «1 день», «2 дня», «5 дней» — форма _many покрывает и 0. */
      emptyDescription_one:
        'Работа попадёт сюда, если не двигалась {{count}} день.',
      emptyDescription_few:
        'Работа попадёт сюда, если не двигалась {{count}} дня.',
      emptyDescription_many:
        'Работа попадёт сюда, если не двигалась {{count}} дней.',
      stuckFor_one: 'Без движения {{count}} день',
      stuckFor_few: 'Без движения {{count}} дня',
      stuckFor_many: 'Без движения {{count}} дней',
      /* Какая именно цифра отрицательная (FR-52). Различие определяет действие:
         прогноз ещё можно исправить, пока работа идёт, а итог — это убыток,
         который уже проведён. */
      minusBasis: {
        forecast: 'Прогноз убыточный',
        final: 'Закрыта с убытком',
      },
      kind: {
        stuck: 'Застряла',
        minus: 'Минус',
        overdue: 'Просрочено',
        checklist: 'Неполные данные',
        'quote-followup': 'Напомнить',
      },
    },

    activity: {
      title: 'Последние события',
      empty: 'Событий пока нет',
    },

    aging: {
      caption: 'Клиентов: {{clients}} · траншей на этой неделе: {{tranches}}',
    },
  },

  cases: {
    conversionLegSkipped:
      'Работа создана без плеча: у выбранного варианта доставки не указан вид перевозки. Добавьте плечо в работе.',
    title: 'Работы',
    subtitle: 'Все работы в движении и сколько они стоят.',
    // Ссылка на раздел у роли с «Нет доступа» к работам (#129).
    noAccess: {
      title: 'Раздел недоступен',
      description:
        'Работы открываются доступом вашей роли к разделу «Работы». Если он вам нужен, обратитесь к администратору.',
    },
    loading: 'Загрузка работы',
    backToList: 'К работам',
    // Пока карточка работы собирается из модулей, которых ещё нет, открывать
    // её нечем — честнее сказать это, чем показать «запись недоступна».
    detailNotReady: 'Карточка работы ещё не подключена — {{number}} создана и есть в списке.',
    sectionsLabel: 'Разделы работы',
    actions: {
      fromQuote: 'Из котировки',
      newCase: 'Новая работа',
      editCase: 'Изменить работу',
      create: 'Создать работу',
      sendStatus: 'Отправить статус клиенту',
    },

    /* FR-41. «Отправить», не «уведомить»: отправляет человек, а не система. */
    statusSend: {
      title: 'Отправить статус клиенту',
      submit: 'Отправить',
      recipient: 'Кому',
      status: 'Статус',
      language: 'Язык письма',
      languageHint: 'Язык, на котором читает клиент, а не вы.',
      note: 'Комментарий',
      noteHint: 'Необязательно. Добавится под статусом и датой прибытия.',
      notePlaceholder: 'Что клиенту стоит знать помимо статуса.',
      noContact:
        'У этого клиента ещё нет контактной почты. Добавьте её в справочнике клиентов, чтобы отправлять обновления.',
      sent: 'Статус отправлен на {{email}}.',
      failed: 'Статус не отправлен. Проверьте соединение и попробуйте снова.',
      /* Роль без доступа к контактам клиента: сервер отказывает (403), потому
         что отправка раскрыла бы адрес. Не «нет почты» — справочник в порядке. */
      noContactAccess: 'Нет доступа к контактам клиента — отправить статус может коллега с доступом',

      /* Письмо, каким его получит клиент: сервер собирает его тем же шаблоном,
         что и отправку. Показывается под полями, которые его меняют. */
      preview: {
        title: 'Предпросмотр',
        hint: 'Так письмо увидит клиент. Обновляется при смене языка и через секунду после правки комментария.',
        loading: 'Готовим предпросмотр письма…',
        frameTitle: 'Письмо клиенту со статусом',
        subject: 'Тема',
        noneSelected: 'Отметьте хотя бы одну работу — здесь появится письмо.',
      },
    },

    /* Русский требует три формы множественного числа (one/few/many), а не две:
       1 работа, 2 работы, 5 работ. Английский `_other` здесь дал бы «5 работы». */
    clientStatusSend: {
      title: 'Отправить статус клиенту',
      client: 'Клиент',
      clientPlaceholder: 'Выберите клиента',
      pickClientFirst: 'Выберите клиента, чтобы увидеть его работы.',
      cases: 'Работы',
      selectAll: 'Выбрать все',
      selectedCount: '{{selected}} из {{total}}',
      submitCount_one: 'Отправить {{count}} работу',
      submitCount_few: 'Отправить {{count}} работы',
      submitCount_many: 'Отправить {{count}} работ',
      noteHint: 'Необязательно. Добавится к каждой работе в этой отправке.',
      noContact:
        'У этого клиента ещё нет контактной почты. Добавьте её в справочнике клиентов, чтобы отправлять обновления.',
      nothingReportable:
        'Все работы этого клиента отменены или закрыты — сообщать не о чем.',
      notReportable: 'Отменена или закрыта — не включена',
      sentAll_one: 'Статус отправлен по {{count}} работе.',
      sentAll_few: 'Статус отправлен по {{count}} работам.',
      sentAll_many: 'Статус отправлен по {{count}} работам.',
      sentPartial: 'Статус отправлен по {{sent}} из {{total}} работ.',
    },

    /* Журнал отправленных статусов: каждое письмо, ушедшее клиенту по работе,
       ровно в том виде, в каком ушло. Открывается в листе «История». */
    statusSends: {
      title: 'Отправленные статусы',
      subtitle: 'Каждое письмо со статусом, ушедшее клиенту по этой работе.',
      loading: 'Загружаем отправленные статусы…',
      empty: 'По этой работе статус клиенту ещё не отправляли.',
      /* Письмо ушло в сводке — одним письмом по нескольким работам клиента. */
      batch: 'сводка',
      batchHint: 'Одно письмо по нескольким работам клиента',
      noRecipientName: 'Без имени',
      openLabel: 'Открыть письмо от {{date}}',
      viewerTitle: 'Письмо от {{date}}',
      subject: 'Тема',
      sentBy: 'Отправитель',
      recipient: 'Кому',
      /* Сводка охватила работу вне доступа читателя — сервер не отдаёт ни тему, ни тело. */
      bodyWithheld: 'Сводка включает перевозки вне вашего доступа — письмо не показывается.',
    },

    form: {
      paymentTermsKind: 'Условия оплаты',
      paymentMethod: 'Способ оплаты',
      creditDays: 'Дней отсрочки',
      creditDaysHint: 'Дней от регистрации до срока оплаты всей суммы.',
      creditDaysInvalid: 'Укажите целое число дней, от 1 до 365.',
      nextStep: 'Плечи и деньги добавляются в самой работе.',
    },
    terms: {
      in_advance: 'Предоплата',
      fifty_fifty: '50 на 50',
      full_on_delivery: 'Полностью по прибытии',
      credit: 'Отсрочка',
      other: 'Другое',
    },
    method: {
      cash: 'Наличные',
      bank_transfer: 'Банковский перевод',
    },
    columns: {
      number: 'Работа',
      client: 'Клиент',
      route: 'Маршрут',
      leg: 'Плечо',
      status: 'Статус',
      eta: 'ETA',
      rate: 'Ставка USD',
      /* Та же цифра, что справа в строке «Прибыль» на работе (S-6): прогноз,
         пока работа идёт, итог — после финансового закрытия или отмены. */
      profit: 'Прибыль USD',
    },
    card: {
      /* Блок цифр на карточке доски. Подписи короткие: их прочитывают один раз,
         а взглядом идут по колонке значений рядом. Даты оставляем как их
         называют на складе и в письмах — ETD и ETA, латиницей. */
      rate: 'Ставка',
      profit: 'Прибыль',
      /* Где груз на маршруте — на карточке доски. Это ПОЗИЦИЯ, а не обратный
         отсчёт: «Плечо 2/3» говорит и сколько пройдено, и сколько
         осталось, тогда как «осталось 1 плечо» — только второе, и звучит
         как обещание. */
      leg: 'Плечо {{sequence}}/{{total}}',
      etd: 'ETD',
      eta: 'ETA',
      stalledShort: '{{count}} д',
    },

    /* Перенос карточки в другую колонку — это смена статуса (FR-25, AD-24).
       Называем номер работы: пока сообщение читают, карточка уже вернулась
       на место. */
    board: {
      moveRefused:
        'Работа {{number}} не перенесена. Сервер отклонил изменение — для финансового закрытия сначала нужно закрыть все балансы.',
      /* Пустая колонка — словами. Приглашение вынесено в ОТДЕЛЬНЫЙ ключ: оно
         верно только там, куда действительно можно перетащить. Над «Отменена»
         такая строка учила бы жесту, который эта колонка не примет. */
      laneEmpty: 'Нет работ',
      laneDrop: 'Перетащите сюда для изменения статуса.',
    },

    /* Жизненный цикл в самой карточке (FR-25, FR-37, AD-24). Названия статусов
       подставляются из каталога `caseStatus` и берутся в кавычки: «Перевести в
       Отменена» по-русски не читается, а склонять название статуса — значит
       завести ему вторую форму и рассинхронизировать её с колонкой доски. */
    lifecycle: {
      unsettledBalances:
        'Работу нельзя закрыть финансово, пока есть незакрытые расчёты: {{items}}.',
      label: 'Жизненный цикл работы',
      change: 'Изменить статус',
      moveTo: 'Перевести в «{{status}}»',
      moving: 'Переносим…',

      confirmTitle: 'Подтвердите изменение',
      confirmFinancialBody:
        'Финансовое закрытие {{number}} завершает денежную сторону работы: счета и платежи по ней больше не принимаются. Открыть её заново сможет только супер-администратор, и это будет записано в историю.',
      confirmFinancialAction: 'Закрыть финансово',
      confirmCancelBody:
        'Отмена {{number}} необратима. История и связь с котировкой сохранятся, но перевести работу в другой статус после этого будет нельзя.',
      confirmCancelAction: 'Отменить работу',

      balancesTitle: 'Работу пока нельзя закрыть финансово',

      reopen: 'Открыть заново',
      reopenTitle: 'Открыть заново {{number}}',
      reopenSubmit: 'Открыть работу',
      reopenBody:
        'Работа вернётся в статус «Закрыта опер.». Отменяется только финансовое закрытие — доставка заново не открывается.',
      reason: 'Причина',
      reasonHint:
        'Запишется в историю работы вместе с вашим именем. Без причины открыть заново нельзя.',
    },
    stalled_one: 'Нет движения {{count}} день',
    stalled_few: 'Нет движения {{count}} дня',
    stalled_many: 'Нет движения {{count}} дней',
    /* Пустой список без фильтра (QW-9, C-7). Строки отбирает сервер по
       доступу читателя (AD-5), а клиент этих правил не знает, поэтому фраза не
       называет никакого правила: говорит, что в списке, и что делать, если
       ждёте чужую работу. «Работ пока нет» было неправдой для того, чья
       коллега уже ведёт работу, просто не видную ему. */
    emptyTitle: 'Нет доступных работ',
    emptyDescription:
      'Здесь видны работы, доступные вам. Если ждёте работу коллеги — попросите добавить вас в её команду.',

    header: {
      rate: 'Ставка',
      cost: 'Затраты',
      /** Уточнение к цифре затрат — «Затраты · прогноз»: факт там, где он
       *  есть, план по строкам, где факта ещё нет. */
      costBasis: 'прогноз',
      profit: 'Прибыль',
      mode: 'Вид перевозки',
    },

    /* Шесть разделов работы (S-11 и его доработка). «Команда» — сотрудники,
       которые ведут работу. «Счета клиенту» — что выставлено клиенту и что он
       заплатил, «Затраты» — во что обходится работа и сколько должны каждому
       поставщику. «Документы» — это ключ `files`: бумаги работы и её файлы
       вместе; старый `?section=documents` — псевдоним и ведёт к счетам в
       «Счетах клиенту» (или сюда — для читателя без рабочего места
       Финансов). История открывается из «⋯» в шапке. */
    tabs: {
      overview: 'Обзор',
      legs: 'Плечи',
      team: 'Команда',
      billing: 'Счета клиенту',
      costs: 'Затраты',
      files: 'Документы',
    },

    /* «Счета клиенту» (доработка S-11): четыре плитки сверху. Все суммы —
       от сервера (`clientTotals`); «Долг клиента» со знаком минус читается
       как «Аванс клиента» без минуса. */
    /* «Затраты» (доработка S-11): четыре плитки и «Платежи поставщикам».
       Все суммы — от сервера (`plannedCost`, `actualToDate`, `suppliers`).
       «Остаток поставщикам» — это прогнозная база вместе со строками без
       поставщика, а не «К оплате» из FR-30. */
    costs: {
      tilesLabel: 'Итоги по затратам',
      planned: 'План',
      actual: 'Факт',
      paid: 'Оплачено',
      outstanding: 'Остаток поставщикам',
      suppliers: {
        title: 'Платежи поставщикам',
        /* Для продаж: сервер не отдаёт групп и имён (AD-6). */
        hidden: 'Поставщики скрыты',
        unattributed: 'Без поставщика / не разнесено',
        debt: 'долг',
        paid: 'оплачено',
        outstanding: 'остаток',
        noPayments: 'Платежей нет',
        emptyTitle: 'Платежей поставщикам пока нет',
        emptyDescription:
          'Здесь появятся затраты по поставщикам и платежи, которые их закрывают.',
        unallocated: 'не разнесено',
        /* Остальные строки платежа, разбитого между поставщиками: действия у
           него одни — на первой строке или на остатке. */
        partOf: 'часть платежа {{reference}}',
        columns: {
          date: 'Дата',
          leg: 'Плечо / единица',
          reference: 'Платёжка',
          amount: 'Сумма',
          status: 'Статус',
          acts: 'Действия',
        },
      },
    },
    billing: {
      tilesLabel: 'Итоги по клиенту',
      rate: 'Ставка',
      noRate: 'Ставка не согласована',
      invoiced: 'Выставлено',
      invoicedNote: 'Счета и дебет-ноты клиенту',
      received: 'Получено',
      receivedUnallocated: 'из них не разнесено {{amounts}}',
      /* S-22f: возврат клиенту — заметка под «Получено», а не пятая плитка;
         «Получено» уже за вычетом возврата. Сумма — как прислал сервер. */
      refunded: 'Возврат: {{amount}}',
      debt: 'Долг клиента',
      advance: 'Аванс клиента',
      advanceNote: 'Клиент заплатил больше выставленного',
    },
    /* «⋯» в шапке (S-11): история ушла сюда из рельса, как на котировке. */
    more: {
      label: 'Ещё',
      history: 'История',
    },
    /* Панель, которую открывает ячейка плеча строки расхода в «Деньгах»
       (S-11): деньги перевозчика по этому плечу, только чтение, и оплата. */
    legDrawer: {
      leg: 'Плечо {{n}} · {{mode}}',
      planned: 'План',
      actual: 'Факт',
      /* Цифра ПЛЕЧА рядом с двумя цифрами СТРОКИ: сколько ещё должны его
         перевозчикам по всем строкам плеча. */
      legOutstanding: 'К оплате по плечу',
      payments: 'Оплаты по единицам',
      openLeg: 'Открыть плечо {{n}}',
      pay: 'Оплатить перевозчику',
      loading: 'Загружаем плечо',
      noMoney: 'Деньги плеча сейчас недоступны. Всё о нём — на странице плеча.',
    },

    overview: {
      route: 'Маршрут',
      /* «жд + авто через Хоргос» — виды транспорта приходят уже переведёнными. */
      routeVia: '{{modes}} через {{border}}',
      stopRole: {
        loading: 'Погрузка',
        border: 'Погранпереход',
        discharge: 'Пункт разгрузки',
        unloading: 'Место выгрузки',
      },
      departed: 'отправление {{date}}',
      arrivedOn: 'прибытие {{date}}',
      hereNow: 'здесь сейчас',
      hereNowFor: 'здесь сейчас, {{days}} дн',
      etaOn: 'ETA {{date}}',

      cargo: 'Груз',
      imo: 'IMO {{class}}',
      kg: 'кг',
      cbm: 'CBM',
      declaredTotal: 'Заявлено всего',
      measuredTotal: 'Измерено',
      differsFromDeclared: 'отличается от заявленного',
      measuredAt: 'Контрольный вес — {{location}}',

      containers: 'Контейнеры и пломбы',
      containersEmptyTitle: 'Контейнеров пока нет',
      containersEmptyDescription:
        'Пары «контейнер — пломба» подставляются в документы, которые выпускает эта работа.',
      seal: 'Пломба',
      equipment: 'Оборудование',

      /* «Детали работы» разделены на две карточки ровной сетки «Обзора»
         (доработка S-11): что договорено, и когда и кем. */
      terms: 'Условия сделки',
      schedule: 'Сроки и ответственные',
      dates: 'Даты',
      transit: 'Срок доставки',
      incoterm: 'Инкотермс',
      paymentTerms: 'Условия оплаты',
      paymentMethod: 'Способ оплаты',
      creditDays_one: '{{count}} день',
      creditDays_few: '{{count}} дня',
      creditDays_many: '{{count}} дней',
      responsible: 'Ответственные',
      opsNamed: 'опер.: {{name}}',
      entity: 'Юрлицо',
      packingList: 'Упаковочный лист',
      packingListReceived: 'Финальный лист получен — данные клиента',
      packingListEstimate: 'Не получен — наша оценка',
      notes: 'Примечания',
      routeEmpty: 'Маршрут ещё не задан',
      cargoEmpty: 'Груз не описан',
      /* Карточка контейнеров показывает четыре и сворачивает остальные. */
      moreContainers: 'ещё {{count}}',
      /* Прибыль работы — карточкой в «Обзоре» (доработка S-11). */
      profit: 'Прибыль',
    },

    legs: {
      carriesExpenses:
        'Плечо нельзя удалить, пока по нему учтены затраты: {{items}}. Сначала перенесите или удалите их.',
      add: 'Добавить плечо',
      edit: 'Изменить плечо',
      /* Доступное имя карточки: она теперь ССЫЛКА на страницу плеча, а
         не блок, открывающий форму. Называет и номер, и сам маршрут — в списке
         из четырёх карточек «Открыть плечо» четыре раза подряд не
         говорит, какое из них открывают. */
      open: 'Открыть плечо {{n}}: {{from}} → {{to}}',
      carrier: 'Перевозчик',
      /* Выбор перевозчика из справочника (FR-40). Предлагаются два вида из
         семи — контрагент, который везёт, и владелец транспорта, — и вид
         подписан в строке: это разные отношения, а одна и та же компания
         иногда и то и другое. */
      carrierPlaceholder: 'Выберите перевозчика…',
      carrierSearch: 'Название перевозчика…',
      /* Для сохранённого перевозчика, которого нет в справочнике: имя из
         накладной, вписанное до того, как поле стало выбором. Остаётся
         выбранным — запись не ошибочна, она старше элемента управления. */
      carrierAsEntered: 'как введено',
      /* Для связанного перевозчика, которого потом отключили в справочнике.
         Тоже остаётся выбранным: перевозка состоялась. */
      carrierInactive: 'отключён',
      /* Запасной выход внутри списка: перевозчика, который сейчас перед
         глазами, в справочнике чаще всего ещё нет, и если заставить выйти из
         направления ради его заведения, направление сохранят вообще без
         перевозчика. */
      carrierAdd: 'Добавить перевозчика',
      /* Заголовок окна справочника, которое открывает эта кнопка: оно
         называет то, что добавляют (QW-2, находка D-4). */
      carrierNew: 'Новый перевозчик',
      /* Кто везёт плечо (S-10): один перевозчик — по имени, несколько —
         числом, имена за раскрытием. Фура без своего перевозчика считается
         под перевозчиком плеча. */
      carriersCount_one: '{{count}} перевозчик',
      carriersCount_few: '{{count}} перевозчика',
      carriersCount_many: '{{count}} перевозчиков',
      carriersList: 'Перевозчики плеча',
      reference: 'Номер документа',
      /* Номер документа направления — по бумаге, на которой едет вид
         транспорта, как в шапке страницы плеча (S-8): CMR на авто, накладная
         на остальных известных видах. Пока вид не выбран или неизвестен —
         «Номер документа» выше. Подсказка на авто говорит, где второй
         «№ CMR»: у каждой фуры он свой. */
      referenceCmr: '№ CMR',
      referenceWaybill: '№ накладной',
      referenceCmrHint: 'У каждой фуры свой № CMR — в карточке «Фуры»',
      /* Страна рядом с каждой точкой маршрута (FR-19). Само МЕСТО остаётся
         прежним — терминал, разъезд, площадка, — а это второй, структурный
         факт, по которому группируется отчёт по коридору. */
      originCountry: 'Страна отправления',
      destinationCountry: 'Страна назначения',
      /* Сводная строка секции: «2 плеча · ж/д + авто через Хоргос». */
      count_one: '{{count}} плечо',
      count_few: '{{count}} плеча',
      count_many: '{{count}} плеч',
      legN: 'Плечо {{n}}',
      /* Документ направления — словами отрасли: CMR на авто, накладная на
         остальных. */
      waybill: 'Накладная',
      cmr: 'CMR',
      notIssued: 'не выписана',
      /* Транспортное средство рейса, подписано по виду транспорта (FR-26).
         Одна подпись «рейс / номер рейса» заставляла бы диспетчера каждый раз
         соображать, какая половина про него; карточка и форма вид уже знают. */
      flightNumber: 'Рейс',
      voyageNumber: 'Рейс судна',
      /* Кнопка, за которой на авто- и ж/д-плече лежит рейс, пока его нет
         (QW-8): у фуры его не бывает. Контейнеры — единицы плеча (S-18b), в
         форме их больше нет. */
      revealVoyage: 'Рейс',
      trucks: 'Фуры',
      /* Контейнеры парами КНТР/пломба (FR-67) — счётчик на карточке плеча. */
      containers: 'Контейнеры',
      /* Незапломбированный контейнер так и пишется. Пустое значение означает
         «ещё не опломбирован» или «неизвестно» — но не «без пломбы», и пробел
         рядом с номером читался бы как контейнер, который пломбировать не
         требовалось. */
      sealNotRecorded: 'пломба не указана',
      datePlanned: '{{date}} · план',
      /* Опоздание против ETA, рядом с фактом: «20 июл (+1 д)». */
      delay: '(+{{count}} д)',
      /* Сколько направление идёт по плану. Приходит с сервера
         (casefile.Leg.TransitDays), а не вычитается здесь: два правила о том,
         какой конец считается, дают два ответа на одном экране. */
      duration: 'Длительность',
      days_one: '{{count}} день',
      days_few: '{{count}} дня',
      days_many: '{{count}} дней',
      /* Ноль дней — это настоящий срок, а не отсутствующий: машина между двумя
         городами грузится и разгружается за утро. Словами, потому что «0 дней»
         читается как направление, которому не проставили даты. */
      sameDay: 'в тот же день',
      /* Срок всего маршрута: первое отправление — последнее прибытие. Без
         счётчика дней рядом: дни считает сервер на каждом направлении, а сумма,
         сложенная здесь, была бы вторым правилом про промежутки между ними. */
      span: '{{from}} — {{to}}',
      spanFrom: 'с {{date}}',
      /* Отметка на плече, где груз сейчас. Какое оно — решает сервер
         (`legnow` в casefile); здесь оно только называется. */
      currentLeg: 'Текущее плечо',
      expenses: 'Затраты',
      plannedExpense: 'Расчётные затраты',
      /* Что уже ушло перевозчику по этому направлению и сколько осталось
         (issue #35, история 6). Считается сервером и складывается на карточку
         из того же чтения денег, что и на странице направления, — две цифры об
         одном долге не разойдутся. */
      paidToCarrier: 'Оплачено',
      carrierOutstanding: 'Остаток перевозчику',
      /* Таблица фур на странице направления (FR-67, история 6). Направление
         обычно заказывают шестью фурами за один звонок: полоса сверху
         подставляет общие данные в новые строки, но у каждой фуры может быть
         свой перевозчик и своя ставка. */
      vehiclesTable: {
        /* «Новые строки:» — с чем рождается новая строка (S-10).
           Перевозчик — перевозчик плеча по умолчанию, ставка и валюта — на эту
           сессию ввода. */
        newRows: 'Новые строки:',
        /* Имя поля в строке таблицы: двадцать одинаковых «Водитель» подряд
           скринридер не различает. */
        cellLabel: '{{column}}, строка {{n}}',
        fillAll: 'всем',
        fillCarrier: 'Перевозчика по умолчанию — во все новые строки',
        fillRate: 'Ставку и валюту по умолчанию — во все новые строки',
        moreFieldsNamed: '№ CMR и контейнер фуры {{plate}}',
        /* Кнопка в окне строки на телефоне; {{unit}} — винительный падеж
           вида единицы («фуру», «контейнер»), cases.legs.units.noun. */
        removeRow: 'Удалить {{unit}}',
        /* Единицу с деньгами удалить нельзя (0056) — вместо кнопки причина.
           {{unit}} — винительный падеж с номером («фуру 01KZ123ABC»),
           cases.legs.units.object. */
        removeHeld: 'Есть затраты или оплаты — удалить нельзя',
        removeHeldNamed: 'Удалить {{unit}} нельзя: есть затраты или оплаты',
        removeRateHeld: 'Ставка ждёт записи — удалить нельзя; «Отмена» сбросит ставку',
        removeRateHeldNamed: 'Удалить {{unit}} нельзя: ставка ждёт записи; «Отмена» сбросит ставку',
        plateHeld: 'Ставка ждёт записи — номер не меняется; «Отмена» сбросит ставку',
        pasteHint: 'Вставьте список номеров — строки добавятся сами',
        pasteRefused_one:
          'Вставлен {{count}} номер, а места осталось на {{room}}: в плече не больше {{max}} единиц. Ничего не добавлено.',
        pasteRefused_few:
          'Вставлено {{count}} номера, а места осталось на {{room}}: в плече не больше {{max}} единиц. Ничего не добавлено.',
        pasteRefused_many:
          'Вставлено {{count}} номеров, а места осталось на {{room}}: в плече не больше {{max}} единиц. Ничего не добавлено.',
        full: 'В плече {{max}} единиц — больше добавить нельзя',
        weightKg: '{{weight}} кг',
        /* Карандаш в строке. Назван по единице, а не по номеру строки: у трёх
           строк одна и та же иконка, и скринридер, читающий «Изменить,
           Изменить», не различает их. */
        editRowNamed: 'Изменить {{unit}}',
        groupedColumns: {
          transport: 'Транспорт', people: 'Перевозчик и водитель', weight: 'Вес, кг',
          rate: 'Ставка', paid: 'Оплачено', actions: 'Действия',
        },
        currency: 'Валюта',
        detailOfNone: 'не детализирует',
        detailOfFailed:
          'Плечо и ставки сохранены, но перепривязать строки плана к выбранной группе не удалось.',
        /* Нетронутый выбор при нескольких строках плана. Раньше он молча
           означал «не детализирует», и ставки фур сохранялись рядом со
           строкой перевозки из квоты — перевозка считалась дважды (обход
           UX, T-1). Теперь вопрос задаётся явно, а сохранение новых ставок
           ждёт ответа. */
        detailOfPlaceholder: 'Выберите строку плана',
        detailOfRequired:
          'Выберите, какую строку плана детализируют ставки, или «не детализирует», если перевозки в плане нет',
        /* Удержанные ставки собраны без ответа, а в плане появилась строка,
           которую они могли бы детализировать: отправить их как есть значит
           посчитать перевозку дважды. Сохранение ждёт, а ответить можно
           после «Отмены» — она сбрасывает эти ставки. */
        detailOfRequiredHeld:
          'Ещё не записанные ставки не детализируют ни одну строку плана, а в плане появилась такая строка. «Отмена» сбросит эти ставки, чтобы можно было выбрать.',
        planLinesLoading: 'Загружаем строки плана…',
        planLinesFailed:
          'Строки плана не загрузились — без них ставки не к чему привязать.',
        /* Факт вне режима правки: ставки этого направления считаются сами по
           себе, а рядом в плане есть строки, которые никто не детализирует.
           Без суммы — клиент деньги не считает. */
        unlinkedWarning: 'Ставки плеча считаются отдельно от строк плана: {{lines}}.',
        quotedLine: '«{{line}}»',
        columns: {
          trailer: 'Прицеп',
          container: 'Контейнер',
          cmr: '№ CMR',
          carrier: 'Перевозчик',
          driver: 'Водитель',
          phone: 'Тел/WhatsApp',
          weight: 'Вес, кг',
          rate: 'Ставка',
          advance: 'Аванс',
          paid: 'Оплачено',
          pay: 'Оплата',
        },
        /* Действие на сохранённой строке. Названо по единице, а не одним
           видимым словом: строк с «Оплатить» несколько, и скринридер, слышащий
           «Оплатить, Оплатить», не знает, за что сейчас заплатит. */
        payRow: 'Оплатить',
        payRowNamed: 'Оплатить за {{unit}}',
        removeRowNamed: 'Удалить {{unit}}',
        /* Чем ставка новой фуры записывается: одной плановой затратой в деньгах
           работы, названной по машине, за которую договорились. НЕ менять
           вслед за глоссарием (QW-2, L7): строка сохраняется как `item`
           затраты, и новая формулировка переименовала бы только будущие
           строки — рядом со старыми получилось бы два названия одного. */
        rateItem: 'Ставка фуры {{plate}}',
        /* Прошла ПОЛОВИНА сохранения, и фраза говорит какая. Более общая
           формулировка заставила бы заново вбивать шесть уже сохранённых
           единиц. */
        savedTrucksRatesFailed_one: 'Плечо сохранено, {{count}} ставка не записана.',
        savedTrucksRatesFailed_few: 'Плечо сохранено, {{count}} ставки не записаны.',
        savedTrucksRatesFailed_many: 'Плечо сохранено, {{count}} ставок не записано.',
        /* Ставка, чей пакет не записался, — та сумма, которую отправит
           «Повторить». Только для чтения: удержанная строка уходит повторно
           как есть, и поле ввода рядом показывало бы не то, что уйдёт. */
        rateHeld: 'Ещё не записана',
        rateHeldMissing: 'Нет курса {{currency}}',
        rateHeldHint: '«Повторить» отправит эту ставку как есть; «Отмена» сбросит её, чтобы ввести заново.',
        /* Две величины, которые в строке можно ввести неверно. Раньше их молча
           отбрасывали: ставка не числом сохраняла фуру вообще без ставки, а вес
           не числом СТИРАЛ уже записанные килограммы — и всё это под успешным
           сохранением. */
        rateInvalid_one: '{{count}} ставка не число — например 1200 или 1 200,50.',
        rateInvalid_few: '{{count}} ставки не числа — например 1200 или 1 200,50.',
        rateInvalid_many: '{{count}} ставок не числа — например 1200 или 1 200,50.',
        weightInvalid_one: '{{count}} вес не в целых килограммах — только цифры.',
        weightInvalid_few: '{{count}} веса не в целых килограммах — только цифры.',
        weightInvalid_many: '{{count}} весов не в целых килограммах — только цифры.',
      },
      /* Единицы плеча (S-18b): фуры, контейнеры, вагоны, авианакладные,
         коносаменты и другое — одна таблица, по карточке на вид. Код вида
         приходит с сервера, слова собираются здесь (lib/legUnits.ts). */
      units: {
        /* Заголовок карточки и группы в окне оплаты. */
        title: {
          truck: 'Фуры',
          container: 'Контейнеры',
          wagon: 'Вагоны',
          awb: 'Авианакладные',
          bl: 'Коносаменты',
          other: 'Другое',
        },
        /* Единица по виду и номеру — там, где раньше печатался голый
           госномер: в затратах, оплатах поставщикам, разнесении. У «другого»
           номер и есть название. */
        label: {
          truck: 'Фура {{number}}',
          container: 'Контейнер {{number}}',
          wagon: 'Вагон {{number}}',
          awb: 'Авианакладная {{number}}',
          bl: 'Коносамент {{number}}',
          other: '{{number}}',
        },
        /* То же в винительном падеже — как дополнение к действию: «Удалить
           фуру 01KZ», «Оплатить за контейнер MSKU…». */
        object: {
          truck: 'фуру {{number}}',
          container: 'контейнер {{number}}',
          wagon: 'вагон {{number}}',
          awb: 'авианакладную {{number}}',
          bl: 'коносамент {{number}}',
          other: '«{{number}}»',
        },
        /* Вид без номера, в винительном падеже: «Удалить фуру». */
        noun: {
          truck: 'фуру',
          container: 'контейнер',
          wagon: 'вагон',
          awb: 'авианакладную',
          bl: 'коносамент',
          other: 'строку',
        },
        /* Строка без номера. */
        rowN: {
          truck: 'Фура {{n}}',
          container: 'Контейнер {{n}}',
          wagon: 'Вагон {{n}}',
          awb: 'Авианакладная {{n}}',
          bl: 'Коносамент {{n}}',
          other: 'Строка {{n}}',
        },
        /* Имя поля в строке: на странице несколько карточек, и «Ставка,
           строка 1» в двух из них скринридер не различил бы. */
        cellLabel: {
          truck: '{{column}}, строка {{n}}',
          container: '{{column}}, контейнер {{n}}',
          wagon: '{{column}}, вагон {{n}}',
          awb: '{{column}}, авианакладная {{n}}',
          bl: '{{column}}, коносамент {{n}}',
          other: '{{column}}, другое, строка {{n}}',
        },
        /* Колонка номера — своя у каждого вида. */
        number: {
          truck: 'Госномер',
          container: '№ контейнера',
          wagon: '№ вагона',
          awb: '№ авианакладной',
          bl: '№ коносамента',
          other: 'Обозначение',
        },
        columns: {
          seal: 'Пломба',
          /* Пломба вагона — ЗПУ (запорно-пломбировочное устройство). */
          zpu: 'ЗПУ',
          unitType: 'Тип',
          /* Сколько ещё должны по единице — с сервера, только для чтения. */
          remaining: 'Остаток',
        },
        /* Чем ставка единицы записывается: плановой затратой, названной по
           единице. НЕ менять задним числом: строка сохраняется как `item`
           затраты. Ставка фуры — прежняя cases.legs.vehiclesTable.rateItem. */
        rateItem: {
          container: 'Ставка контейнера {{number}}',
          wagon: 'Ставка вагона {{number}}',
          awb: 'Ставка по авианакладной {{number}}',
          bl: 'Ставка по коносаменту {{number}}',
          other: 'Ставка {{number}}',
        },
        /* Кнопка на телефоне под карточками. */
        add: {
          truck: 'Добавить фуру',
          container: 'Добавить контейнер',
          wagon: 'Добавить вагон',
          awb: 'Добавить авианакладную',
          bl: 'Добавить коносамент',
          other: 'Добавить строку',
        },
        /* «Новые строки:» у каждой карточки свои; {{kind}} — заголовок
           карточки. */
        defaultCarrier: 'Перевозчик по умолчанию, {{kind}}',
        defaultRate: 'Ставка перевозчика по умолчанию, {{kind}}',
        defaultCurrency: 'Валюта новых строк, {{kind}}',
        fillCarrier: 'Перевозчика по умолчанию — во все новые строки, {{kind}}',
        fillRate: 'Ставку и валюту по умолчанию — во все новые строки, {{kind}}',
        empty: 'Пока ничего не записано.',
        /* Синяя ссылка под карточками: добавляет карточку вида, которого
           режим плеча не показал. */
        addKind: 'Другой вид единицы',
      },
      /* Почему плечо открыто только для просмотра тому, кому перевозчик и
         водитель не показаны (AD-6, #133). Сервер сохранённых перевозчика и
         водителя такому читателю не стирает (`keepIdentity`), поэтому фраза не
         обещает, что сохранение их очистит: страница просто не берёт ввод,
         который не сможет отправить. Одна фраза на всё плечо — шапку,
         «Параметры плеча» и карточки единиц. */
      identityHidden:
        'Перевозчик и водитель по этому плечу вашей роли не показаны, поэтому здесь плечо открыто только для просмотра. Изменить его может тот, чья роль видит контрагентов.',
      /* Здесь был `createdTemplateFailed` и ушёл вместе с поверхностью, которой
         он был нужен: создание больше не держит читателя на форме, когда отказало
         только применение шаблона. Оно ведёт на существующее направление, где над
         пустой цепочкой стоит отказ, а рядом — «Применить шаблон»: обычное
         состояние плеча без коридора плюс одна фраза, а не отдельный режим
         со своим словарём. */
      /* Где направление видели последний раз (FR-24). Сервер передавал это с историей
         3.4, и ни один компонент не выводил. */
      lastLocation: 'Последнее местоположение',
      reportedAt: 'сообщено {{at}}',
      /* Удаление плеча. Отказ здесь и есть контроль (FR-67):
         плечо с затратами удалить нельзя, и сервер называет эти
         затраты. */
      delete: 'Удалить плечо',
      deleteConfirm:
        'Удалить плечо? Вместе с ним уйдут его даты, точки и контейнеры.',
      deleteConfirmAction: 'Удалить плечо',
      emptyTitle: 'Плеч пока нет',
      emptyDescription:
        'Добавьте плечи перевозки, чтобы отслеживать её от отправления до доставки.',
    },

    /* Страница одного направления (issue #35, история 3).
     *
     * Направление стало отдельным адресом, потому что у него появилась ЦЕПОЧКА
     * ТОЧЕК: где груз сейчас, сколько дней положено на каждый переход и когда
     * ожидается следующая точка. Цепочку читают поперёк, а не вниз по колонке
     * шириной в модальное окно.
     *
     * Слова здесь держат два факта раздельно. ЦЕПОЧКА — это позиция груза,
     * СТАТУС — это отчёт о движении: направление может стоять «на границе»,
     * когда последняя пройденная точка — Алматы, и одно слово на оба вопроса
     * половину времени врало бы. */
    legPage: {
      dateLabels: { etd: 'План отправления', eta: 'План прибытия', atd: 'Факт отправления', ata: 'Факт прибытия' },
      /* Путь назад ведёт в тот же раздел, из которого пришли: обе карточки,
         открывающие направление, живут там, и «Обзор» заставил бы искать
         список заново. */
      back: 'назад к работе',
      /* S-18a: оплата перевозчику — вторичная кнопка в строке сводки, левее
         «Параметры плеча». Единственная синяя кнопка страницы — основное
         действие панели сохранения. */
      payCarrier: 'Оплатить перевозчику',
      /* Нейтральная метка: у цепочки есть нормативы, значит позиция
         передвинется сама (история 4). Это ФАКТ об этом направлении, а не
         настройка: цепочка, набранная руками без сроков, никуда не поедет, и
         метка на ней обещала бы движение, которого не будет. */
      autoTag: 'Авто',
      /* Подзаголовок-строка: кто везёт, по какой бумаге, когда выехал и когда
         ждём. Пустые части не печатаются — «Перевозчик — · — · —» это четыре
         разделителя ни о чём. */
      departs: 'выезд {{date}}',
      departed: 'выехал {{date}}',
      arrives: 'ожидаемое прибытие {{date}}',
      arrived: 'прибыл {{date}}',

      routeTitle: 'Точки маршрута',
      /* Нажатие на кружок. Две подписи, потому что это два разных действия, и
         обе называют точку: кружок сам по себе ничего не говорит скринридеру, а
         «Отметить» без названия не сказало бы, какую именно. Название НЕ
         склоняется — произвольный топоним из справочника просклонять нельзя, —
         поэтому оно в кавычках и в именительном падеже. */
      markThrough: 'Отметить прибытие по «{{point}}» включительно',
      clearFrom: 'Снять отметку с «{{point}}» и со всех точек после неё',
      /* Отказ, а не запрет по правам: у отправленного плеча сервер сам
         возвращает отметку в точку отправления (stampDeparture), так что снятие
         отменило бы само себя. Молча ничего не делающий контрол читается как
         сломанная система, поэтому причина названа. */
      departureHeld:
        'Плечо отправлено: отметку в точке отправления снимает смена статуса, а не этот экран.',
      /* Норматив перехода под отрезком между точками. Без форм множественного
         числа: «д.» не склоняется, как и «(+{{count}} д)» у опоздания выше. */
      normDays: '{{count}} д.',
      /* Чьим словом отмечено прибытие. Это разная степень уверенности в том,
         где груз: «факт» — сообщил человек, «авто» — предположила система по
         нормативу. Одно слово на оба случая позволило бы прочитать
         предположение как подтверждение. */
      pointReachedFact: 'факт',
      pointReachedAuto: 'авто',
      pointExpected: 'ожид. {{date}}',
      /* Только для скринридера: какая точка текущая, видно цветом и ореолом, а
         это должно пережить отсутствие цвета. */
      currentPoint: 'Текущая точка',
      /* Переход просрочен. Тон тихий: на коридоре с нормативом это ОБЫЧНОЕ
         событие — позиция передвинется сама, — а строка нужна ради поправки,
         которую может внести только человек. */
      overdueBanner_one:
        'Переход {{from}} → {{to}} просрочен на {{count}} день. Позиция передвинется автоматически; отметьте фактическое прибытие, если груз уже там.',
      overdueBanner_few:
        'Переход {{from}} → {{to}} просрочен на {{count}} дня. Позиция передвинется автоматически; отметьте фактическое прибытие, если груз уже там.',
      overdueBanner_many:
        'Переход {{from}} → {{to}} просрочен на {{count}} дней. Позиция передвинется автоматически; отметьте фактическое прибытие, если груз уже там.',
      /* Название точки НЕ склоняется — «Груз в Сарахс» было бы ошибкой, а
         просклонять произвольный топоним из справочника нельзя. Двоеточие
         оставляет название в именительном падеже и читается как подпись
         действия, а не как оборванная фраза. */
      cargoAtNext: 'Отметить прибытие: {{point}}',
      /* Отказ сервера, ради которого стоит писать отдельную фразу: путь к полю
         («points.2.reached») не подсказывает, что делать. */
      notContiguous:
        'Нельзя отметить прибытие в точку, пропустив предыдущую: отметьте сначала её.',

      /* Правка цепочки руками. Живёт рядом с «Применить шаблон» в шапке
         карточки: оба контрола пишут одну и ту же цепочку, только с разной
         ценой ввода. */
      routeEdit: 'Изменить маршрут',
      routeEditTitle: 'Правка маршрута',
      routeEditSubtitle:
        'Точки в порядке следования, с нормативом на каждый переход. Первая и последняя задают «Откуда» и «Куда» плеча.',
      /* Предупреждение, а не ошибка: ничего не сломалось, это постоянное
         свойство сохранения. Отметки сервер переносит ПО ПОЗИЦИИ, а не по
         названию, поэтому перестановка строк оставляет отметку на месте. */
      routeEditArrivalsPinned:
        'Отметки о прибытии привязаны к позиции в цепочке, а не к названию точки: если переставить строки, отметка останется на своём месте в списке.',
      routeEditTooFew:
        'Маршрут из одной точки невозможен: в нём нет ни одного перехода. Добавьте вторую точку или удалите единственную.',
      routeEditTooMany: 'В маршруте не больше {{max}} точек.',
      routeEditSave: 'Сохранить маршрут',
      routeEditEndsHint: 'Концы плеча подставятся из первой и последней точки',
      arrivalHeldHere: 'На этой позиции записано прибытие',
      movePointUp: 'Переместить точку {{n}} выше',
      movePointDown: 'Переместить точку {{n}} ниже',

      applyTemplate: 'Применить шаблон',
      applyTemplateWarning:
        'Применение заменит текущие точки маршрута вместе с отметками о прибытии. Отменить это нельзя.',
      directionTemplate: 'Шаблон маршрута',
      directionTemplateHint:
        'Точки шаблона копируются в это плечо. Если потом изменить шаблон в настройках, плечо не изменится.',
      directionTemplateNone: 'Без шаблона',

      /* Фуры переехали в `cases.legs.vehiclesTable` (история 6), а с S-18b —
         в карточки единиц (`cases.legs.units`). */
      vehiclesTitle: 'Транспорт',

      stallNote: 'Причина задержки',
      lastLocationHint: 'Откуда пришло последнее сообщение о грузе.',
    },

    /* Добавление направления — отдельная страница `/cases/:caseId/legs/new`.
     *
     * Создание ушло из модального окна по той же причине, что и сама запись:
     * направление — это страница, а форма, которую заполняли в окне и после
     * сохранения открывали как страницу, была последним местом, где это
     * противоречие ещё жило. Слова здесь делают ту же работу, что и в окне:
     * говорят, где спрашивают всё остальное, чтобы никто не искал на этой форме
     * цепочку точек, фуры или деньги. */
    legNew: {
      title: 'Новое плечо',
      create: 'Создать плечо',
      moneyLater: 'Согласовано, оплачено и остаток — после создания.',
    },

    /* «Плечо целиком» (S-18a): карточка над фурами — перевозчик плеча,
       ставка за плечо в своей валюте и остаток, который считает сервер. Ставка
       вводится один раз, пока у плеча нет своей ставки (правило S-10 для
       ставок фур); дальше её правят на экране денег. */
    legWhole: {
      title: 'Плечо целиком',
      carrier: 'Перевозчик плеча',
      carrierHint: 'Меняется в «Параметрах плеча» → «Транспорт».',
      rate: 'Ставка',
      rateLabel: 'Ставка за плечо',
      currencyLabel: 'Валюта ставки за плечо',
      remaining: 'Остаток',
      /* Несколько ставок на уровне плеча — показываем их сумму в USD, как её
         считает сервер, и не даём ввести ещё одну. */
      severalRates: 'несколько ставок, в USD',
      /* Хранится как `item` строки затраты — как «Ставка фуры {{plate}}» у
         фур: переименование изменило бы только строки, записанные потом. */
      rateItem: 'Ставка за плечо',
      rateInvalid: 'Ставка за плечо — не число',
    },
    /* «Параметры плеча» — всё, что не фура, в боковой панели (S-10).
     * «Готово» ничего не записывает: правки ложатся в черновик страницы, а
     * пишет только её кнопка сохранения. */
    legParams: {
      title: 'Параметры плеча',
      open: 'Параметры плеча',
      tabsLabel: 'Разделы параметров плеча',
      tabs: {
        route: 'Маршрут',
        transport: 'Транспорт',
        documents: 'Документы',
        dates: 'Даты',
        status: 'Статус',
      },
      done: 'Готово',
      discardConfirm: 'Отменить изменения в параметрах?',
      keepEditing: 'Продолжить',
      discard: 'Отменить изменения',
      newTemplate: 'Новый',
      addPoint: 'Добавить точку',
      daysUnit: 'дн',
      chainNote:
        'Первая и последняя точка — концы плеча. Дни между точками — норматив перехода: по нему позиция груза движется сама.',
      origin: 'Откуда',
      destination: 'Куда',
      saveAsTemplate: 'Сохранить как шаблон',
      templateName: 'Название шаблона',
      /* Имя занято и скрытым шаблоном: уникальность считается по всем
         шаблонам, без учёта регистра. */
      templateNameTaken: 'Шаблон с таким названием уже есть — возможно, скрытый. Выберите другое.',
      templateNameRequired: 'Назовите шаблон',
      templateNoDays: 'Шаблону нужны дни на каждом переходе — укажите их между точками.',
      templateNameTooLong: 'В шаблоне название точки — не длиннее 120 знаков.',
      endsMissing: 'Не указан конец плеча',
      modeMissing: 'Не выбран вид транспорта',
      editPoints: 'Изменить точки',
      /* Перевозчик самого плеча (#131): тот же, что «Новые строки:» на
         карточке единиц. */
      carrierHint:
        'Новые единицы получают этого перевозчика; единица без своего перевозчика относится к нему.',
    },

    /* Одна кнопка сохранения плеча (S-10) и то, что она сообщает. Каждая
     * часть, которая не записалась, называется своей фразой: «фуры сохранены,
     * ставки нет» — это другое действие, чем «ничего не сохранено». */
    legSave: {
      unsavedLeg: 'Несохранённое плечо',
      unsavedChanges: 'Несохранённые изменения',
      saved: 'Все изменения сохранены',
      detailOf: 'Ставки детализируют',
      detailOfHeld: 'Как у ещё не записанных ставок',
      detailOfHeldHint: '«Повторить» отправит ставки с этим ответом; «Отмена» сбросит их, чтобы ответить заново.',
      discardConfirm: 'Отменить несохранённые изменения?',
      missing: 'Не заполнено: {{fields}}',
      missingFix: 'Заполнить: {{fields}}',
      missingOrigin: 'откуда',
      missingDestination: 'куда',
      missingMode: 'вид транспорта',
      overCap: 'В плече не больше {{max}} единиц.',
      nothingToSave: 'Нет несохранённых изменений',
      readOnlyNoRight: 'Ваша роль не меняет маршруты — плечо открыто только для просмотра.',
      readOnlyClosed: 'Работа закрыта — плечо открыто только для просмотра.',
      legFailed: 'Плечо не сохранено.',
      chainFailed: 'Плечо сохранено, маршрут не записан.',
      templateFailed: 'Плечо сохранено, шаблон маршрута не создан.',
      markFailed: 'Прибытие не отмечено.',
      retry: 'Повторить',
      /* 409 `leg-units-changed` (S-18b): одна фраза на все виды единиц.
         Страница уже перечитала плечо и наложила ваши правки — поэтому
         «нажмите ещё раз», а не «обновите страницу», которое их бы стёрло. */
      unitsChanged:
        'Единицы плеча уже изменил кто-то другой. Сохранённые показаны, ваши новые строки и правки остались — нажмите «Сохранить» ещё раз.',
      unitsVanished: 'Удалены другим пользователем: {{units}}.',
      /* Ставка, которую нажатие не отправило: её единицы больше нет в плече.
         Называется по виду и номеру — молча её не теряем. */
      ratesUnsent_one: 'Ставка не отправлена — этой единицы больше нет в плече: {{units}}.',
      ratesUnsent_few: 'Ставки не отправлены — этих единиц больше нет в плече: {{units}}.',
      ratesUnsent_many: 'Ставки не отправлены — этих единиц больше нет в плече: {{units}}.',
      /* Кнопка рядом с отказом `retired`: страница старше сервера. */
      reload: 'Обновить страницу',
      unlinkedHint:
        'Если одна из них — эта же перевозка, выберите её в «Ставки детализируют» внизу; если нет — «не детализирует».',
    },

    /* Деньги одного направления (issue #35, история 6).
     *
     * Блок отдаётся ВСЕМ, кто дотягивается до работы, а имя перевозчика внутри
     * него скрывается по полям — поэтому здесь нигде не написано «вашей роли не
     * показано» про цифру. Разделение «аванс / оплачено» — ответ сервера по
     * правилу ATA, и формулировки нарочно не выдают его за то, что посчитал
     * экран. */
    legFinance: {
      title: 'Оплаты перевозчику',
      subtitle: 'Аванс и оплаты по плечу считаются из проведённых платежей',
      agreed: 'Согласовано',
      advance: 'Аванс',
      paidTotal: 'Оплачено всего',
      outstanding: 'Остаток',
      /* Подписи под плитками: по чему считается каждая цифра. */
      units_one: '{{count}} единица',
      units_few: '{{count}} единицы',
      units_many: '{{count}} единиц',
      beforeArrival: 'до прибытия',
      paymentCount_one: '{{count}} платёж',
      paymentCount_few: '{{count}} платежа',
      paymentCount_many: '{{count}} платежей',
      perUnit_one: 'по {{count}} единице',
      perUnit_few: 'по {{count}} единицам',
      perUnit_many: 'по {{count}} единицам',
      /* Нейтральная метка на оплате, прошедшей до прибытия. Это ФАКТ СЕРВЕРА:
         таблица его выводит и никогда не выводит заново из дат рядом. */
      advanceTag: 'аванс',
      /* У записи ещё не было второй пары глаз (0059, L3). Про ДЕНЬГИ это не
         говорит ничего: неподтверждённая оплата входит во все цифры карточки
         наравне с подтверждённой (L4) — поэтому «не подтверждён» про запись, а
         не «не оплачено». */
      pendingTag: 'не подтверждён',
      columns: {
        date: 'Дата',
        item: 'Назначение',
        unit: 'Единица',
        method: 'Способ',
        /* S-12: что плательщик написал основанием платежа, и комиссия банка
           за перевод — отдельной колонкой, никогда не в сумме (AD-11). */
        reference: 'Основание',
        fee: 'Комиссия',
        amount: 'Сумма',
        decisions: 'Действия',
      },
      noSettlements: 'По этому плечу пока ничего не оплачено.',
      /* Список по фурам и действие на каждой строке. Он заменил «сначала
         проведите факт»: ставка без счёта больше не шаг, который нужно пройти
         до оплаты, — оплата признаёт долг тем же запросом. */

      /* «Оплатить перевозчику» — одно окно на долг, платёж и привязку.
         Формулировки нигде не обещают, что цифры кого-то ждут: платёж входит в
         суммы сразу, подтверждение — проверка записи (L4). */
      pay: {
        /* S-18a: одна оплата на несколько фур одного перевозчика или на плечо
           целиком. Заголовок — существительное, как в макете: окно — это
           оплата, а кнопка, которая его открывает, — «Оплатить перевозчику». */
        title: 'Оплата перевозчику',
        submit: 'Записать оплату',
        atomicNote:
          'Сохранение сразу записывает затраты по отмеченным строкам, один платёж перевозчику и связывает их. Финансы проверят платёж позже, но в суммах он учитывается сразу.',
        /* Сначала перевозчик: от него зависит, какие строки можно отметить, —
           одной оплатой платят одному перевозчику из справочника. */
        carrier: 'Перевозчик',
        /* Перевозчик без записи в справочнике (только имя, или никто): сервер
           не может сверить, что строки — одного перевозчика, поэтому одной
           оплатой только одна строка. */
        offDirectory: 'Перевозчик не из справочника',
        offDirectoryOne:
          'Перевозчик не из справочника — одной оплатой оплачивается одна строка.',
        /* Валюта, в которой уходят деньги: в ней и суммы строк «К оплате», и
           итог. */
        currency: 'Валюта оплаты',
        whatHeading: 'Что оплачиваем',
        columns: {
          unit: 'Единица',
          remainder: 'Остаток',
          amount: 'К оплате',
        },
        wholeLeg: 'Плечо целиком',
        /* Строка, которую уже оплатили целиком: серая, отметить нельзя. */
        paidTag: 'оплачено',
        /* У фуры (или у плеча) несколько счетов или несколько ставок — какой
           из них гасит эта оплата, решают на экране денег. */
        several: 'Несколько счетов — оплатите на экране денег',
        /* S-22d: новый долг записывается на перевозчика из справочника. Если
           перевозчик фуры (или плеча, когда у фуры своего нет) вписан
           текстом, строку не отметить — фраза говорит, где это исправить. */
        unitCarrierNotInDirectory: 'Выберите перевозчика фуры из справочника',
        legCarrierNotInDirectory: 'Выберите перевозчика плеча из справочника',
        noRows: 'У этого перевозчика на плече нечего оплачивать.',
        chooseRows: 'Отметьте, что оплачиваем.',
        rowAmount: 'К оплате: {{unit}}',
        /* Единственная сумма, которую считает клиент (S-18a): точное сложение
           строк. Сервер всё равно сверяет её с суммой строк. */
        total: 'Итого к оплате',

        /* Долг по строке — только там, где оплата признаёт затрату в том же
           запросе (ставка без счёта или вовсе без ставки). Свёрнут: обычно
           долг равен ставке. */
        debtHeading: 'Долг',
        debtSummary: 'Долг: {{amount}}',
        debtAmount: 'Сумма долга',
        debtFromPlan:
          'Ставка перевозчика по договорённости — {{amount}}. Измените, если перевозчик выставил другую сумму.',
        debtUnplanned:
          'Ставки не было — затрата проведётся как внеплановая, на сумму долга.',
        /* Своя валюта долга (S-4.1, доработки, D1). */
        debtCurrency: 'Валюта долга',
        /* Цифра плана в другую валюту не переносится: 3 000 USD превратились
           бы в 3 000 CNY, поэтому поле пустое. */
        debtOtherCurrency:
          'Ставка перевозчика по договорённости — {{amount}}. Укажите долг в {{currency}}: согласованная сумма в другую валюту не переносится.',
        /* Две валюты — курсы двух разных дней (L3). */
        debtPaymentCurrencies:
          'Долг и платёж в разных валютах: курс между датой работы и датой платежа меняется, поэтому может остаться остаток, а распределение сверх долга будет отклонено.',
        debtComment: 'Причина',
        debtCommentHint: 'Необязательно. Останется при затрате, никому не отправляется.',
        /* В разных валютах сервер сравнивает в USD по курсам работы; у
           клиента курсов нет. */
        debtCommentInUsd:
          'В другой валюте долг сравнивается с согласованными {{amount}} в USD, по курсу на дату регистрации работы. Если больше — нужна причина.',
        debtCommentRequired:
          'Это больше согласованных {{amount}} — напишите почему. Причина останется при затрате.',

        paymentHeading: 'Платёж',
        /* Получатель в самом платеже. Со S-18a окно начинается с выбора
           «Перевозчик», поэтому поле платежа подписано по своей роли —
           получатель, а не бухгалтерский «Контрагент» общей формы (F1).
           Заполняется именем выбранного перевозчика и правится. */
        payee: 'Получатель платежа',
        payeeHint: 'Кому уходит оплата — по умолчанию выбранный перевозчик.',
        payeePlaceholder: 'Выберите перевозчика',
      },
      unavailableTitle: 'Не удалось прочитать деньги по этому плечу',
      unavailableDescription:
        'Ничего не потеряно. Попробуйте снова — если не проходит, возможно, нет курса на дату одной из сумм.',
    },

    team: {
      title: 'Команда',
      /* Раздел теперь стоит сам по себе, а не карточкой сбоку от «Обзора», и
         должен сказать, что добавление ДЕЛАЕТ: членство и есть доступ (AD-5,
         FR-56), а не оформление. Формулировка та же, что у котировки. */
      hint: 'Коллеги, которые ведут эту работу. Добавленный увидит её со следующего запроса.',
      assign: 'Назначить коллегу',
      colleague: 'Коллега',
      assignHint:
        'Работа станет ему видна со следующего запроса. Отключённых учётных записей в списке нет.',
      owner: 'Ответственный',
      remove: 'Убрать {{name}} из команды',
    },
    money: {
      /* `forbiddenTitle` и `forbiddenDescription` УДАЛЕНЫ, а не перенесены. Они
         описывали состояние, которого сервер не может выдать: по AD-5 деньги
         работы доступны её команде, а тот, кто до работы не дотягивается,
         получает 404 на саму работу. На деле они выводились для работы без
         ставки — состояния, с которого начинается каждая работа, созданная
         напрямую, и все 241 перенесённых. */
      /* Блока нет вовсе: не удалось прочитать данные или курс под ними. Называет
         и препятствие, и причину, и никогда не извиняется. */
      unavailableTitle: 'Не удалось прочитать деньги по этой работе',
      unavailableDescription:
        'Ничего не потеряно. Попробуйте снова — если не проходит, возможно, нет курса на дату одной из сумм.',
      /* Ещё ничего не внесено — это не то же самое, что «не показываем».
         Говорит, для чего раздел, и предлагает действие (UX-DR-17). */
      untouchedTitle: 'По этой работе пока ничего не внесено',
      untouchedDescription:
        'Внесённые затраты появятся здесь рядом с расчётом. Согласованную ставку выставляют «Финансы» в самой работе.',
      /* Карточка строк затрат на «Затратах» (доработка S-11). */
      title: 'Строки затрат',
      /* Подзаголовок называет правило всей карточки (FR-36): расчёт пришёл из
         принятой котировки, ставка меняется только через согласование. */
      subtitleFromQuote:
        'Статьи расчёта перенесены из котировки {{number}} · ставка зафиксирована, изменения — через согласование',
      subtitle: 'Ставка зафиксирована — изменения через согласование',
      recordActual: 'Внести фактическую затрату',
      /* Две пометки вверху карточки денег (S-12, направление A). Ни одна не
         меняет цифр: двойной счёт решает сервер одним SQL-условием, а
         неразнесённый платёж ни к чему не применяется сам (AD-11). «по работе»,
         а не «по делу» из макета: «дело» — выведенное слово глоссария. */
      notices: {
        doubleCountTitle_one: 'Ставки фур считаются отдельно от строки плана {{lines}}',
        doubleCountTitle_few: 'Ставки фур считаются отдельно от строк плана: {{lines}}',
        doubleCountTitle_many: 'Ставки фур считаются отдельно от строк плана: {{lines}}',
        doubleCountTitleUnnamed: 'Ставки фур считаются отдельно от строк плана',
        doubleCountBody:
          'Перевозка может считаться дважды: прогноз затрат завышен, прибыль занижена.',
        doubleCountLegs: 'Таких плечей: {{count}}, откроется первое.',
        openLeg: 'Открыть плечо',
        unallocatedTitle: 'Есть неразнесённые платежи по работе: {{amounts}}',
        allocate: 'Разнести',
        allocateBlocked: 'Разносить платежи могут только «Финансы»',
      },
      addExpense: 'Добавить затрату',
      /* Новый факт по плановой строке, которая его ждёт (S-4.1, доработки):
         иначе счёт за запланированную затрату вносился вне плана рядом с
         планом, и прогноз учитывал затрату дважды. */
      planLink: 'Плановая статья',
      planLinkNone: 'Без плана — новая затрата',
      planLinkHint: 'Если затрата уже есть в плане, выберите её — факт встанет к этой статье.',
      editExpense: 'Изменить затрату',
      /* Статья, которую эта форма править не может. У расчёта, закрытого
         НЕСКОЛЬКИМИ счетами (AD-4), нет одной фактической суммы — свёрнутой
         форме нечего держать, и сервер отклоняет такое сохранение (409). Текст
         называет и статью, и число фактов: «нельзя изменить» без того и другого
         читается как поломка, а не как факт про эту конкретную статью. */
      severalActualsTitle: 'Закрыта несколькими счетами',
      severalActualsDescription:
        'По статье «{{description}}» к одному расчёту отнесено фактов: {{lines}} — одной фактической суммы для правки здесь нет. Изменяйте счета там, где они были внесены.',
      /* Удаление статьи убирает СТАТЬЮ целиком: и расчёт, и все факты по нему,
         в одной транзакции. Раньше удалялся только факт, а расчёт оставался на
         экране, будто ничего не удалили. */
      deleteExpense: 'Удалить статью',
      /* Две иконки в самой строке. Названы ПО СТАТЬЕ, а не только по
         действию: в таблице из восьми затрат восемь одинаковых карандашей,
         и скринридер, читающий «Изменить, Изменить», не различает строки. */
      editExpenseNamed: 'Изменить «{{line}}»',
      deleteExpenseNamed: 'Удалить «{{line}}»',
      deleteExpenseConfirm: 'Удалить эту статью затрат?',
      deleteExpenseConfirmAction: 'Удалить статью',
      /* Оплачена ли затрата (S-4.2). Галочки «Оплачено» больше нет: статус
         выводит сервер из платежей, разнесённых на фактические затраты строки
         (ожидающие подтверждения тоже считаются), и таблица пишет его словом
         рядом с названием. У плана без факта статуса нет — счёта ещё нет. */
      settlement: {
        unpaid: 'Не оплачено',
        'partly-paid': 'Частично оплачено',
        paid: 'Оплачено',
      },
      actualHint: 'Оставьте пустым, пока затрата не понесена. Отклонение считается само.',
      /* Примечание к факту (S-22d): рядом с контрагентом, уходит вместе с
         фактом и скрыто вместе с ним. */
      note: 'Примечание',
      noteHint: 'Сохраняется вместе с фактом.',
      /* Факт называет контрагента из справочника, поэтому вносит его только
         тот, кто видит контрагентов. Продажи по-прежнему вносят план. */
      actualNeedsCounterparties:
        'Факт, контрагента и примечание вносит сотрудник с доступом к контрагентам.',
      /* Затрата без плана (S-4.1). Новая строка принимает план, факт или оба;
         пустые оба — отказ, и сказан он под планом. Внеплановая строка остаётся
         внеплановой: план у неё только для чтения, а факт обязателен — без него
         у строки не осталось бы ни одной суммы. */
      planOrActualRequired: 'Укажите план или факт',
      plannedHintNew: 'Можно оставить пустым и внести только факт — строка будет вне плана.',
      actualRequired: 'Укажите факт. Чтобы убрать внеплановую затрату, удалите её.',
      unplanned: 'Вне плана',
      unplannedHint: 'Вне плана: затрата внесена без плана',
      /* У каждой стороны своя валюта: план в долларах, счёт в юанях — обычное
         дело. Подписи не повторяют «Расчёт» и «Факт» с заглавной: поле суммы
         находят по ним, а валюта — отдельный вопрос рядом. */
      plannedCurrency: 'Валюта расчёта',
      actualCurrency: 'Валюта факта',
      totals: 'Итого',
      /* Под строкой, которую она объясняет, а не в баннере над карточкой —
         сама строка держит жёлтую заливку, пока Суперадмин решает. */
      passThroughPending: 'Перевыставляется клиенту, ждёт согласования',
      expensesEmptyTitle: 'Статей затрат пока нет',
      expensesEmptyDescription:
        'Статьи расчёта переносятся из котировки; факт вносится по мере поступления счетов.',
      /* Из чего складывается сумма к получению (FR-32, FR-33). `tranchePaid`
         ушёл вместе с `ReceivableTranche.settled`: чтобы сказать, оплачен ли
         отдельный транш, нужны распределения именно по нему, а блок отдаёт лишь
         общую сумму к получению — различие, которого клиент не может посчитать,
         он не должен и рисовать. */
      trancheExpected: '№{{n}} {{amount}} — ожидается {{date}}',
      trancheUndated: '№{{n}} {{amount}} — дата не согласована',
      /* Полоса с датами. Транш без даты не просрочен: вторая половина 50-50
         создаётся без даты и ждёт доставки. */
      tranchesTitle: 'Ожидаемые поступления',
      trancheN: 'Транш {{n}}',
      trancheDue: 'Ожидается',
      /* Собственное имя пикера, и оно называет ТРАНШ: у работы 50-50 их два
         подряд, а серия, прочитанная как «Ожидается, Ожидается», — серия, с
         которой ничего нельзя сделать. */
      trancheDueNamed: 'Ожидаемая дата транша {{n}}',
      /* Две колонки, по которым выравнивается серия. Сказаны один раз над
         колонкой, а не в каждой строке — см. заметку в TrancheStrip. */
      trancheColumns: { tranche: 'Транш', share: 'Доля', amount: 'Сумма' },
      /* Имя самой строки. Номер — это СМЫСЛ серии («второй транш» — то, что
         спрашивают в работе), а общее число рядом превращает «1» в половину,
         а не в целое. */
      trancheOfSeries: 'Транш {{n}} из {{total}}',

      /* Одна прибыль на работе (S-6). Слово одно — «Прибыль», а три
         показателя FR-30 различает уточнение: «план», «прогноз», «итог».
         Справа в строке стоит та цифра, которую выбрал сервер: прогноз, пока
         работа идёт, итог — когда она финансово закрыта или отменена. Как
         получена каждая цифра, показывает панель «Как посчитано»: только
         слагаемые с сервера, клиент ничего не складывает сам. */
      profit: {
        lead: 'Прибыль:',
        planned: 'план',
        forecast: 'прогноз',
        final: 'итог',
        noRate: 'Ставка не согласована',
        howCalculated: 'Как посчитано',
        rows: {
          planned: 'План',
          forecast: 'Прогноз',
          final: 'Итог',
          cashToDate: 'Деньги на сегодня',
        },
        terms: {
          rate: 'Ставка',
          plannedCost: 'плановые затраты',
          billedExtras: 'доп. счета',
          debitNoteRecoveries: 'дебет-ноты',
          cost: 'затраты',
          received: 'Получено и разнесено',
          paid: 'оплачено',
        },
        cashToDateNote: 'Станет итогом после финансового закрытия',
      },

      payments: {
        heading: 'Платежи по работе',
        /* «Счета клиенту» показывает только входящие (доработка S-11). */
        receivedHeading: 'Полученные платежи',
        receivedEmptyTitle: 'Поступлений пока нет',
        receivedEmptyDescription:
          'Поступления вносятся в разделе «Финансы» → «Платежи» и появляются здесь вместе с тем, что они закрывают.',
        emptyTitle: 'Платежей пока нет',
        emptyDescription:
          'Приход и расход вносятся в разделе «Финансы» → «Платежи» и появляются здесь вместе с тем, что они закрывают.',
        unallocated: '{{amount}} ещё не распределено',
        /* Пять фактов о платеже, по колонке на каждый. «Комиссия» — своя
           колонка и никогда не часть «Суммы»: сумма — то, что ЗАКРЫЛОСЬ,
           комиссия — во сколько обошёлся перевод, и складывать их нельзя
           нигде (AD-11). */
        columns: {
          payment: 'Платёж',
          fee: 'Комиссия',
          unallocated: 'Не распределено',
          amount: 'Сумма',
          /* В таблице и входящие, и исходящие платежи (P-1, QW-2). */
          date: 'Дата платежа',
        },
      },

      columns: {
        description: 'Статья',
        counterparty: 'Контрагент',
        planned: 'Расчёт',
        actual: 'Факт',
        line: 'Статья затрат',
        vendor: 'Контрагент',
        leg: 'Плечо',
        plannedIn: 'Расчёт {{currency}}',
        actualIn: 'Факт {{currency}}',
        variance: 'Отклонение',
        actions: 'Действия',
      },
      /* За что заплатили, ссылкой на страницу плеча (FR-67, история 6).
         Номер фуры идёт рядом, когда строка её называет: «какое плечо» и
         «какая фура» — один вопрос на рабочем месте. */
      legLink: 'Плечо {{n}}',
      legLinkUnit: 'Плечо {{n}} · {{unit}}',
      /* Затрата, которая не относится ни к одному движению, — оформление,
         банковская комиссия. Это законно и часто, поэтому сказано словами:
         пустая клетка читалась бы как строка, которую забыли заполнить. */
      unattributed: 'не привязан',
      /* План, детализированный по фурам (спека
         2026-09-01-expense-detail-grouping). Собственная цифра родителя в
         итогах больше не считается, поэтому она сказана ЗДЕСЬ — как бенчмарк,
         которым стала: что заложил план и насколько от него ушли реально
         законтрактованные ставки. При разных валютах дельты нет — вычитать их
         друг из друга нельзя, — и подпись остаётся счётом строк. «План», а не
         «квота» (T-4): квотой эту цифру звала рабочая книга, читатель зовёт её
         планом. Параметр `quota` в ключе остаётся — это имя для кода. */
      groupNote: 'план {{quota}} · {{delta}} · строк: {{count}}',
      groupNoteMixed: 'детальных строк: {{count}}',
      toggleGroup: 'Показать/скрыть детали {{line}}',
    },

    documents: {
      title: 'Счета и дебет-ноты',
      /* Отличие от «Файлов»: здесь то, что мы выставили сами, а не то, что
         прислали и приложили. */
      subtitle: 'Документы, выставленные по этой работе, и что по ним не закрыто.',
      raise: 'Новый документ',
      emptyTitle: 'Пока ничего не выставлено',
      emptyDescription:
        'Здесь появятся счета и дебет-ноты по этой работе. Новый документ создаётся черновиком — выставление это отдельный шаг.',
      /* Ссылка, а не кнопка «Изменить»: счёт правится на своей странице. */
      openInvoice: 'Открыть счёт',
    },

    /* «Бумаги работы» — список только для чтения вверху «Документов» работы
       (S-11; старый `?section=documents` теперь псевдоним и ведёт сюда
       читателя без рабочего места Финансов): какие документы есть по работе,
       без сумм и без действий Финансов. */
    papers: {
      title: 'Бумаги работы',
      /* Сказано один раз над списком, а не отказом в каждой строке. */
      subtitle:
        'Счета и дебет-ноты выставляют Финансы — здесь видно, что и когда выставлено. PDF счетов и дебет-нот открывают Финансы.',
      document: 'Документ',
      /* Вид документа, которого эта сборка ещё не знает. */
      unknownKind: 'Документ',
      openLabel: 'Открыть: {{document}}',
      loading: 'Загружаем бумаги работы',
      emptyTitle: 'Бумаг по работе пока нет',
      emptyDescription:
        'Здесь появятся выставленные счета и дебет-ноты, а также статусы для клиента.',
    },

    /* Рабочее место Финансов под «Счетами клиенту» в «Деньгах» работы (S-11;
     * `?section=documents` теперь псевдоним и ведёт сюда): согласованная ставка,
     * график платежей по ней и бумага, которая по этому графику ушла клиенту.
     *
     * Отдельная группа рядом с `documents`, а не внутри неё, потому что это два
     * разных вопроса на одном экране: «сколько ещё можно выставить» и «что уже
     * отправлено». Слово «работа» — как во всём финансовом словаре здесь. */
    invoices: {
      rateTitle: 'Ставка по котировке',
      rateSubtitle:
        'Сколько согласовано с клиентом, сколько уже выставлено и чем закрывается остаток.',
      fromQuote: 'по котировке {{quote}}',
      rate: 'Ставка',
      billed: 'Выставлено',
      paid: 'Оплачено',
      headroom: 'Можно выставить ещё',
      /* Цвет никогда не несёт смысл в одиночку: полоса — это украшение над
         цифрами, которые и так напечатаны рядом, а легенда называет каждую
         полосу словами. */
      legendPaid: 'оплачено клиентом',
      legendAwaiting: 'выставлено, ждём оплаты',
      legendFree: 'ещё не выставлено',
      /* Что именно внутри «выставлено». Сервер считает потолок по ВСЕМ
         позициям ВСЕХ счетов работы, включая черновики (`BilledOnCase`), и по
         этой же сумме откажет в следующей строке. Читатель, решивший, что
         считаются только выпущенные счета, увидит остаток больше того, что
         разрешит запись. */
      billedCountsDrafts:
        'В «выставлено» входят и черновики: потолок ставки считается по всем позициям всех счетов работы, и по этой же сумме система откажет в следующей строке.',
      instalment: 'Платёж',
      expected: 'Ожидается',
      /* Транш без даты — не просрочка: вторую половину при схеме 50/50 заводят
         без даты нарочно, она ждёт даты поставки, которой пока никто не знает. */
      undated: 'дата не согласована',
      notBilled: 'счёт не выставлен',
      /* Выпущенный счёт снимает транш с учёта ЦЕЛИКОМ, на какую бы сумму он ни
         был выставлен: сервер спрашивает «есть ли выпущенный счёт на этот
         транш», а не «покрывает ли он его». Поэтому счёт на 500 против транша
         на 2 000 тихо убирает 1 500 из ожидаемых поступлений. Строка раньше
         показывала сумму транша рядом с номером такого счёта — единственное
         прочтение, которое заведомо неверно. */
      billedShort: 'выставлено {{billed}} из {{expected}} {{currency}} — остаток больше не ожидается',
      raiseInvoice: 'Выставить счёт',
      raising: 'Создаём…',
      noSchedule:
        'График платежей не заведён — ставка собирается одним счётом. Выставить можно и так: такой счёт просто не закрывает ни один транш.',
      /* Роли без коммерческого доступа денежный блок не приходит вовсе (AD-6).
         Это не «ставки нет» — это «мы не вправе показать», и путать их нельзя. */
      rateWithheldTitle: 'Деньги по этой работе не показываются',
      /* Ради этой фразы раздел и переделывали: расходы работы — это её
         себестоимость, и в счёте клиенту им делать нечего. */
      expensesNote:
        'Расходы работы на этот счёт не влияют: они складываются в себестоимость и перевыставляются клиенту дебет-нотой — отдельным документом.',
    },

    files: {
      title: 'Файлы',
      upload: 'Загрузить',
      takePhoto: 'Фото',
      pending: 'В очереди',
      uploading: 'Отправка…',
      /* Загрузивший файл из очереди — тот, кто смотрит на экран: строки на
         сервере ещё нет, имя брать неоткуда. */
      you: 'Вы',
      /* Story 8.2, UX-DR-19. Четыре формы, а не две: в русском счётная форма
         зависит от последней цифры, и «3 файла» против «5 файлов» — это не
         стилистика, а грамматика. i18next выбирает по правилам плюрализации. */
      queuedTitle_one: '{{count}} файл ждёт отправки',
      queuedTitle_few: '{{count}} файла ждут отправки',
      queuedTitle_many: '{{count}} файлов ждут отправки',
      queuedTitle_other: '{{count}} файлов ждут отправки',
      queuedSending:
        'Сейчас отправляются. Можно уйти со страницы — они дойдут сами.',
      /* Здесь названа сама гарантия: файлы лежат на устройстве и уйдут сами.
         Это то обещание, из-за которого не придётся возвращаться и
         переснимать перегрузку. */
      queuedOffline:
        'Связи нет. Файлы сохранены на устройстве и уйдут сами, когда появится сигнал, — приложение можно закрыть.',
      sendNow: 'Отправить сейчас',
      dropHint:
        'Перетащите файлы сюда или нажмите «Загрузить». С телефона можно снять фото сразу.',
      uploadFailedTitle: 'Файл не отправился',
      /* Называет следующее действие, а не ошибку: с «PUT вернул 403» сделать
         нечего, а «добавьте файл снова» — можно. */
      uploadFailedBody:
        'По нему ничего не сохранено. Проверьте связь и добавьте файл ещё раз.',
      emptyTitle: 'Файлов пока нет',
      emptyDescription:
        'Здесь появятся накладные, упаковочные листы и фото перегрузки по этой работе.',

      /* Тип документа спрашивается один раз на всю выбранную пачку (0045).
         «Какой это документ» — вопрос про бумагу, а не про формат файла:
         сорок PDF, сгруппированных по «PDF», сгруппированы ни по чему. */
      kindTitle: 'Что это за документ',
      kind: 'Тип документа',
      kindHint:
        'По типу файлы группируются в списке — так нужную бумагу находят, не открывая каждую.',
      kindFieldHint: 'Не уверены — оставьте «Прочее», тип видно и его можно исправить.',
      kindSubmit_one: 'Добавить {{count}} файл',
      kindSubmit_few: 'Добавить {{count}} файла',
      kindSubmit_many: 'Добавить {{count}} файлов',

      /* Названия типов. Это ПОДПИСИ к кодам с сервера — на проводе едет
         'cmr', а не «CMR»: иначе группировка зависела бы от языка читателя, и
         два коллеги видели бы одну работу по-разному (AD-27). */
      kinds: {
        cmr: 'CMR',
        awb: 'Авианакладная',
        'rail-waybill': 'Ж/д накладная (СМГС)',
        'bill-of-lading': 'Коносамент',
        invoice: 'Инвойс',
        'packing-list': 'Упаковочный лист',
        'customs-declaration': 'Таможенная декларация',
        certificate: 'Сертификат',
        contract: 'Договор',
        photo: 'Фото',
        other: 'Прочее',
      },

      /* Удаление. Название файла подставляется в подпись кнопки, потому что
         строк в списке много, а «Удалить» на каждой — это кнопка, про которую
         с клавиатуры не понять, что она удалит. */
      delete: 'Удалить «{{name}}»',
      deleteConfirm: 'Удалить «{{name}}»? Файл исчезнет и из хранилища.',
      deleteConfirmAction: 'Удалить',
      deleteFailed: 'Файл не удалён',
      deleteFailedHint:
        'Он остался на месте. Проверьте связь и попробуйте ещё раз.',
    },

    /* История работы: ОДНА лента из двух источников — журнал действий и
       финансовый аудиторский след. Разделять их в тексте нечем и незачем:
       человек спрашивает «когда это изменилось и кто», а не «в какой из двух
       таблиц это лежит».

       Ключи `actions.*` буквально повторяют машинные ключи из
       `backend/cmd/asa/actions.go`, из которых снят префикс `case.`:
       "case.status.change" → `actions.status.change`. Совпадение буквальное
       НАМЕРЕННО — лента переводит ключ напрямую и при отсутствии перевода
       печатает сам ключ, поэтому маршрут, добавленный в actions.go завтра,
       портит одну строку, а не гасит раздел.

       Голос — тот же, что у `quotes.history.actions` ниже: короткое
       предложение в страдательном залоге о том, ЧТО произошло, а не о том, на
       что нажали. «Статус изменён», не «Пользователь изменил статус»: кто
       именно — лента печатает отдельной строкой, и повторять это в каждом
       заголовке значит писать имя дважды. */
    history: {
      title: 'История',
      count_one: '{{count}} событие',
      count_few: '{{count}} события',
      count_many: '{{count}} событий',
      /* Пусто — значит пусто по ОБОИМ источникам. Прежний текст обещал только
         финансовые изменения; с появлением журнала действий это перестало быть
         правдой, и обещание расширено до всего, что с работой делали. */
      emptyTitle: 'Пока ничего не происходило',
      emptyDescription:
        'Здесь появится всё, что делали с этой работой: действия команды и финансовые изменения — кто и когда.',
      /* Окна времени. «За всё время» выбрано по умолчанию: история одной
         работы — десятки записей, и сузить её это осознанный шаг, а не
         состояние, в котором экран открывается. Формулировки те же, что у
         периода в отчётах, — один и тот же выбор, сказанный двумя разными
         фразами, читается как два разных выбора. */
      filters: {
        all: 'За всё время',
        today: 'Сегодня',
        yesterday: 'Вчера',
        week: 'За неделю',
      },
      searchPlaceholder: 'Поиск по истории…',
      /* Отдельно от `emptyTitle` выше, и это разница по существу: у работы без
         истории снимать нечего, а здесь есть что — и текст называет, что
         именно снять. */
      noMatchTitle: 'Ничего не найдено',
      noMatchDescription:
        'Измените запрос или выберите «За всё время», чтобы снять фильтр по времени.',
      /* Снова отдельно от `emptyTitle` — и по той же причине, по которой
         скелет ждёт оба источника: если один отказал, а во втором ничего нет,
         «Пока ничего не происходило» это утверждение об истории, прочитанной
         наполовину. Неизвестно — не то же самое, что пусто. */
      unknownTitle: 'Историю не удалось прочитать целиком',
      unknownDescription:
        'Один из двух источников не ответил, а во втором записей нет — поэтому неизвестно, происходило ли с этой работой что-нибудь. Повторите загрузку выше.',
      /* Отказ ОДНОГО источника не гасит раздел: второй показывается как есть,
         а плашка называет, чего в ленте не хватает. Молчаливая половина
         истории читается как вся история — и именно так рождается «этого
         никто не менял». */
      loadFailed: {
        actions: 'Журнал действий не загрузился — в ленте пока только финансовые изменения.',
        audit: 'Финансовые изменения не загрузились — в ленте пока только действия команды.',
        retry: 'Повторить',
      },
      /* Лента не листается, и предел есть у обоих источников: у следа — тот,
         который запрашивает клиент, у журнала действий — тот, который сервер
         накладывает сам, потому что параметра предела у этого чтения нет.
         Когда предел достигнут, об этом говорят вслух — обрезанная лента,
         которая молчит, выглядит как полная. Фраза у каждого источника своя:
         достигнутый предел одного ничего не говорит о другом. Числа
         подставляются, а не написаны здесь: иначе рост предела молча оставил
         бы во фразе старое. */
      truncated: {
        actions: 'Показаны последние {{limit}} записанных действий.',
        audit: 'Показаны последние {{limit}} финансовых изменений.',
      },
      audit: {
        reason: 'Причина',
        /* Строка финансового следа: «<запись> · <поле>» и значения «было →
           стало». Сервер хранит машинные имена, коды и короткие английские
           фразы, и старые записи не переписать — слова подставляет клиент
           (features/cases/lib/auditWording.ts). Словарь сервера — файл
           backend/internal/finance/testdata/audit_phrases.golden.json; тест
           требует здесь подпись для каждой записи и каждого поля из него. */
        records: {
          quote: 'Котировка',
          case: 'Работа',
          invoice: 'Счёт',
          debit_note: 'Дебет-нота',
          payment: 'Платёж',
          expense_line: 'Статья затрат',
          rate_change_request: 'Изменение ставки',
        },
        fields: {
          case: {
            billed_total: {
              /* Сумма, перенесённая из таблиц при переходе на систему, без
                 согласования — поэтому и отдельное поле. */
              migration_bypass: 'Выставлено клиенту (перенос данных)',
            },
            rate: 'Ставка',
            receivableTranche: 'Транш',
            status: 'Статус',
            trancheExpectedDate: 'Ожидаемая дата транша',
          },
          debit_note: {
            amount: 'Сумма',
            counterparty: 'Контрагент',
            line_amount: 'Сумма позиции',
            note_date: 'Дата',
            number: 'Номер',
            status: 'Статус',
          },
          expense_line: {
            amount: 'Сумма',
            detail_of: 'Детализирует строку плана',
            remarks: 'Примечание',
            stands_alone: 'Не детализирует строку плана',
            vendor_id: 'Контрагент из справочника',
            vendor_name: 'Поставщик',
          },
          invoice: {
            amount: 'Сумма',
            invoice_date: 'Дата счёта',
            invoice_number: 'Номер',
            line_amount: 'Сумма позиции',
            status: 'Статус',
            vat: 'НДС',
          },
          payment: {
            /* Куда разнесён платёж — значение называет сам транш, счёт,
               дебет-ноту или статью затрат. `expense` — прежнее имя поля,
               новые записи идут под `expense_item`. */
            allocation: {
              tranche: 'Разнесение',
              invoice: 'Разнесение',
              debit_note: 'Разнесение',
              expense_item: 'Разнесение',
              expense: 'Разнесение',
            },
            amount: 'Сумма',
            /* Ссылки на записи (0080): значение — id, поэтому строка истории
               говорит «Задано / Изменено / Снято», а имя рядом —
               `counterparty_name`. */
            client_id: 'Клиент из справочника',
            counterparty_id: 'Контрагент из справочника',
            counterparty_name: 'Контрагент',
            direction: 'Направление',
            fee: 'Комиссия за перевод',
            fee_amount: 'Комиссия за перевод',
            fx_settlement_difference: 'Курсовая разница',
            issuing_entity: 'Наше юрлицо',
            issuing_entity_id: 'Наше юрлицо',
            method: 'Способ оплаты',
            received_on: 'Дата платежа',
            reference: 'Основание',
            status: 'Статус',
          },
          rate_change_request: {
            requested_rate: 'Запрошенная ставка',
            status: 'Статус',
          },
        },
        values: {
          /* Поле, значение которого — идентификатор (наше юрлицо, строка
             плана, контрагент дебет-ноты). Идентификатор не печатается:
             читателю нужно «изменено», а не uuid. */
          reference: {
            set: 'Задано',
            changed: 'Изменено',
            cleared: 'Снято',
          },
          vat: {
            none: 'без НДС',
            rate: 'НДС {{rate}}%',
          },
          allocation: {
            invoice: 'Счёт',
            debitNote: 'Дебет-нота',
            vendorUnstated: 'поставщик не указан',
            /* S-21: отменённое разнесение и перенос с транша на счёт при
               выставлении (auditWording.ts wordAllocationMove). Номер счёта
               дописывается после фразы, не подставляется. */
            reversed: 'Разнесение отменено',
            carried: 'Перенесено с транша {{n}} на счёт',
            carriedOf: 'Перенесено с транша {{n}} из {{total}} на счёт',
          },
          trancheDate: {
            expected: '№{{n}} — ожидается {{date}}',
            undated: '№{{n}} — дата не согласована',
          },
          debitNoteStatus: {
            draft: 'Черновик',
            issued: 'Выставлена',
          },
          rateChangeStatus: {
            pending: 'Ожидает',
            approved: 'Согласовано',
            rejected: 'Отклонено',
          },
        },
      },
      actions: {
        create: 'Работа создана',
        update: 'Данные работы изменены',
        status: {
          change: 'Статус изменён',
        },
        reopen: 'Работа открыта заново',
        leg: {
          add: 'Добавлено плечо',
          update: 'Плечо изменено',
          remove: 'Плечо удалено',
          /* Правка маршрута точка за точкой и шаблон, который ПЕРЕЗАПИСЫВАЕТ
             весь маршрут, — разные действия, и лента называет их по-разному:
             иначе перезапись тонет среди правок. */
          points: {
            replace: 'Маршрут плеча изменён',
          },
          template: {
            apply: 'К плечу применён шаблон маршрута',
          },
          /* Отметку ставит платформа, а не человек, и лента говорит это
             прямо. */
          'auto-advance': 'Точка маршрута отмечена автоматически',
        },
        /* Участники — внешние стороны перевозки; команда ниже — сотрудники
           ASA. Пара стоит рядом, потому что путаются именно эти два, и
           различие несут сами слова, а не порядок ключей. */
        participant: {
          add: 'Добавлен участник',
          update: 'Участник изменён',
          remove: 'Участник убран из работы',
        },
        team: {
          add: 'Сотрудник добавлен в команду',
          remove: 'Сотрудник выведен из команды',
        },
        /* `remove` в ленте работы не появится: удаление файла пишется в журнал
           под сущностью "file", а не "case". Перевод стоит здесь всё равно —
           он стоит одну строку и избавляет следующего читателя от вывода,
           будто словарь неполон. То же ниже у `status-update.send-bulk`. */
        file: {
          attach: 'Файл прикреплён',
          remove: 'Файл удалён',
        },
        /* Два ключа, один текст, и это не копипаста. Обе записи означают одно:
           в переписку по работе что-то написали. Придумывать им разные
           заголовки значит показать читателю различие, по которому он всё
           равно ничего не сделает. */
        message: {
          post: 'Сообщение отправлено в чат',
        },
        chat: {
          post: 'Сообщение отправлено в чат',
        },
        /* Массовая отправка называет свои работы в теле запроса, а не в пути,
           поэтому запись остаётся без id работы и ни в одну ленту не
           попадает. Переведена по той же причине, что и `file.remove`. */
        'status-update': {
          send: 'Клиенту отправлен статус',
          'send-bulk': 'Статус отправлен по нескольким работам',
        },
        rate: {
          set: 'Ставка установлена',
        },
        /* Построчные записи (`record`, `update`, `delete`) и записи затраты
           целиком — план вместе с фактами (`save`, `revise`, `remove`) —
           разные действия: одна запись второго рода может означать две строки
           сразу, и «Статья затрат удалена» нарисовала бы читателю одну. */
        expense: {
          record: 'Добавлена статья затрат',
          update: 'Статья затрат изменена',
          delete: 'Статья затрат удалена',
          detail: {
            set: 'Изменена привязка затраты к строке плана',
          },
          batch: 'Внесены ставки перевозчиков по фурам',
          save: 'Затрата внесена',
          revise: 'Затрата исправлена',
          remove: 'Затрата удалена целиком',
        },
        payment: {
          record: 'Внесён платёж',
        },
        carrier: {
          pay: 'Записана оплата перевозчику',
        },
        invoice: {
          raise: 'Создан черновик счёта',
          update: 'Счёт изменён',
          line: {
            add: 'В счёт добавлена позиция',
            update: 'Позиция счёта изменена',
            delete: 'Позиция счёта удалена',
          },
          render: 'По счёту сформирован документ',
          discard: 'Черновик счёта удалён',
          issue: 'Счёт выставлен',
        },
        'debit-note': {
          raise: 'Создан черновик дебет-ноты',
          update: 'Дебет-нота изменена',
          line: {
            add: 'В дебет-ноту добавлена позиция',
            update: 'Позиция дебет-ноты изменена',
            delete: 'Позиция дебет-ноты удалена',
          },
          render: 'По дебет-ноте сформирован документ',
          issue: 'Дебет-нота выставлена',
        },
        tranche: {
          date: {
            set: 'Назначена дата транша',
          },
        },
      },
    },

    /* Панель чата одна на работу и котировку (ChatPanel не знает, чья она),
       поэтому её собственные слова называют команду, а не запись: «Чат по
       работе» на котировке снова называл бы котировку работой (QW-2, Q-3).
       Запись называют шапка дока (`dock.case` / `dock.quote`) и страница чата. */
    chat: {
      label: 'Чат команды',
      placeholder: 'Написать команде',
      send: 'Отправить сообщение',
      heading: 'Чат',
      internalOnly: 'Только для своих',
      you: 'Вы',
      sending: 'Отправляется…',
      latest: 'К последним',
      /* Story 8.3. Переписка грузится страницами и опрашивается, пока панель
         на экране, — эти строки описывают состояния, которых у витринной
         ленты не было: первая страница в пути, догрузка истории и сообщения,
         пришедшие, пока человек читал выше. */
      loading: 'Загружаем переписку',
      failed: 'Не удалось загрузить переписку.',
      empty: 'Пока ничего не написали. Начните разговор.',
      earlier: 'Что было раньше',
      loadingEarlier: 'Загружаем…',
      /* Плашка «вниз» после того, как что-то пришло ниже читателя. Счёт назван
         прямо: вернуться к одному сообщению и к трём — разные дела. */
      unread_one: '{{count}} новое сообщение',
      unread_few: '{{count}} новых сообщения',
      unread_many: '{{count}} новых сообщений',
      /* Имя кнопки в шапке на телефоне для скринридера. Заменяет обычную
         подпись, а не дополняет её, — счёт должен звучать как часть того, зачем
         кнопка, а не как довесок в конце. */
      openWithUnread_one: 'Чат команды, {{count}} новое сообщение',
      openWithUnread_few: 'Чат команды, {{count}} новых сообщения',
      openWithUnread_many: 'Чат команды, {{count}} новых сообщений',
      messageLabel: 'Сообщение',
      sendHint: 'Enter — отправить · Shift + Enter — новая строка',
      attach: 'Прикрепить файлы',
      removeAttachment: 'Убрать {{name}}',
      dropHere: 'Отпустите, чтобы прикрепить',
      tooLarge: '{{name}} больше 50 МБ — файл не прикреплён.',
      tooMany: 'К сообщению можно приложить {{count}} файлов.',
      /* Разделители дней между сообщениями. Сначала два названных дня, потом
         дата: «сегодня» и «вчера» — это то, с чем читатель сверяет рабочую
         переписку, а абсолютная дата для сказанного четыре часа назад
         заставляет считать в уме. */
      today: 'Сегодня',
      yesterday: 'Вчера',
      /* Переписка по котировке, из которой создана работа (S-1, решение O3):
         свёрнутый блок над чатом работы, только чтение. Ссылка на котировку —
         только тому, кто может её открыть. */
      archive: {
        title: 'Переписка по котировке (архив)',
        readOnly: 'Только чтение: переписка до создания работы.',
        openQuote: 'Открыть котировку {{number}}',
        quote: 'Котировка {{number}}',
        truncated_one: 'Показано последнее {{count}} сообщение.',
        truncated_few: 'Показаны последние {{count}} сообщения.',
        truncated_many: 'Показаны последние {{count}} сообщений.',
        truncated_other: 'Показаны последние {{count}} сообщения.',
      },
      /* Док на карточке записи (FR-46, UX-DR-13, 0052). Отдельная группа:
         здесь названо само ОКНО — его шапка и три кнопки, — тогда как всё
         выше описывает переписку внутри, а она та же самая, что и на
         странице чата. */
      dock: {
        case: 'Чат по работе',
        quote: 'Чат по котировке',
        subject: '{{number}} · {{client}}',
        internal: 'Только свои',
        teamOnly: 'Видно только вашей команде',
        minimise: 'Свернуть чат',
        expand: 'Открыть страницу чата',
        /* Свёрнутое состояние: плавающая кнопка с подписью при наведении.
           Говорит «внутренний чат», а не называет запись, — запись и так на
           экране позади. */
        open: 'Внутренний чат',
        /* Заменяет обычную подпись, а не дополняет её: счёт должен звучать
           как часть того, зачем кнопка. */
        openWithUnread_one: 'Внутренний чат, {{count}} новое сообщение',
        openWithUnread_few: 'Внутренний чат, {{count}} новых сообщения',
        openWithUnread_many: 'Внутренний чат, {{count}} новых сообщений',
      },
    },
  },

  /* Страница чата (0029): переписка по записям, в двух вкладках.
     
     Отдельное пространство имён, а не ещё ключи внутри `cases`: страница
     охватывает и котировки, и работы, так что вложить её в одно из двух
     значило бы неверно назвать половину того, что она показывает.

     До глоссария QW-2 здесь «работой» называлась КОТИРОВКА, а работа —
     «кейсом» (находки Q-3, C-5). Теперь как везде: работа — это работа,
     котировка — котировка; ключи `cases`/`quotes` не менялись. Строки самой панели
     остаются в `cases.chat.*` — это те же слова, и переносить их значит
     тронуть каждое место вызова в ChatPanel без выгоды для читателя.
     
     Словарь взят из существующего каталога, а не переведён заново: в
     интерфейсе «Чат», в прозе «переписка». */
  chat: {
    title: 'Чат',
    subtitle: 'Переписка по котировкам и работам, которые вы ведёте.',
    tabs: {
      cases: 'Работы',
      quotes: 'Котировки',
    },
    emptyCases: 'Переписки по работам пока нет.',
    emptyQuotes: 'Переписки по котировкам пока нет.',
    pickOne: 'Выберите беседу, чтобы прочитать её.',
    loading: 'Загружаем беседы',
    failed: 'Не удалось загрузить беседы.',
    unread_one: '{{count}} непрочитанное',
    unread_few: '{{count}} непрочитанных',
    unread_many: '{{count}} непрочитанных',
    unreadCapped: '99+',
    archived: 'Архив — только чтение',
    archivedWhy: 'По этой котировке создана работа. Переписка продолжается там.',
    edited: 'изменено',
    deletedBody: 'Сообщение удалено',
    edit: 'Изменить',
    editing: 'Правка',
    saveEdit: 'Сохранить',
    cancelEdit: 'Отмена',
    delete: 'Удалить',
    confirmDelete: 'Удалить сообщение? Оно останется в переписке пометкой о том, что что-то убрали.',
    editFailed: 'Не удалось изменить сообщение.',
    deleteFailed: 'Не удалось удалить сообщение.',
    noAttachmentsOnQuotes: 'Файлы прикрепляются к работам, а не к котировкам.',

    /* Собственный фильтр списка (0052). По уже загруженным строкам: сервер
       отдаёт все доступные читателю беседы одним ответом, так что сузить их —
       это проверка на подстроку, а не запрос. Подсказка своя на каждой
       вкладке, потому что и список свой: предлагать искать работы на вкладке
       котировок значило бы описывать строки, которых там нет. */
    search: {
      cases: 'Поиск по работам…',
      quotes: 'Поиск по котировкам…',
      /* Доступное имя поля. Подсказка внутри им не является — она исчезает
         ровно тогда, когда человек начал печатать. */
      label: 'Поиск бесед',
      /* Фильтр ничего не нашёл. Это не то же, что `emptyCases` выше: беседы у
         читателя есть, просто ни одна не подходит под текст. */
      none: 'Под запрос ничего не подошло.',
    },
    /* Последняя строка беседы в её строке списка. Автор и текст одной
       строкой, а не двумя кусками, — чтобы переводчик мог двигать двоеточие. */
    preview: '{{author}}: {{body}}',

    /* Шапка переписки. Называет запись: список рядом можно отмотать от
       выбранной строки, и переписка без заголовка превращается в
       необъяснённый столбец реплик. */
    threadSubtitle: {
      case: '{{client}} · Переписка по работе',
      quote: '{{client}} · Переписка по котировке',
    },
    /* Та же строка для записи, чьего клиента списки ещё не подтянули: лучше
       короткая правда, чем «undefined · …». */
    conversationKind: {
      case: 'Переписка по работе',
      quote: 'Переписка по котировке',
    },
    /* Внутренняя всегда, и сказано об этом дважды намеренно: плашка рядом с
       заголовком — для тех, кто знает, и полоса под ней — для тех, кто нет.
       Внешних участников на этой поверхности нет структурно (FR-46). */
    internal: 'Только свои',
    internalBanner: 'Внутренняя переписка — видна только вашей команде',
    /* Из переписки к самой записи. Ссылка, а не действие, — открывается в
       новой вкладке обычным способом. */
    viewCase: 'Открыть работу',
    viewQuote: 'Открыть котировку',
  },

  quotes: {
    title: 'Котировки',
    subtitle: 'Что мы предложили и сколько это принесёт, если сложится.',
    // Ссылка на раздел у роли с «Нет доступа» к котировкам (#129).
    noAccess: {
      title: 'Раздел недоступен',
      description:
        'Котировки открываются доступом вашей роли к разделу «Котировки». Если он вам нужен, обратитесь к администратору.',
    },
    loading: 'Загрузка котировки',
    backToList: 'К котировкам',
    /* Счётчик в подвале таблицы. Считает то, что видно ПОСЛЕ фильтра: подвал
       стоит под самим списком и должен отвечать за него, а не за набор,
       который экран решил не показывать. */
    count_one: '{{count}} котировка',
    count_few: '{{count}} котировки',
    count_many: '{{count}} котировок',

    /* Пять вариантов оплаты из листа Reference list исходной книги. ХРАНИТСЯ и
       печатается английская строка — это то, что читает клиент в оферте; здесь
       только подписи для нашего же коллеги в выпадающем списке. */
    paymentOptions: {
      'in-advance-payment': 'Предоплата',
      '50-50-payment-upon-pickup-delivery': '50/50 — при загрузке и при выдаче',
      'full-payment-upon-delivery-before-unloading':
        'Полная оплата при выдаче, до разгрузки',
      credit: 'В кредит',
      other: 'Другое — условия ниже',
    },

    /* Тип упаковки груза (F3): выбор хранит КОД (`pallet`), а не слово, и
       каждый читатель — экран и КП — называет код на своём языке. То, что
       человек вписал в «Другое…», не переводится и печатается как есть.
       Слова совпадают с `offerPackages` в backend/internal/quote/offer.go —
       packageTypes.test.ts сверяет их. */
    packageTypes: {
      none: 'Не указан',
      other: 'Другое…',
      otherText: 'Другой тип упаковки',
      /* В списке: именительный падеж, единственное число. */
      options: {
        pallet: 'Паллета',
        crate: 'Ящик',
        box: 'Коробка',
        drum: 'Бочка',
        bag: 'Мешок',
        'big-bag': 'Биг-бэг',
        roll: 'Рулон',
        bale: 'Кипа',
      },
      /* Рядом с числом мест: «1 паллета», «3 паллеты», «12 паллет». */
      counted: {
        pallet_one: 'паллета',
        pallet_few: 'паллеты',
        pallet_many: 'паллет',
        crate_one: 'ящик',
        crate_few: 'ящика',
        crate_many: 'ящиков',
        box_one: 'коробка',
        box_few: 'коробки',
        box_many: 'коробок',
        drum_one: 'бочка',
        drum_few: 'бочки',
        drum_many: 'бочек',
        bag_one: 'мешок',
        bag_few: 'мешка',
        bag_many: 'мешков',
        'big-bag_one': 'биг-бэг',
        'big-bag_few': 'биг-бэга',
        'big-bag_many': 'биг-бэгов',
        roll_one: 'рулон',
        roll_few: 'рулона',
        roll_many: 'рулонов',
        bale_one: 'кипа',
        bale_few: 'кипы',
        bale_many: 'кип',
      },
    },

    /* Выиграно/проиграно за период (FR-15). ЧЬИ это цифры — часть самой
       цифры: у продавца они считаются по его собственным котировкам, и
       прочитать личный показатель как показатель компании — ровно та ошибка,
       от которой защищает AC 6. Поэтому оговорка стоит в самом блоке, а не в
       подсказке. «Истекло» считается отдельно и никогда не сливается с
       «Проиграно»: котировка без ответа — это не отказ клиента. */
    statistics: {
      title: 'Выиграно / проиграно',
      won: 'Выиграно',
      wonHint: 'Переведено в работу',
      lost: 'Проиграно',
      lostHint: 'Отклонено',
      expired: 'Истекло',
      expiredHint: 'Считается отдельно, не как проигрыш',
      conversion: 'Конверсия',
      /* Называет знаменатель, а не описывает его: «выиграно из решённых»
         оставляет читателя гадать, входят ли туда истёкшие. Цифры отвечают
         прямо — и это те же три показателя, что стоят рядом. */
      conversionHint: '{{won}} из {{decided}} решённых',
      wonValue: 'Сумма выигранных',
      scope: {
        own: 'Только ваши котировки',
        assigned: 'Ваши котировки и те, где вы в команде',
        all: 'Все котировки, всех владельцев',
      },
      range: {
        label: 'Период',
        d30: 'Последние 30 дней',
        d90: 'Последние 90 дней',
        year: 'С начала года',
        all: 'За всё время',
      },
      /* Дни, которые сервер действительно посчитал: переключатель называет
         запрошенный период, эта строка — тот, который вернулся. Только даты:
         строка стоит в шапке панели рядом с заголовком, и приставка «учтён
         период» повторяла бы очевидное там, где дорог каждый символ. */
      period: '{{from}} — {{to}}',
      error: 'Не удалось получить показатели побед и проигрышей.',
    },

    /* Липкая панель разделов страницы котировки (S-3): одна прокручиваемая
       страница из пяти разделов, ссылки ведут к ним. Версии, история и
       команда — в меню «⋯» в шапке, а не разделами. */
    sectionsLabel: 'Разделы котировки',
    sections: {
      client: 'Клиент и груз',
      route: 'Варианты доставки',
      pricing: 'Расчёт',
      terms: 'Условия',
      preview: 'Предпросмотр',
    },
    /* Меню «⋯» в шапке. Число в скобках — сколько записей откроется. */
    more: {
      label: 'Ещё',
      revisions: 'Версии ({{n}})',
      history: 'История',
      team: 'Команда ({{n}})',
    },
    /* Блок клиента в начале раздела «Клиент и груз»: то, что спрашивает
       шапка, в одном месте. «Изменить» открывает ту же шторку, что и кнопка
       в шапке. */
    clientBlock: {
      title: 'Клиент и контакт',
      /* Доступное имя кнопки: видимое «Изменить» плюс то, что меняется, —
         в шапке есть своя «Изменить». */
      editLabel: 'Изменить клиента и контакт',
    },
    /* Раздел «Предпросмотр» на черновике: сводка того, что уйдёт, и две
       кнопки — сам документ открывается в шторке, как и раньше. */
    previewSection: {
      hint: 'Так КП увидит клиент. Проверьте документ, прежде чем отправлять.',
      open: 'Открыть предпросмотр',
    },

    actions: {
      newQuote: 'Новая котировка',
      editQuote: 'Изменить котировку',
      /* Возврат в пошаговый конструктор из сохранённой котировки. Подпись
         говорит, что работа ПРОДОЛЖАЕТСЯ, а не что открывается ещё один экран:
         тот, кому это нужно, уже был там — он там же котировку и создал, ушёл и
         вернулся за наценкой, которая живёт только там. Соседнее «Изменить
         котировку» — это шторка с шапкой, другое и меньшее действие; две
         подписи должны различаться. */
      create: 'Создать котировку',
      /* Подпись говорит, что делает нажатие (QW-11): согласие клиента создаёт
         работу. `approveLabel` — доступное имя кнопки, те же слова без
         стрелки, которую экранный диктор зачитал бы вслух. */
      approve: 'Клиент согласовал → создать работу',
      approveLabel: 'Клиент согласовал, создать работу',
      converting: 'Создаём работу…',
      openCase: 'Открыть работу {{number}}',
      newRevision: 'Новая версия',
      reopening: 'Открываем…',
      sendOffer: 'Отправить КП',
      preview: 'Предпросмотр',
      fixCargo: 'Заполнить груз',
    },

    /* Подтверждение перед созданием работы (QW-11, находка C-1). Работы ещё
       нет, поэтому каждая строка — то, что она ПОЛУЧИТ, прочитанное из
       котировки так же, как это сделает сервер. Главное здесь — строки о том,
       чего работа НЕ получит: направления, ставки, условий оплаты как есть,
       ответственного от операций. */
    convert: {
      title: 'Создать работу по {{number}}',
      intro: 'Клиент согласовал. Работа будет создана из котировки в нынешнем виде:',
      confirm: 'Создать работу',
      /* Когда котировка под открытой шторкой больше не «Отправлена», а отказа,
         который объяснил бы это, нет. */
      notSent: 'Котировка больше не ждёт ответа клиента, поэтому создать из неё работу нельзя.',
      client: 'Клиент',
      entity: 'Юрлицо',
      paymentTerms: 'Условия оплаты',
      /* Только для OTHER и формулировок не из списка: в работе они станут
         «другими», а условия работы потом не меняет ни один экран. CREDIT
         с S-12 переносится как кредит со своим сроком. */
      termsCaveat:
        'В работе эти условия станут «другими»: один платёж с датой регистрации. Изменить их в работе потом нельзя.',
      /* Платежи появляются только вместе со ставкой (CreateInTx), поэтому
         без ставки их нет вовсе — что бы ни говорили условия. */
      termsCaveatNoRate:
        'В работе эти условия станут «другими», и платежей не будет: ставка не устанавливается. Изменить условия в работе потом нельзя.',
      termsNoRate: 'Платежей не будет: ставка не устанавливается.',
      /* S-12: кредит без срока в КП. Работа получит кредит с этим числом дней,
         а без него сервер работу не создаст. */
      creditDaysAsk: 'В котировке срок отсрочки не указан — укажите его, и работа получит кредит на эти дни.',
      creditDaysBlocked: 'Укажите дней отсрочки — от 1 до 365',
      incoterm: 'Инкотермс',
      mode: 'Вид перевозки',
      transit: 'Срок в пути',
      leg: 'Плечо',
      legPlanned: 'Одно плановое плечо, {{origin}} → {{destination}}',
      legNoOption:
        'Не будет создано: в котировке не выбран вариант доставки. Операционный отдел добавит его в работе.',
      legNoMode:
        'Не будет создано: у выбранного варианта доставки нет вида перевозки. Операционный отдел добавит его в работе.',
      rate: 'Ставка',
      /* Констатация, а не указание: ставку работы не задаёт ни один экран. */
      noRate: 'Ставка не будет установлена: цена клиенту в котировке равна нулю.',
      plannedCosts: 'Плановые затраты',
      plannedLines_one: '{{count}} статья',
      plannedLines_few: '{{count}} статьи',
      plannedLines_many: '{{count}} статей',
      plannedLines_other: '{{count}} статьи',
      noPlannedLines: 'Плановых затрат не будет: ни в одной статье нет себестоимости.',
      team: 'Команда работы',
      /* S-1: ответственный от операций выбирается здесь же и входит в команду;
         прайсинг и финансы по-прежнему не переходят (решение O1). */
      notJoining:
        'Коллеги из прайсинга и финансов в работу не переходят, из операций — только выбранный ответственный. О созданной работе они всё равно получат уведомление.',
      /* Обязательный выбор ответственного от операций (S-1). Список отдаёт
         сервер: только те, кто может вести работу. */
      owner: {
        label: 'Ответственный (Операции)',
        hint: 'Войдёт в команду работы и получит уведомление.',
        /* Список прочитан и пуст — не «загружается» и не «ошибка». */
        empty: 'Назначить некого: никто не может вести работу. Обратитесь к администратору.',
        /* Подсказка на кнопке «Создать работу», пока никто не выбран. */
        blocked: 'Сначала выберите ответственного (Операции).',
        errors: {
          required: 'Выберите ответственного.',
          notAssignable: 'Этого сотрудника нельзя назначить: учётная запись отключена или удалена. Выберите другого.',
          notOperations: 'Этот сотрудник не может вести работу. Выберите другого из операций.',
        },
      },
      irreversibleTitle: 'Это действие нельзя отменить.',
      irreversible:
        'Котировка закроется, а её чат уйдёт в архив; переписка продолжится в работе.',
      /* Три отказа, которые сервер присылает токеном в detail у `conflict`. */
      errors: {
        /* {{section}} — подпись раздела в боковом меню (`nav.cases`). Ссылки
           не обещаем: чтение котировки отдаёт ссылку на работу только тому,
           чья область видимости работ до неё дотягивается, а этот читатель
           может быть не из них. */
        alreadyConverted:
          'По этой котировке работа уже создана. Найдите её в разделе «{{section}}».',
        notConvertible:
          'Котировка больше не ждёт ответа клиента, поэтому работу из неё создать нельзя. Страница обновлена.',
        moneyNotBookable:
          'Суммы котировки нельзя провести в работе. Проверьте валюту и суммы в статьях затрат и попробуйте снова.',
      },
    },

    /* ВНУТРЕННЯЯ команда по котировке (миграция 0028) — коллеги, сотрудники
       ASA с учётными записями. Внешних сторон у котировки с S-22a нет вовсе
       (миграция 0075 убрала её участников).

       Не переиспользует `cases.team`: работа уже идёт, а котировка ещё не
       выиграна, и «ему это видно» значит на двух экранах разное. */
    team: {
      title: 'Команда',
      /* Прямо сказано, что даёт добавление: на котировке это не оформление —
         членство в команде и есть то, что открывает доступ. */
      hint: 'Коллеги, которые ведут эту котировку вместе с вами. Добавленный увидит её. Необязательно.',
      empty: 'Пока только вы. Добавьте коллегу, если котировку ведёт кто-то ещё.',
      add: 'Добавить коллегу',
      colleague: 'Коллега',
      /* По имени, а не по позиции: коллегу выбирают из списка, а не вводят
         вручную, поэтому имя к моменту чтения подписи уже есть. */
      remove: 'Убрать {{name}} из команды',
      /* Ответственный попадает в команду при создании котировки и остаётся
         единственным, кого нельзя убрать. */
      owner: 'Ответственный',
      you: 'Вы',
      /* Отличает «больше некого добавить» от «список не загрузился» — именно
         так читается пустой выпадающий список, если ничего не сказать. */
      noneAvailable: 'Больше некого добавить.',
      /* Два отказа сервера — словами, по которым понятно, что делать. */
      lastMember: 'Назначьте другого коллегу, прежде чем убрать последнего.',
      notAssignable: 'Эту учётную запись нельзя назначить.',
    },

    /* Создание котировки (UJ-1, FR-4, FR-5).

       Всё, что осталось от трёхшагового мастера. Шаги убраны: у каждого был
       двойник на странице котировки. Здесь спрашивается только то, что несёт
       запрос на создание, остальное заполняется уже в самой котировке. */
    create: {
      title: 'Новая котировка',
      subtitle: 'Для кого предложение. Всё остальное заполняется в самой котировке.',
      identity: 'Клиент и груз',
      identityHint: 'Для кого предложение и что едет.',
      // Ссылка на /quotes/new у роли без права создавать котировки (#128).
      noAccess: {
        title: 'Создание котировок недоступно',
        description:
          'Новую котировку открывает право «Создавать черновик котировки». Если оно вам нужно, обратитесь к администратору.',
      },
    },
    /* «Готовность к отправке» (S-3): что нужно серверу для отправки и что
       только желательно. Строки — ссылки на разделы; те же строки дают
       отметки на панели разделов. Состояние словами — цвет один ничего не
       говорит. */
    readiness: {
      required: 'Нужно для отправки',
      advised: 'Желательно',
      row: {
        cargo: 'Груз',
        recipient: 'Получатель',
        route: 'Вариант доставки',
        pricing: 'Расчёт',
        validity: 'Срок действия',
      },
      done: 'готово',
      gap: {
        issues_one: '{{count}} замечание',
        issues_few: '{{count}} замечания',
        issues_many: '{{count}} замечаний',
        issues_other: '{{count}} замечания',
        noPositions: 'нет позиций',
        noContactAccess: 'нет доступа к контактам клиента',
        noRecipient: 'у контактов клиента нет почты',
        noRoute: 'ещё не добавлен',
        noCostLines: 'нет статей затрат',
        noValidity: 'не указан',
      },
      /* Подпись отметки на панели разделов для экранного диктора и подсказки. */
      marker: '{{row}}: {{gap}}',
    },

    /* Карточки-справки рядом с обоими экранами котировки — только чтение. Ни
       один факт не живёт здесь единственным экземпляром: всё это есть и в
       основной колонке, поэтому на узком экране рейл можно просто убрать. */
    aside: {
      quote: 'Котировка',
      number: 'Номер',
      /* Номер выдаёт сервер (FR-5). Сказано словами, чтобы никто не искал поле,
         которого нет. */
      numberPending: 'будет присвоен при создании',
      status: 'Статус',
      owner: 'Владелец',
      readiness: 'Готовность к отправке',
      offerCarries: 'В КП попадёт',
      /* Тёмная карточка денег. Три цифры на ней подписаны словарями расчёта и
         наценки — `quotes.costLines.customerTotal`, `quotes.pricing.cost`,
         `quotes.pricing.marginOverRevenue`, — потому что те же величины стоят
         под теми же подписями на вкладке затрат. Второй словарь для одной
         величины — это две величины для того, кто читает бегло. */
      money: 'Деньги по котировке',
      /* Показывается, только пока расчёт пуст: три нуля без объяснения похожи
         на карточку, которая не загрузилась. */
      moneyNote:
        'Пока нет ни одной статьи затрат — цифры появятся здесь, как только расчёт заполнится.',
    },

    form: {
      origin: 'Откуда',
      destination: 'Куда',
      mode: 'Вид перевозки',
      incoterm: 'Инкотермс',
      /* Наша фраза о правиле, а не название самого правила: коды ИКЦ в обоих
         каталогах остаются английскими. */
      incotermSeaOnly: 'только море и внутренние водные пути',
      quoteDate: 'Дата котировки',
      paymentTerms: 'Условия оплаты',
      /* S-12: срок кредита. Спрашивается, только пока выбран «Кредит», и
         переносится в работу как отсрочка в днях от регистрации. */
      creditDays: 'Дней отсрочки',
      creditDaysHint: 'от 1 до 365',
      creditDaysValue_one: '{{terms}}, {{count}} день',
      creditDaysValue_few: '{{terms}}, {{count}} дня',
      creditDaysValue_many: '{{terms}}, {{count}} дней',
      transitDays: 'Срок доставки по запросу клиента, дней',
      transitDaysMin: 'Минимум дней',
      transitDaysMax: 'Максимум дней',
      transitInverted: 'Минимальный срок не может быть больше максимального.',
      /* Девять полей шапки (FR-4 / FR-11 / FR-59), которые до 17.08.2026 нельзя
         было заполнить ни на одном экране: колонки в `quotes` были, DTO их
         отдавал, а формы не спрашивали. */
      clientContact: 'Контакт у клиента',
      /* Называет человека, которому уйдёт КП, если контакт не выбран: одного
         «необязательно» мало — читатель не знает, отправится ли КП вообще. */
      clientContactDefault: 'По умолчанию — {{name}}',
      /* Сервер отказал: контакт не из списка этого клиента (клиента сменили,
         или контакт удалили из справочника). */
      clientContactNotOfClient: 'Контакт не принадлежит этому клиенту — выберите заново',
      cargoOwner: 'Владелец груза',
      /* Подсказка внутри поля вместо слова «Необязательно» после подписи. Она
         отвечает на вопрос, на который то слово не отвечало, — как выглядит
         хороший ответ, — и не добавляет полю ни строчки высоты. */
      cargoOwnerPlaceholder: 'Если это не клиент',
      cargoDescription: 'Описание груза',
      cargoDescriptionPlaceholder: 'Мрамор в слэбах, 15 паллет',
      pickupLocation: 'Место забора груза',
      dealTermsLegend: 'Коммерческие условия',
      demurrageTerms: 'Демередж после свободного времени',
      demurrageTermsHint: 'Как договорились, словами — например, 80 USD/сутки за контейнер.',
      deliveryTerms: 'Условия доставки',
      /* Говорит, чем это НЕ является: выше в той же форме стоит Инкотерм, и их
         легко перепутать. */
      deliveryTermsHint: 'Как доставка происходит на практике — это не Инкотерм.',
      carrierDisplayLegend: 'Строка перевозчика в КП',
      carrierDisplay: 'Перевозчик или поставщик, как печатается',
      /* Вторая часть критерия AC 2 истории 2.1 — там, где иначе это поле начнут
         использовать как поле вендора. */
      carrierDisplayHint:
        'Печатается в КП только для информации. Не назначает вендора ни одной строке затрат.',
      validUntilHint: 'После этой даты котировка показывается как истёкшая.',
      measures: 'Вес и объём',
      grossWeightOverride: 'Вес брутто, кг',
      volumeOverride: 'Объём, м³',
      overrideHint:
        'Оставьте пустым — возьмём сумму по позициям. Значение здесь заменит её в КП.',
      chargeableWeightHint: 'На всю отправку, если перевозчик даёт одну цифру.',
      finalPackingList: 'Финальный упаковочный лист получен',
      finalPackingListHint:
        'Объёмы выше — клиентские, а не наша оценка. Останется видно в работе.',
      nextStep: 'Груз и статьи затрат добавляются в самой котировке.',
    },
    columns: {
      number: 'Котировка',
      client: 'Клиент',
      route: 'Маршрут',
      /* Описание груза из шапки котировки. Заголовок короткий — колонка узкая,
         а «Описание груза» перенесётся на две строки над ячейкой, в которой и
         так одна обрезанная строка. */
      cargo: 'Груз',
      status: 'Статус',
      validUntil: 'Действует до',
      sell: 'Продажа USD',
      margin: 'Маржа USD',
    },
    /* Квитанция за перетаскивание по доске: что и куда, одной фразой,
       с отменой рядом. */
    board: {
      moved: '{{number}} — теперь «{{status}}»',
      /* Пустая колонка — словами. Приглашение вынесено в ОТДЕЛЬНЫЙ ключ: оно
         верно только там, куда действительно можно перетащить. Над
         «Конвертирована» такая строка учила бы жесту, который эта колонка
         не примет. */
      laneEmpty: 'Нет котировок',
      laneDrop: 'Перетащите сюда для изменения статуса.',
    },
    card: {
      /* Метка отправленной версии рядом с датой: «версия A · 26 июл» — тем же
         словом, что и на экране КП; «Rev» здесь никто не говорит. */
      rev: 'версия {{label}}',
      total: 'Итого',
      margin: 'Маржа',
      validTo: 'Действует до',
      /* Тишина со стороны клиента, как её называют на планёрке. */
      noAnswer_one: 'без ответа {{count}} д',
      noAnswer_few: 'без ответа {{count}} д',
      noAnswer_many: 'без ответа {{count}} д',
      /* Смешанный груз считаем позициями, а не называем один тип упаковки. */
      positions_one: '{{count}} позиция',
      positions_few: '{{count}} позиции',
      positions_many: '{{count}} позиций',
      /* Что мешает отправить КП. На карточке — счёт, в самой котировке — что
         именно исправить (FR-3). «Не заполнена» — как говорят про позицию. */
      blocked_one: '{{count}} позиция не заполнена',
      blocked_few: '{{count}} позиции не заполнены',
      blocked_many: '{{count}} позиций не заполнено',
    },
    /* Как у работ (QW-9, C-7): список отбирает сервер, правило не названо. */
    emptyTitle: 'Нет доступных котировок',
    emptyDescription:
      'Здесь видны котировки, доступные вам. Если ждёте котировку коллеги — попросите добавить вас в её команду.',

    cargo: {
      title: 'Груз',
      add: 'Добавить позицию',
      edit: 'Изменить позицию',
      position: 'Позиция',
      hsCode: 'Код ТН ВЭД',
      packages: 'Мест',
      /* Число мест уходит на сервер целым: «2,5» — не опечатка в формате, а
         не то число. */
      packagesInvalid: 'Введите целое число мест',
      packageType: 'Тип упаковки',
      weight: 'Вес, кг',
      volume: 'Объём, м³',
      /* В таблице груза, вместо числа: объём не измерен. */
      missing: 'Не указан',
      /* Объём нужен для ОТПРАВКИ, не для сохранения (FR-12), и подсказка
         говорит именно это (QW-9, Q-6): «Обязательно» подталкивало вписать
         выдуманное число. */
      volumeRequired: 'Нужен для отправки КП. Позицию можно сохранить без него.',
      volumeHint: 'Сверяется с букингом до отправки КП.',
      /* Сборный груз — обычное дело: короба трёх отправителей в одной машине,
         и в КП должно быть видно, чьё что. */
      shipper: 'Отправитель',
      shipperHint: 'Только если эта позиция едет от другого отправителя.',
      /* Вводится, а не считается: делитель — перевозчика, не наш. */
      chargeableWeight: 'Оплачиваемый вес, кг',
      chargeableWeightHint: 'Только если перевозчик считает не по фактическому весу.',
      totals: 'Итого',
      overriddenMark: 'вручную',
      overriddenFrom: 'Задано вручную. По позициям выходит {{value}}.',
      partialTotal: 'По части позиций данных нет — итог неполный.',
      packingListReceived: 'Финальный упаковочный лист получен',
      dimensions: 'Габариты, см',
      lengthCm: 'Длина',
      widthCm: 'Ширина',
      heightCm: 'Высота',
      /* Как подсказка у объёма: все три нужны для отправки, а не для
         сохранения (FR-12, N-15). Предупреждение, а не отказ. */
      dimensionsRequired:
        'Нужны все три для отправки КП. Позицию можно сохранить без них.',
      isChemical: 'Химический груз',
      chemicalNote:
        'Для химического груза нужны MSDS и сертификат на перевозку — без них КП не отправить.',
      hasMsds: 'MSDS получен',
      hasTransportCertificate: 'Сертификат на перевозку получен',
      chemical: 'Химия',
      readyTitle: 'Готово к отправке',
      readyDescription: 'Данные по грузу заполнены, КП можно отправлять.',
      emptyTitle: 'Груза пока нет',
      emptyDescription:
        'Добавьте позиции, на которые считаем КП. Пока они не заполнены, КП не отправить.',
      delete: 'Удалить позицию',
      deleteConfirm: 'Удалить позицию? Действие необратимо.',
      deleteConfirmAction: 'Удалить',
    },

    /* Чек-лист считает, что отделяет черновик от отправленного КП. */
    checklist: {
      blocked_one: '{{count}} пункт мешает отправить КП',
      blocked_few: '{{count}} пункта мешают отправить КП',
      blocked_many: '{{count}} пунктов мешают отправить КП',

      /* Пункт списка: чего не хватает и что это блокирует. Склейка здесь, а не
         в коде — тире и порядок частей у каждого языка свои. */
      line: '{{issue}} — {{blocks}}',

      /* Чего не хватает. Позицию называем по номеру — рядом таблица, где
         менеджер считает строки глазами. Форма без номера нужна для позиции,
         которую ещё не пронумеровали. */
      issue: {
        dimensionsMissing: 'Не указаны габариты в позиции {{position}}',
        dimensionsMissing_noPosition: 'Не указаны габариты',
        volumeMissing: 'Не указан объём в позиции {{position}}',
        volumeMissing_noPosition: 'Не указан объём',
        msdsMissing: 'Нет MSDS в позиции {{position}}',
        msdsMissing_noPosition: 'Нет MSDS',
        certificateMissing: 'Нет сертификата на перевозку в позиции {{position}}',
        certificateMissing_noPosition: 'Нет сертификата на перевозку',
      },

      /* Что именно это блокирует. Для химии причина строгости названа прямо —
         иначе непонятно, почему к этой позиции требований больше. */
      blocks: {
        send: 'КП не отправить',
        sendChemical: 'КП не отправить — груз химический',
      },
    },

    routes: {
      title: 'Варианты доставки',
      transitDays_one: '{{count}} день',
      transitDays_few: '{{count}} дня',
      transitDays_many: '{{count}} дней',
      /* Короткие формы для строк A/B/C, где полная фраза не помещается. */
      daysShort: 'дн',
      transitUpTo: 'до {{count}} дн',
      transitFrom: 'от {{count}} дн',
      emptyTitle: 'Вариантов доставки пока нет',
      emptyDescription: 'Просчитанные варианты доставки появятся здесь — из них клиент и выбирает.',
      add: 'Добавить вариант',
      /* Пунктирная плитка в пустой ячейке сетки. Называет, ЗАЧЕМ нужен ещё один
         маршрут, а не само действие: действие уже названо кнопкой в шапке
         карточки, а два элемента с одним именем — два элемента, которые
         скринридер не различит. */
      addNext: 'Вариант {{letter}} — клиенту на выбор',
      edit: 'Изменить вариант',
      origin: 'Откуда',
      points: 'Точки маршрута',
      noPoints: 'Между отправлением и назначением точек нет.',
      addPoint: 'Добавить точку',
      removePoint: 'Удалить точку {{n}}',
      movePointUp: 'Поднять точку {{n}}',
      movePointDown: 'Опустить точку {{n}}',
      pointName: 'Название точки {{n}}',
      pointCountry: 'Страна точки {{n}}',
      pointDays: 'Дней до точки {{n}}',
      pointPlaceholder: 'Пункт',
      daysPlaceholder: 'Дней',
      destinationDays: 'Дней до назначения',
      applyDirection: 'Применить шаблон',
      applyDirectionHint:
        'Шаблон копируется: первая точка — в «Откуда», последняя — в «Куда», остальные — в точки. Правки шаблона в настройках потом вариант не меняют.',
      destination: 'Куда',
      mode: 'Вид перевозки',
      indicativeTotal: 'Ориентировочная сумма, USD',
      indicativeHint:
        'Показывается на вариантах B и C. Выбранный вариант считается по статьям затрат.',
      selected: 'Выбран',
      select: 'Выбрать',
      /* Предупреждение FR-3 о перерасчёте: сервер не двигает выбор молча,
         это — его вопрос. */
      selectWarning:
        'Статьи затрат просчитаны под выбранный сейчас вариант. После переключения они будут описывать этот вариант — проверьте их перед отправкой КП.',
      selectConfirm: 'Переключить',
      delete: 'Удалить вариант',
      deleteConfirm: 'Удалить этот вариант доставки? В КП его больше не будет.',
      deleteConfirmAction: 'Удалить',
      /* FR-3: выбранный вариант держит просчёт. Инструкция, а не извинение —
         решается одним переключением. */
      deleteHoldsCosting:
        'Этот вариант выбран, и статьи затрат просчитаны под него. Сначала выберите другой вариант — КП не остаётся без варианта, под который посчитана цена.',
      deleteFailed: 'Не удалось удалить вариант. Попробуйте ещё раз.',
    },

    terms: {
      /* Первая карточка под заголовком раздела «Условия»: называет то, что в
         ней, а не повторяет слово раздела. */
      title: 'Базис, оплата и срок',
      /* Подзаголовок карточки, когда её можно править прямо здесь. Говорит, что
         правка попадёт в следующее КП, — иначе неясно, меняет ли она уже
         отправленное. */
      editHint: 'Правки попадут в следующее КП. Уже отправленные версии не меняются.',
      /* Спрашиваем ДО сохранения (N-11): смена Инкотермс на готовом расчёте
         возвращает его в «Ждёт расчёта». Не обещаем, кому придёт уведомление, —
         его получают только те, кто ещё работает с котировкой. */
      resetWarning:
        'Расчёт уже готов. Если сменить Инкотермс, он вернётся в «Ждёт расчёта», и его нужно будет подтвердить заново.',
      resetConfirm: 'Сохранить и сбросить расчёт',
      days_one: '{{count}} день',
      days_few: '{{count}} дня',
      days_many: '{{count}} дней',
      days_other: '{{count}} дней',
    },
    /* Третий блок референсного КП — IMPORTANT NOTES.

       «Примечание», а не «оговорка»: оговорка — юридическое слово про
       ограничение ответственности, а сюда на деле пишут «свободное время обычно
       двое суток в пункте назначения». На бумаге этот блок называется notes.
       В базе вид записи по-прежнему `disclaimer` — так его называют шаблоны и
       контракт (0032); изменился только экран. */
    notes: {
      title: 'Примечания',
      hint: 'Печатается в КП после цены.',
      frozen: 'Напечатано в отправленном КП. Отправленное КП показывает то, что получил клиент.',
      empty: 'Примечаний пока нет.',
      /* Подпись внутри поля — отдельной надписи над ним нет: заголовок карточки
         уже сказал, что это. */
      addPlaceholder: 'Добавить примечание…',
      add: 'Добавить',
      edit: 'Изменить это примечание',
      remove: 'Удалить это примечание',
    },
    /* Что цена покрывает и чего не покрывает — печатается в КП (Story 5.2,
       FR-60). Это текст, который получит клиент, поэтому формулировки здесь —
       про бумагу, а не про базу: «печатается», а не «сохраняется». Заголовки
       совпадают с шаблонами в настройках, откуда этот текст обычно и копируют.

       Оговорки, которые были здесь третьим списком, теперь `quotes.notes` —
       на своей карточке. */
    blocks: {
      title: 'Включено и не включено',
      hint: 'Печатается в КП после цены: что входит в стоимость и что в неё не входит.',
      frozen: 'Напечатано в отправленном КП. Отправленное КП показывает то, что получил клиент.',
      /* «Включено» идёт первым — в таком порядке это печатается в КП. */
      included: 'Включено',
      includedEmpty: 'Пока ничего не отмечено как включённое.',
      addIncludedPlaceholder: 'Добавить включённое…',
      addIncluded: 'Добавить включённое',
      editIncluded: 'Изменить этот пункт',
      removeIncluded: 'Удалить этот пункт',
      notIncluded: 'Не включено',
      notIncludedEmpty: 'Исключений пока нет.',
      addNotIncludedPlaceholder: 'Добавить исключение…',
      addNotIncluded: 'Добавить исключение',
      editNotIncluded: 'Изменить это исключение',
      removeNotIncluded: 'Удалить это исключение',
      delete: 'Удалить',
      capped:
        'В этой котировке уже столько записей такого вида, сколько помещается в КП. Удалите одну, чтобы освободить место.',
      loadFailed: 'Не удалось загрузить. Обновите страницу и попробуйте ещё раз.',
    },
    /* История действий (вкладка «История»): записи глобального журнала по
       этой котировке. Сервер хранит машинный ключ ("quote.cargo.create"),
       перевод живёт здесь — и в словах ASA: работа, КП, статьи затрат
       (не калька из брифа). Прошедшее время везде: это факты о
       том, что произошло, а не кнопки. */
    history: {
      title: 'История',
      emptyTitle: 'Пока пусто',
      emptyDescription: 'Каждое действие по этой котировке записывается здесь.',
      actions: {
        created: 'Котировка создана',
        updated: 'Данные котировки изменены',
        cargoAdded: 'Добавлена позиция груза',
        cargoUpdated: 'Позиция груза изменена',
        cargoRemoved: 'Позиция груза удалена',
        costLineAdded: 'Добавлена статья затрат',
        costLineUpdated: 'Статья затрат изменена',
        costLineRemoved: 'Статья затрат удалена',
        routeOptionAdded: 'Добавлен вариант доставки',
        routeOptionUpdated: 'Вариант доставки изменён',
        routeOptionSelected: 'Выбран вариант доставки',
        routeOptionRemoved: 'Вариант доставки удалён',
        blockAdded: 'Добавлен блок текста КП',
        blockUpdated: 'Изменён блок текста КП',
        blockRemoved: 'Удалён блок текста КП',
        sent: 'КП отправлено клиенту',
        reopened: 'Открыта новая версия',
        answerApproved: 'Клиент согласовал',
        answerRejected: 'Клиент отклонил',
        answerRecorded: 'Записан ответ клиента',
        converted: 'По котировке создана работа',
        /* S-2a: состояние расчёта. Прошедшее время, как у соседей. */
        pricingRequested: 'Запрошен расчёт',
        pricingReady: 'Расчёт готов',
        pricingWithdrawn: 'Запрос расчёта отозван',
        /* Типовой набор — один запрос и одна запись, сколько бы статей он ни
           вставил; наценка переоценивает все статьи разом. */
        costLinesBatchAdded: 'Добавлены статьи затрат из типового набора',
        markupApplied: 'Наценка применена ко всем статьям',
        messagePosted: 'Сообщение отправлено в чат',
        /* Участники — внешние стороны перевозки; команда — сотрудники ASA.
           Та же пара и те же слова, что в ленте работы. */
        participantAdded: 'Добавлен участник',
        participantUpdated: 'Участник изменён',
        participantRemoved: 'Участник убран из котировки',
        teamAdded: 'Сотрудник добавлен в команду',
        teamRemoved: 'Сотрудник выведен из команды',
      },
      /* Правка, которая вернула готовый расчёт в «Ждёт расчёта» (сервер
         помечает такую запись `pricing: reopened`). Вторая строка под
         действием, а не отдельное событие: расчёт сбросила именно эта правка. */
      pricingReopened: 'Расчёт снова нужен — котировка изменилась после него',
    },

    /* Состояние расчёта котировки (S-2a): просьба посчитать цену как ЗАПИСЬ,
       а не только уведомление. «Ждёт расчёта» / «Расчёт готов» — термины
       глоссария; меняются здесь и в глоссарии вместе. */
    pricingState: {
      title: 'Расчёт',
      states: {
        none: 'Расчёт не запрошен',
        requested: 'Ждёт расчёта',
        ready: 'Расчёт готов',
      },
      /* Кто и когда. Без согласования по роду: «Считает: Сунь Ди», а не
         «Сунь Ди считает/считала». */
      assignee: 'Считает: {{name}}',
      requestedBy: 'Запрос: {{name}}, {{when}}',
      requestedAt: 'Запрошен {{when}}',
      readyBy: 'Готово: {{name}}, {{when}}',
      /* Сброс готового расчёта правкой (N-11) — не запрос: расчёт вернулся в
         «Ждёт расчёта», потому что изменили то, от чего зависит цена. Имя — кто
         правил; без согласования по роду, как и строки выше. */
      reopenedBy: 'Сброшен после правки: {{causes}} — {{name}}, {{when}}',
      reopenedAt: 'Сброшен после правки: {{causes}} — {{when}}',
      /* Что изменили, строчными — встают в строку через запятую. «Другое» —
         для кода, которого этот клиент ещё не знает. */
      causes: {
        cargo: 'груз',
        routeOption: 'вариант доставки',
        transportMode: 'вид транспорта',
        pickupLocation: 'место забора груза',
        incoterm: 'Инкотермс',
        transitDays: 'срок в пути',
        measures: 'вес и объём',
        other: 'другое',
      },
      noneHint:
        'Попросите коллегу из отдела расчёта посчитать цену — ему придёт уведомление, а котировка будет ждать расчёта.',
      actions: {
        request: 'Запросить расчёт…',
        reRequest: 'Запросить заново…',
        ready: 'Расчёт готов',
        marking: 'Отмечаем…',
        withdraw: 'Отозвать запрос',
        withdrawing: 'Отзываем…',
      },
      sheet: {
        title: 'Запросить расчёт',
        reTitle: 'Запросить расчёт заново',
        colleague: 'Кто считает',
        hint: 'Только коллеги, которые могут считать котировки.',
        /* Пустой список — это не «ничего не найдено»: право считать котировки
           не выдано никому, и чинится это в настройках ролей. */
        empty:
          'Попросить некого: право считать котировки пока не выдано ни одной роли. Его включают в настройках ролей.',
        blocked: 'Сначала выберите, кто будет считать.',
        note: 'Сообщение коллеге',
        noteHint: 'Одна строка — что нужно и к какому сроку. Необязательно.',
        submit: 'Запросить',
        /* Повторный запрос снимает готовность — говорим до нажатия. */
        reReady: 'Готовый расчёт снимется: котировка снова будет ждать расчёта.',
      },
      errors: {
        required: 'Выберите, кто будет считать.',
        notAssignable: 'Эта учётная запись не может войти в систему — выберите другого коллегу.',
        notPricing: 'Этот коллега не может считать котировки — выберите другого.',
        noteTooLong: 'Сообщение длиннее 500 символов — сократите его.',
        alreadyReady: 'Расчёт уже отмечен готовым — данные обновлены.',
        notRequested: 'Запроса уже нет: его отозвали или расчёт готов. Данные обновлены.',
      },
      /* O7: предупреждение при отправке, не запрет. */
      sendWarning: 'Расчёт запрошен, но ещё не готов',
      sendWarningBody: 'Отправить можно и сейчас, но цена в КП может ещё измениться.',
    },
    revisions: {
      title: 'Версии',
      emptyTitle: 'Версий пока нет',
      emptyDescription: 'Каждое отправленное КП сохраняется здесь в том виде, в каком ушло.',
      /* Версия без своей заметки. */
      issued: 'Отправлена',
      /* Why the button is off. The status is the reason, so the sentence
         names it rather than saying "нельзя" — a converted работа has its own
         запись, and a черновик is already editable. */
      notReopenable: 'Новую версию можно открыть только по отправленной или отклонённой котировке.',
      /* Почему шапку нельзя править. Говорит, что делать дальше: отправленное КП
         меняется новой версией, а не на месте (FR-7). */
      notEditable:
        'Отправленное КП правится через новую версию, а не на месте.',
    },

    /* Sending the offer (FR-71). The sheet exists to show the address before
       письмо уходит навсегда — не чтобы спросить лишнего. */
      /* Предпросмотр КП (UX-DR-15) перед необратимой отправкой.

         Подписи самого документа сюда не входят: они приходят с сервера на языке
         клиента, потому что именно на нём печатается PDF. Здесь — только рамка
         листа, на языке пользователя. */
      preview: {
        title: 'Предпросмотр КП',
        loading: 'Собираем КП',
        close: 'Закрыть',
        /* На месте номера выданного документа. У предпросмотра номера нет —
           номер присваивается при отправке (AD-12), и «ЧЕРНОВИК» — то же слово,
           которое печатает генератор PDF. */
        draft: 'ЧЕРНОВИК',
        revision: 'ревизия {{label}}',
        signature: 'Подпись и печать',
        /* Язык САМОГО документа, а не интерфейса — поэтому подпись именно
           такая, а не просто «Язык». КП пишется для клиента, и менеджеру,
           работающему по-русски, всё равно нужно прочитать английскую
           страницу перед отправкой. */
        language: 'Язык документа',
        /* Говорит, что именно переключается: обвязка листа за переключателем
           не идёт — таблицы и подписи полей ниже принадлежат клиенту, кнопки
           вокруг них остаются на языке читающего. */
        languageHint: 'На этом языке документ прочитает клиент. Отправка возьмёт тот же язык.',
        blockedTitle: 'Отправить пока нельзя',
        blockedNoCargo: 'В КП нет груза. Добавьте позиции в разделе «Клиент и груз» — КП без позиций ничего не оценивает.',
        blockedChecklist: 'Чек-лист по грузу ещё открыт. Чего не хватает, сказано над разделами.',
        blockedNotDraft: 'Это КП уже выпущено. Откройте новую ревизию, чтобы отправить снова.',
        blockedNoRecipient: 'У клиента нет контакта с почтой. Добавьте его в справочнике.',
        blockedGeneric: 'Сервер пока не выпускает это КП.',
      },
    offerSend: {
      title: 'Отправить КП',
      document: 'Документ',
      nextRevision: 'версия {{label}}',
      validUntil: 'Действительно до {{date}}',
      recipient: 'Получатель',
      noRecipient: 'У клиента нет контакта с почтой. Добавьте его в справочнике — без адреса КП отправить некуда.',
      /* Роль без доступа к контактам участников не видит адрес, и сервер не
         даст ей отправить КП. Это не «у клиента нет почты» — исправлять надо
         не справочник. */
      noContactAccess: 'Нет доступа к контактам клиента — отправить КП может коллега с доступом',
      /* Отказа по FR-64 здесь больше НЕТ: он в `errors.branding`, потому что КП
         больше не единственный документ, который он может остановить. */
      explainer: 'КП сохранится как неизменяемая версия, PDF попадёт в файлы, а клиент получит письмо со ссылкой на документ.',
    },
    costLines: {
      title: 'Статьи затрат',
      add: 'Добавить статью',
      edit: 'Изменить статью',
      description: 'Статья',
      vendor: 'Поставщик',
      /* Подсказка под полем поставщика. Говорит ровно то, что решает миграция
         0019: справочник здесь подсказывает, а не ограничивает. */
      vendorHint: 'Выберите из справочника или впишите своё',
      vendorPlaceholder: 'Начните вводить название',
      vendorNoMatches: 'В справочнике нет такого контрагента',
      vendorFreeTextHint: 'Введённое название сохранится как есть',
      qty: 'Кол-во',
      quantity: 'Количество',
      /* ПЕРВАЯ денежная колонка и единственная цена, которую спрашивает форма
         статьи: СЕБЕСТОИМОСТЬ единицы, как в блоке INTERNAL COST AND SELLING
         рабочей книги. Цена клиенту получается из неё одной сохранённой
         наценкой котировки — считается, а не вводится. Колонки: Кол-во |
         Себестоимость за ед. | Себестоимость | Наценка | Цена клиенту (S-5,
         решение владельца O5). Ключ сохранил старое имя, слова — себестоимости. */
      unitPrice: 'Себестоимость за ед.',
      /* ВТОРАЯ: себестоимость единицы, умноженная на количество, — во что
         обходится строка. Итог этой колонки — себестоимость котировки. Только
         читается, и в обычной строке, и в открытой. */
      amount: 'Себестоимость',
      /* ТРЕТЬЯ: наценка строки на её себестоимость — цифра сервера (наценка
         котировки или собственная наценка строки, если та посчитана иначе;
         такая бывает только в старом черновике). */
      markup: 'Наценка',
      /* Отметка у строки не по наценке котировки: значок и эти слова, не
         только цвет. */
      offMarkup: 'Не по наценке котировки',
      /* Память ставки СООБЩАЕТ, а не заполняет молча (FR-17). Принятая
         подсказка кладёт цифру в поле себестоимости за единицу — её видно и
         можно перебить до сохранения. */
      rateMemory: 'В прошлый раз по этому маршруту: {{amount}} · {{case}} · {{month}}',
      useRate: 'Взять',
      /* Две строки подвала на телефоне (O5): себестоимость и цена клиенту —
         каждая сумма того, что несут карточки над ней. «Себестоимость, итого»,
         а не «Итого»: строка называет ОДНУ сторону котировки.

         «Цена клиенту» — слово глоссария QW-2 (решение владельца O3) для
         рассчитанной суммы котировки; оно же заголовок последней колонки:
         доля строки над чертой, вся сумма — в подвале. */
      costTotal: 'Себестоимость, итого',
      customerTotal: 'Цена клиенту',
      /* Первая строка подвала таблицы: каждая сумма под своей колонкой —
         себестоимость под «Себестоимостью», цена клиенту под «Ценой клиенту». */
      total: 'Итого',
      /* Вторая строка подвала и единый словарь (S-5, L19): наценку задают,
         маржа получается, и показываются они вместе. Проценты приходят уже
         отформатированными («15.00%»). Если строки посчитаны по разной
         наценке, она средняя — так и сказано. */
      markupAndMargin: 'Наценка {{markup}} · Маржа {{margin}}',
      markupAndMarginMixed: 'Наценка в среднем {{markup}} · Маржа {{margin}}',
      /* Вторая строка карточки на телефоне, под суммой клиенту (O5). */
      costAndMarkup: 'себестоимость {{cost}} · наценка {{markup}}',
      /* Q-12 / P0-4: с 2026-09-29 КП печатает клиенту ОДНУ строку — ставку за
         перевозку на сумму «Цена клиенту» (PrintedRevisionLines). Порядок
         статей и их цены по отдельности клиент не видит. */
      offerPrintsOneLine:
        'В КП клиенту печатается одна строка — «Ставка за перевозку» на сумму «Цена клиенту». Статьи затрат клиент не видит.',
      delete: 'Удалить статью',
      /* States what goes, not "are you sure": a line taking its share of the
         margin with it is the consequence worth naming. */
      deleteConfirm: 'Удалить статью вместе с её суммой в марже?',
      deleteConfirmAction: 'Удалить',
    },

    /* Цена от себестоимости (FR-1): панель наценки на вкладке расчёта.
       Просьба посчитать цену живёт в `pricingState` ниже (S-2a). */
    pricing: {
      /* Панель наценки. Два процента, две подписи — см. MarginPanel.

         `customerTotal` здесь НЕТ намеренно. Панель печатает `totals.sell` —
         ту же величину, что и подвал таблицы себестоимости; с тех пор как
         панель встала на вкладку расчёта, обе стоят на одном экране в
         нескольких сантиметрах друг от друга. Подпись берётся из
         `quotes.costLines.customerTotal` — словаря самого расчёта, общего со
         списком на телефоне и шапкой мастера. Вторая запись здесь держала ту
         же строку и была в одной правке от того, чтобы одна величина выглядела
         как две. */
      title: 'Цена от себестоимости',
      cost: 'Себестоимость',
      /* Подпись называет знаменатель: рядом стоит процент от ДРУГОЙ величины, и
         пара читается верно, только когда каждая цифра говорит, от чего она. */
      marginOverRevenue: 'Маржа от цены клиенту',
      /* Поле: наценку ЗАДАЮТ, маржа получается (S-5, L19). От какой базы
         считается наценка, говорит подсказка над полем. */
      markup: 'Наценка, %',
      /* Сохранённая наценка котировки, только для чтения, — так её показывает
         котировка, которая уже ушла клиенту (L18). */
      markupStored: 'Наценка котировки',
      /* Действующая наценка расчёта рядом с маржой (O6): сохранённая, пока все
         строки посчитаны по ней, средняя — когда строки расходятся. */
      markupEffective: 'Наценка',
      markupAverage: 'Наценка в среднем',
      /* Q-11: два процента рядом — одна и та же прибыль от двух баз. Подсказка
         называет обе базы и даёт пару, которую можно проверить в уме: 15% на
         100 — это 115, а 15 из 115 — 13.04%. Два знака, как у маржи рядом
         (O4 / L19) и в глоссарии. */
      markupHint:
        'Наценка 15% даёт маржу 13.04%: это одна и та же прибыль, только наценка считается от себестоимости, а маржа — от цены клиенту.',
      applyMarkup: 'Применить',
      /* Сказано ДО нажатия: наценка теперь ХРАНИТСЯ в котировке (S-5), так что
         «Применить» — это настройка, а не разовый пересчёт, и строка,
         записанная потом, следует ей. */
      applyMarkupConfirm:
        'Сохранит наценку в котировке и пересчитает по ней все строки. Новые и изменённые строки получат ту же наценку.',
      /* Почему «Применить» не нажимается, пока в поле то, что нельзя
         сохранить: сервер принимает число от 0, до четырёх знаков после запятой. */
      markupInvalid: 'Введите наценку: число от 0, не больше четырёх знаков после запятой.',
      /* L10: черновик, где не все строки посчитаны по наценке котировки, —
         такой бывает только у расчёта, сделанного до того, как наценку стали
         хранить. Сказано до правок, вместе с тем, что сделает каждое действие. */
      markupMixed_one:
        '{{count}} строка посчитана по другой наценке. Изменённая строка получит наценку котировки, а «Применить» пересчитает все строки.',
      markupMixed_few:
        '{{count}} строки посчитаны по другой наценке. Изменённая строка получит наценку котировки, а «Применить» пересчитает все строки.',
      markupMixed_many:
        '{{count}} строк посчитаны по другой наценке. Изменённая строка получит наценку котировки, а «Применить» пересчитает все строки.',
      preset: 'Типовой набор',
      presetHint:
        'Постоянные статьи затрат, вставляются без сумм. Ведутся в настройках.',
      noPreset: 'В настройках пока нет постоянных статей затрат.',
    },
  },

  finance: {
    title: 'Деньги',
    subtitle: 'Кто кому должен.',
    sectionsLabel: 'Разделы финансов',
    /* N-21: the one state a reader without `finance.receivables.read` sees on
       /finance — instead of the heading, the tabs and a refusal per tab. */
    noAccess: {
      title: 'Раздел недоступен',
      description:
        'Деньги по всем работам открываются правом «Видеть долги по срокам, балансы и реестр дебиторки по всем работам». Если оно вам нужно, обратитесь к администратору.',
    },

    tabs: {
      balances: 'Балансы',
      aging: 'По срокам',
      payments: 'Платежи',
      /* «Ставок», а не просто «Согласования» (QW-10, F-1): здесь только
         изменения ставки. Подтверждение платежей — другая очередь, на вкладке
         «Платежи», и одно слово на обе отправляло Финансы не туда. */
      approvals: 'Согласования ставок',
      documents: 'Документы',
    },

    balances: {
      counterparty: 'Контрагент',
      type: 'Тип',
      /* Какой справочник за строкой. Сервер присылает код (`client` /
         `participant`), и до QW-1 он печатался как есть — по-английски. */
      kinds: {
        client: 'Клиент',
        /* Код `participant` — запись справочника контрагентов; слово
           «Участник» ушло вместе с участниками внутри работы (S-22a). */
        participant: 'Контрагент',
      },
      receivable: 'Дебиторка',
      payable: 'Кредиторка',
      net: 'Сальдо',
      oldest: 'Самый старый',
      /* Возраст самой старой непогашенной позиции в днях. Всегда есть и со
         ЗНАКОМ: реальное чтение присылает число в каждой строке, а контрагент,
         которому нечего гасить, в список не попадает. Отрицательный возраст —
         срок ещё не наступил — рендерится словами корзины старения, а не вторым
         вариантом фразы здесь. */
      oldestDays_one: '{{count}} день',
      oldestDays_few: '{{count}} дня',
      oldestDays_many: '{{count}} дней',
      tableLabel: 'Балансы — {{entity}}',
      /* S-12: исходящие платежи, которые никто не разнёс, за всё время и по
         всем работам. Число и суммы по валютам — как их прислал сервер, без
         пересчёта и сложения (AD-9). */
      unallocatedOut: 'Неразнесённые исходящие платежи: {{n}} · {{amounts}}',
      /* N-04: то, что строки балансов не показывают. Транш не называет
         контрагента (AD-26), поэтому строкой он быть не может, но «По срокам»
         и «К получению» на главной его считают. Сумма — как её прислали. */
      uninvoiced: 'Без учёта сумм, по которым ещё не выставлен счёт: {{amount}}',
      uninvoicedHint:
        'Траншей без счёта: {{n}}. Они входят в «По срокам», но не в балансы: транш не называет контрагента.',
      unallocatedShow: 'Показать',
      emptyTitle: 'За этот период балансов нет',
      emptyDescription:
        'Контрагент появится здесь, когда по работе пройдут деньги: выставленный счёт, срок транша или проведённые затраты поставщика.',
    },

    aging: {
      title: 'Дебиторка по срокам',
      /* Пять групп, а не три. Две дополнительные и несут весь смысл экрана:
         FR-34 AC 5 запрещает читать «не погашено» как «просрочено», а AC 6 —
         сваливать позицию без даты в произвольную корзину. */
      buckets: {
        'not-yet-due': 'Срок не наступил',
        '0-30': '0–30 дней',
        '31-60': '31–60 дней',
        '60-plus': '60+ дней',
        'no-date': 'Без ожидаемой даты',
      },
      emptyTitle: 'За этот период непогашенного нет',
      emptyDescription:
        'Транши, выставленные счета и затраты поставщиков остаются здесь, пока не закрыты.',
    },

    payments: {
      title: 'Платежи',
      record: 'Внести платёж',
      reference: 'Основание',
      referenceHint: 'Банковское назначение платежа — как в выписке.',
      /* Дата платежа в обе стороны (P-1): «Получен» читался неверно у
         исходящего платежа перевозчику. Ключ остался прежним. */
      received: 'Дата платежа',
      amount: 'Сумма',
      currency: 'Валюта',
      /* Входящий или исходящий — единственное «направление», оставшееся в
         каталоге: это не плечо (глоссарий QW-2, список разрешённых в
         glossary.test.ts). */
      direction: 'Направление',
      in: 'Входящий',
      out: 'Исходящий',
      directionLabel: 'Направление',
      allocated: 'Разнесено',
      unallocated: 'Не разнесено',
      detailTitle: 'Платёж {{reference}}',
      /* Работа, к которой относится платёж, в окне платежа — ссылкой, с
         маршрутом после точки. Видна, только если работа читается. */
      caseLine: 'Работа {{number}}',
      /* Видимое подтверждение после «Подтвердить»: кнопки исчезают вместе с
         меткой «не подтверждён», и без этой строки нажатие выглядело бы как
         ничего. */
      confirmed: 'Платёж подтверждён',
      emptyTitle: 'За этот период платежей нет',
      emptyDescription:
        'Деньги на входе и выходе появляются здесь по мере внесения — вместе с тем, что каждый платёж закрывает.',
      case: 'Работа',
      caseHint: 'Работа, по которой прошли деньги.',
      method: 'Способ',
      methods: {
        cash: 'Наличные',
        bank_transfer: 'Банковский перевод',
      },
      /* НАШЕ юрлицо, не контрагент: два прочтения «issuing entity» дают разный
         учёт, поэтому в названии поля сказано, чьё именно. */
      entity: 'Наше юрлицо',
      entityHint:
        'Какое из наших юрлиц получило или отправило деньги — не контрагент.',
      bookedAt: 'Курс {{rate}} на {{date}} · {{usd}} USD',
      allocationNote:
        'Платёж поступает неразнесённым. Разнесите его по счетам отдельным действием.',

      counterpartyPlaceholder: 'Выберите контрагента',
      counterpartySearch: 'Поиск по справочнику',
      /* Подсказка справа у строки клиента работы в списке сторон платежа:
         клиент лежит в другом справочнике, чем контрагенты под ним. */
      counterpartyKindClient: 'Клиент',
      /* Имя, сохранённое до того, как поле стало выбором из списка, — или
         списанное с платёжки для компании, которой в справочнике нет. Остаётся
         выбранным: ничто здесь не вправе отвергнуть уже сохранённые данные. */
      counterpartyAsEntered: 'как введено',
      counterpartyAdd: 'Добавить контрагента',

      /* Сторона платежа — кому заплатили или от кого получили — записью, а не
         написанием. Исходящий платёж называет контрагента из справочника или
         клиента работы (возврат); входящий — клиента работы или другого
         плательщика, как в платёжке. */
      party: {
        caseClient: 'Клиент работы — {{name}}',
        /* Клиент, записанный на платеже, который больше не клиент этой
           работы: его не теряют, но и заново не предлагают. */
        asRecorded: 'как записано',
        other: 'Другой плательщик',
        otherName: 'Плательщик — как в платёжке',
        hintOut:
          'Кому ушли деньги: контрагент из справочника или клиент работы, если это возврат.',
        hintIn: 'От кого пришли деньги: клиент работы или другой плательщик — как в платёжке.',
        pickOut: 'Выберите контрагента из справочника или клиента работы',
        clientChanged: 'Это уже не клиент этой работы — выберите заново',
        /* S-22f: исходящий платёж, уже разнесённый на затраты, нельзя
           переписать на клиента работы — возврат на затраты не разносится. */
        refundAllocated: 'Платёж уже разнесён на затраты — его нельзя сделать возвратом',
      },

      /* Комиссия банка за перевод (миграция 0058).
         ПУСТО значит «комиссия не записана», а не «комиссия равна нулю»: ноль
         в отчёте читался бы как перевод, который ничего не стоил. */
      feeHeading: 'Комиссия за перевод',
      fee: 'Комиссия',
      feeCurrency: 'Валюта комиссии',
      feeHint:
        'Сколько банк удержал за перевод. Необязательно. Эти деньги не доходят до контрагента и ничего не закрывают — закрывает только сумма платежа выше.',
      /* На карточке платежа, отдельной строкой рядом с суммой. Складывать эти
         два числа нельзя, поэтому они и не стоят в одном итоге. */
      feeRecorded: 'Комиссия за перевод — {{amount}}',

      /* Подтверждение записи (0059, L3/L4/L8).
       *
       * Каждая фраза здесь — про ЗАПИСЬ и ни одна не про деньги.
       * Неподтверждённый платёж входит в «Оплачено», в балансы и в старение
       * наравне с подтверждённым, поэтому ничто не должно читаться как «эти
       * деньги ещё не считаются» — именно такая формулировка отправляет
       * заплатить перевозчику второй раз.
       *
       * Статуса «отклонён» нет, потому что отклонение — это УДАЛЕНИЕ:
       * отклонённая строка, оставшаяся в реестре, — цифра, которую каждая сумма
       * потом обязана не забыть исключить. */
      status: 'Статус',
      statuses: {
        pending: 'Ждёт подтверждения',
        confirmed: 'Подтверждён',
      },
      /* Метка в строке. Короткая — она стоит рядом с основанием и суммой;
         длинная форма живёт в колонке статуса и в фильтре. */
      pendingTag: 'не подтверждён',
      pendingFilter: 'Ждут подтверждения ({{count}})',
      /* Над очередью: переключатель периода вверху страницы продолжает
         показывать свой период, а очередь — за всё время. */
      pendingAllPeriods:
        'Здесь все неподтверждённые платежи за всё время — период вверху страницы к очереди не применяется.',
      pendingEmptyTitle: 'Подтверждать нечего',
      pendingEmptyDescription: 'Все записанные платежи проверены.',
      /* S-12: сужение, в которое ведёт «Показать» со вкладки «Сальдо». */
      unallocatedOutFilter: 'Неразнесённые исходящие',
      unallocatedOutEmptyTitle: 'Разносить нечего',
      unallocatedOutEmptyDescription: 'Все исходящие платежи разнесены по затратам.',
      confirm: 'Подтвердить',
      confirmedOn: 'Подтверждён {{date}}',
      reject: 'Отклонить',
      rejectTitle: 'Отклонить платёж {{reference}}',
      rejectBody:
        'Запись удалится вместе с её привязками. Долг перевозчику при этом остаётся — фактическая затрата никуда не денется. Отклонить можно только то, что ещё никто не подтвердил.',
      rejectReason: 'Причина',
      rejectReasonHint:
        'Обязательно. Строки сейчас не станет — журнал аудита останется единственным местом, где сказано почему.',
      rejectSubmit: 'Отклонить и удалить',
      /* S-18a: одна оплата может гасить несколько затрат — каждая остаётся
         долгом после удаления платежа (L8). */
      rejectDebtsStay: 'Долги останутся:',
      amend: 'Исправить',
      amendTitle: 'Исправить платёж {{reference}}',
      amendSubmit: 'Сохранить исправление',
      amendFixedFacts:
        'Валюту ({{currency}}) и дату движения денег ({{date}}) исправить нельзя — на них держатся все букинги этого платежа. Если ошибка в них — отклоните запись с причиной и внесите заново.',
      amendAction: 'Исправить платёж {{reference}}',
      rejectAction: 'Отклонить платёж {{reference}}',
      /* S-21: входящий платёж клиента — его разнесение уходит вместе с ним,
         и документы клиента снова ждут оплаты; долга перевозчику тут нет. */
      rejectBodyIn:
        'Запись удалится вместе с её разнесением — документы клиента снова станут неоплаченными на эти суммы. Удалить можно только то, что ещё никто не подтвердил.',
      /* S-21: тот, кто сам внёс неподтверждённый платёж клиента, удаляет
         свою запись — «отклонить» здесь решение финансов, а не его. */
      delete: 'Удалить',
      deleteTitle: 'Удалить платёж {{reference}}',
      deleteSubmit: 'Удалить платёж',
      deleteAction: 'Удалить платёж {{reference}}',
    },

    /* Разнесение ИСХОДЯЩЕГО платежа по затратам (история 4.5, AD-11). Входящий
     * платёж клиента с S-21 разносится в «Платёж клиента» (`clientPayment`)
     * по счетам, дебет-нотам и траншам работы. */
    allocations: {
      heading: 'Разнесено на',
      allocate: 'Разнести',
      submit: 'Разнести',
      title: 'Разнести {{reference}}',
      none: 'Пока не разнесено. Неразнесённые деньги — это нормально: они разносятся, только когда человек укажет, что именно они закрывают, и никогда автоматически.',
      fullyAllocated: 'Платёж разнесён полностью — разносить больше нечего.',
      /* S-22f: исходящий платёж клиенту работы — это возврат, деньги клиента,
         а не оплата поставщику; на строку затрат он не разносится. */
      refundNotCost: 'Возврат клиенту не разносится на затраты',
      target: 'Закрывает',
      targetPlaceholder: 'Выберите затрату',
      targetN: 'Что закрывает часть {{n}}',
      targetRequired: 'Укажите, что закрывает эта часть.',
      /* ИСХОДЯЩИЙ платёж закрывает затрату, а не транш: сервер отказывает на
         несовпадении направления (`ErrAllocationDirectionMismatch`), так что
         предлагать здесь транши означало предлагать единственное, что заведомо
         не сработает. Пустое состояние называет действие, которое это меняет, а
         не повторяет совет про ставку клиента — к оплате перевозчика он
         отношения не имеет. */
      expenseTargetsNote:
        'Фактические затраты по работе — исходящий платёж закрывает именно их, а не транши клиента.',
      expenseTargetsNone:
        'По этой работе нет фактических затрат, поэтому платежу нечего закрывать. Сначала запишите долг — «Оплатить перевозчику» на плече делает и то и другое одним действием.',
      /* ДРУГОЙ пустой список: затраты записаны, и все оплачены полностью.
         Фраза выше здесь была бы неправдой, а её совет — записать долг — ровно
         то, из-за чего перевозчику платят дважды. Остаётся убрать дубль, а
         убрать можно только неподтверждённую запись. */
      expenseTargetsAllPaid:
        'Все фактические затраты по этой работе уже оплачены, поэтому платежу нечего закрывать. Если он записан повторно, отклоните его — отклонить можно только неподтверждённую запись.',
      /* Сказано на КНОПКЕ, а не внутри окна. Открыть список, чей запрос ещё не
         ответил, значит написать «затрат нет» про работу, где их может быть
         несколько. */
      expenseTargetsLoading: 'Читаем фактические затраты по работе…',
      expenseTargetsFailed:
        'Не удалось прочитать затраты по этой работе — предлагать нечего. Повторите попытку перед разнесением.',
      amountN: 'Сумма части {{n}}',
      amountHint: 'В {{currency}} — валюте, в которой пришёл платёж.',
      add: 'Добавить часть',
      remove: 'Удалить часть {{n}}',
      requested: 'Разносится',
      splitsRequired: 'Укажите документ и сумму в каждой части.',
      exceedsBalance:
        'Сумма частей больше, чем {{balance}}, которые ещё не разнесены по этому платежу.',
      /* Только для распределения НЕ в USD. Распределение в USD показывает одну
         сумму в USD без «по 1» — курс 1 повторял валюту и читался как
         пересчёт, которого не было (F1, как QW-6 у платежа). */
      settledAt: 'по {{rate}} · {{usd}} USD',
      /* Колонки списка распределений. «Проведено» называет ячейку
         «по 7.250000 · 2 500.00 USD» — курс, по которому взято это
         распределение, и во сколько оно вышло в USD: это проводка ЦЕЛИ, а не
         платежа. У распределения в USD там только «2 500.00 USD». */
      columns: { target: 'Закрывает', amount: 'Зачтено', booked: 'Проведено', at: 'Распределено' },
      fxDifference: 'Курс {{value}}',
      fxDifferenceHint:
        'Разница между курсом закрытия и курсом, по которому был учтён документ. Записывается здесь; учтённая сумма документа не меняется.',
    },

    /* S-21: дебиторка работы по документам — счета, дебет-ноты клиента и
     * транши, с тем, что оплачено и что осталось. Цифры сервера; здесь только
     * слова. Ярлыки собираются из номера документа или места транша в серии,
     * а не из английского `targetLabel` сервера. */
    receivables: {
      invoice: 'Счёт {{number}}',
      debitNote: 'Дебет-нота {{number}}',
      tranche: 'Транш {{sequence}} из {{of}}',
      status: {
        open: 'Ожидается',
        partial: 'Частично',
        paid: 'Оплачен',
        overpaid: 'Переплата {{amount}}',
      },
      /* Транш, вместо которого выставлен счёт: деньги идут на счёт. */
      replacedBy: 'заменён счётом {{number}}',
      invoiceIssued: 'выставлен {{date}} · {{total}}',
      debitNoteIssued: 'выставлена {{date}} · {{total}}',
      trancheDue: 'без счёта · срок {{date}}',
      trancheUndated: 'без счёта · срок не согласован',
      paid: 'Оплачено',
      outstanding: 'Остаток',
      pay: 'Внести платёж',
      payFor: 'Внести платёж по «{{target}}»',
      /* В подтверждении выпуска счёта по траншу: уже полученные по траншу
         деньги переходят на этот счёт. */
      carryOver: 'Уже полученные по «{{tranche}}» {{amount}} перейдут на этот счёт.',
    },

    /* S-21: «Платёж клиента» — запись платежа клиента и его разнесение по
     * открытым долгам работы одним действием. Ничего не разносится само:
     * заполнена только строка, с которой открыто окно. */
    clientPayment: {
      title: 'Платёж клиента',
      allocateTitle: 'Разнести платёж {{reference}}',
      receivedOn: 'Получено',
      entityHint: 'Какое из наших юрлиц получило деньги.',
      feeSummary: 'Комиссия банка',
      pending:
        'Платёж уйдёт финансам на подтверждение. До подтверждения его можно исправить или отменить.',
      debtsHeading: 'Открытые долги',
      columns: {
        document: 'Документ',
        outstanding: 'Остаток',
        apply: 'Разнести',
        applyIn: 'Разнести, {{currency}}',
      },
      openedFrom: 'отмечен — окно открыто с него',
      inPayment: '≈ {{amount}} по курсу на {{date}}',
      wholeRemainder: 'Весь остаток',
      wholeRemainderFor: 'Весь остаток по «{{target}}»',
      rowAmount: 'Сумма на «{{target}}»',
      rateMissing:
        'Курса на {{date}} ещё нет, поэтому долги в другой валюте здесь не пересчитаны. Сумму на них проверит сервер при записи.',
      nothingOpen:
        'По работе нет неоплаченных счетов, дебет-нот и траншей. Платёж запишется неразнесённым — долг клиента он не уменьшит.',
      nothingOpenAllocate:
        'По работе нет неоплаченных счетов, дебет-нот и траншей — разносить этот платёж не на что.',
      receivablesLoading: 'Читаем открытые долги по работе…',
      receivablesFailed:
        'Не удалось прочитать долги по работе. Платёж можно записать неразнесённым или повторить попытку.',
      footer: {
        received: 'Получено',
        payment: 'Платёж',
      },
      submitAllocate: 'Внести и разнести',
      blocked: {
        rowOver: '«{{target}}» примет не больше {{amount}}',
        rowAmount: 'Укажите сумму для каждого отмеченного долга',
        totalOver: 'Разнесено больше, чем получено, — уменьшите суммы',
        totalOverUnallocated:
          'Разнесено больше, чем осталось в платеже ({{amount}}), — уменьшите суммы',
        nothingTicked: 'Отметьте, что закрывает платёж',
        /* Новые дата или валюта — новый расчёт остатков; пока он не пришёл,
           отправлять нечего. */
        reading: 'Открытые долги ещё читаются — подождите',
      },
      alreadyRecorded:
        'Этот платёж уже записан — список платежей обновлён. Закройте окно и проверьте «Полученные платежи».',
      refusal: {
        overTarget:
          'На «{{target}}» можно разнести не больше {{remaining}} — остальное уже оплачено. Остатки обновлены, поправьте сумму.',
        overPayment:
          'В платеже осталось только {{remaining}} — на «{{target}}» столько не разнести. Остатки обновлены, поправьте суммы.',
        overPaymentUnnamed:
          'В платеже осталось только {{remaining}}. Остатки обновлены, поправьте суммы.',
        targetClosed:
          '«{{target}}» уже заменён счётом {{number}} — разнесите платёж на этот счёт. Остатки обновлены.',
        unnamed: 'этот документ',
      },
    },

    /* S-21: отмена одного или нескольких разнесений входящего платежа — с
     * причиной, которая уходит в журнал: строки разнесения больше не будет. */
    reverse: {
      action: 'Отменить разнесение',
      title: 'Отменить разнесение',
      what: 'Что отменить',
      reason: 'Причина',
      reasonHint: 'Обязательно — причина попадёт в журнал.',
      consequence:
        'Отмеченные суммы снова станут неразнесёнными, а документы — неоплаченными на эти суммы. Запись попадёт в журнал.',
      keep: 'Не отменять',
      submit: 'Отменить разнесение',
      submitting: 'Отменяем…',
      pickRequired: 'Отметьте, что отменить',
      stopped: 'Отменено {{done}} из {{total}}, на следующем сервер отказал:',
    },

    approvals: {
      pending: 'Ожидает',
      current: 'Текущая',
      requested: 'Запрошена',
      change: 'Изменение',
      approve: 'Согласовать',
      reject: 'Отклонить',
      confirmTitle: 'Согласовать изменение ставки по {{number}}',
      confirmBody:
        'Ставка по {{number}} изменится с {{current}} на {{requested}}. Решение обязывающее — отменить нельзя, для исправления потребуется новый запрос.',
      emptyTitle: 'Согласований нет',
      emptyDescription:
        'Изменения ставки, требующие решения, появятся здесь и останутся до ответа.',
    },

    documents: {
      title: 'Счета и дебет-ноты',
      newInvoice: 'Новый счёт',
      edit: 'Изменить документ',
      number: 'Номер',
      numberNote: 'Номер присваивается по схеме нумерации этого юрлица.',
      entity: 'Юрлицо',
      case: 'Работа',
      issued: 'Выставлен',
      status: 'Статус',
      due: 'Срок',
      total: 'Итого',
      outstanding: 'Остаток',

      /* Всё, что ниже, — это форма «создать → сформировать → выпустить» и её
       * предпросмотр, и лежит она под `finance`, а не под `cases`, намеренно:
       * раздел «Документы» в работе и страница «Финансы» создают и смотрят
       * документы ОДНИМ компонентом и одной мутацией. Работа добавляет фильтр
       * и подстановку известных ей фактов, но форму не разветвляет. Вторая
       * копия этих строк под `cases` была бы первым шагом к такому ветвлению:
       * экраны разойдутся в словах раньше, чем разойдутся в поведении.
       */

      /* Два списка, на которые разделён раздел, и состояние строки внутри них.
         «Не сформирован» именно написано, а не оставлено пустым: пустая ячейка
         в архиве читается как незагрузившаяся колонка, а не как документ,
         который ещё никто не сформировал. */
      debitNotes: 'Дебет-ноты',
      invoices: 'Инвойсы',
      rendered: 'Сформирован',
      notRendered: 'Не сформирован',

      /* N-30: реестр выпущенной бумаги компании — вкладка «Документы» без
         выбранной работы. Только выпущенное (черновик долга не создаёт, FR-62).
         Сумма «Итого» — в валюте самого документа, «Оплачено» и «Остаток» — в
         USD по курсу проводки: у каждой колонки своя валюта рядом с числом. */
      register: {
        allCases: 'Все работы',
        caseColumn: 'Работа',
        entityColumn: 'Юрлицо',
        paidColumn: 'Оплачено, USD',
        outstandingColumn: 'Остаток, USD',
        emptyTitle: 'Выпущенных документов нет',
        emptyDescription:
          'Здесь появятся счета и дебет-ноты, которые компания уже выставила. Выберите работу, чтобы увидеть её черновики и создать новый документ.',
        capped: 'Показаны последние {{shown}} из {{total}} документов. Сузьте период, чтобы увидеть остальные.',
        openCase: 'Открыть работу {{number}}',
        noFile: 'Файл этого документа не сформирован.',
        fileUnavailable: 'Файл этого документа вам недоступен.',
      },

      /* Три действия, три вызова сервера и намеренно ни одного сокращения,
         которое делает два сразу: сформировать — не значит выпустить
         (UX-DR-15), а флаг «выпустить при формировании» клиент однажды
         выставит по ошибке. «Сформировать заново» — отдельная формулировка,
         потому что повторное нажатие заменяет файл, который уже лежит в
         архиве, и кнопка с прежней надписью читалась бы как первое
         формирование. */
      actions: {
        generate: 'Сформировать PDF',
        regenerate: 'Сформировать заново',
        generating: 'Формируем…',
        issue: 'Выпустить',
        issuing: 'Выпускаем…',
        /* Действия строкой в таблице. Значки — но подпись у каждого есть: она
           читается вслух и всплывает при наведении, а решение по двум опасным
           (выпуск и удаление) принимается уже в диалоге, где всё написано
           словами. Значок здесь открывает разговор, а не совершает поступок. */
        preview: 'Посмотреть документ',
        open: 'Открыть счёт',
        discard: 'Удалить черновик',
      },

      /* Удаление возможно только у черновика. Выпущенный документ клиент держит
         на руках, и номер, который никуда не ведёт, хуже документа, который
         придётся объяснять (AD-24). Про потолок сказано отдельно, потому что
         это и есть повод удалять: позиции брошенного черновика съедают остаток
         согласованной ставки у всех следующих счетов. */
      discardConfirm: {
        title: 'Удалить черновик?',
        body: 'Черновик и его позиции будут удалены безвозвратно. Пока черновик существует, его суммы занимают часть согласованной ставки и уменьшают остаток, доступный следующим счетам.',
        confirm: 'Удалить',
      },

      /* Подтверждение стоит на выпуске, потому что необратим именно он.
         Формировать можно хоть весь день — это стоит только рендера; выпуск же
         присваивает номер, фиксирует документ и создаёт дебиторскую
         задолженность, и AD-24 делает этот переход односторонним. В тексте
         названы все три последствия, а затем сказано, как исправлять:
         «отменить нельзя» само по себе читается как тупик, а не как обычный
         бумажный порядок. */
      issueConfirm: {
        title: 'Выпустить документ?',
        body: 'При выпуске документу присваивается номер по схеме нумерации этого юрлица, документ фиксируется, и по нему возникает дебиторская задолженность. Отменить это нельзя — исправление оформляется новым документом по этой же работе.',
        confirm: 'Выпустить',
      },

      /* AC 7, и блокирующее сообщение в принятом здесь виде: запрет и причина
         вместе, чтобы правило запомнилось с первого раза. Номер печатается на
         архивном файле, поэтому у несформированного документа его просто не на
         чем напечатать — и сказано именно это, а не «пока нельзя». */
      needsRender: 'Сначала нужно сформировать PDF — номер печатается на архивном файле, и без него выпустить документ не на чем.',
      /* AC 9. Это подсказка интерфейса, а не защита: сервер откажет в правке в
         любом случае. Фраза нужна, чтобы экран объяснил, почему полей нет, а
         не показывал поля, которые всё равно не примут. */
      issuedReadOnly: 'Выпущенный документ изменить нельзя. Чтобы исправить, создайте новый.',
      /* Отказ, который получает ВТОРОЕ нажатие «Выпустить». Сформулирован как
         факт о первом нажатии, а не как ошибка: человеку нужно понять, что
         документ ушёл. Проглоченный отказ или отказ в виде сбоя учат ровно
         обратному (UX-DR-24). */
      alreadyIssued: 'Этот документ уже выпущен.',
      /* Модуль `document` может отсутствовать: без настроенного объектного
         хранилища main.go не создаёт его, маршруты не подключаются, и ноутбук
         без бакета получает 404. Показывать это пустым списком нельзя — пустой
         список говорит, что по работе нет бумаг, а правда в том, что смотреть
         не в чем. */
      archiveUnavailable: 'Архив документов в этом окружении не настроен, поэтому здесь ничего нельзя сформировать и показать.',

      /* Словом «предпросмотр» названы две разные вещи, и различает их именно
         этот текст. ПРЕДПРОСМОТР ЧЕРНОВИКА клиент рисует сам из полей на
         экране — без рендера и без записи в архиве, — чтобы цикл «набрал
         строку → увидел → поправил» не стоил рендера страницы на каждое
         нажатие клавиши. ГОТОВЫЙ ДОКУМЕНТ — это сам архивный PDF, те самые
         байты, которые получит клиент. Тот, кто видел только первое, документа
         не видел; поэтому второе и существует, и поэтому ни одно из них не
         занимает слова и действия другого. */
      preview: {
        /* Окно, показывающее страницу, СОБРАННУЮ СЕРВЕРОМ, — тем же шаблоном,
           из которого печатается PDF для клиента. Стоит в одной ветке с
           надписями ниже намеренно: те описывают нарисованную вручную разметку,
           которую это заменяет, и соседство делает пару заметной тому, кто
           будет убирать остатки старого. */
        title: 'Документ',
        subtitle: 'Так документ выглядит на бумаге. У черновика вместо номера печатается слово DRAFT.',
        composeFailed: 'Не удалось собрать страницу.',

        draftBadge: 'Черновик',
        draftTitle: 'Предпросмотр черновика',
        draftHint: 'Это разметка того, что набрано, а не сам документ. Нажмите «Сформировать PDF», чтобы получить файл, который получит клиент.',
        renderedTitle: 'Готовый документ',
        /* AC 8. Показывается вместо вчерашнего PDF рядом с сегодняшними
           суммами: экран возвращается к живому черновику и говорит, на что
           именно человек сейчас смотрит, а не оставляет молча устаревший
           кадр, который выглядит не менее убедительно. */
        outOfDate: 'Черновик изменился после формирования, поэтому в архиве лежит устаревшая копия. Сформируйте заново перед выпуском.',
        /* Подписанная ссылка живёт пять минут (AD-18) — чтобы скопированная со
           страницы ссылка умерла раньше, чем её успеют переслать. Срок назван
           затем, чтобы кнопка повтора рядом читалась как обычный следующий
           шаг, а не как сбой по вине читающего. */
        failed: 'Не удалось загрузить документ. Ссылка на него живёт пять минут.',
        loading: 'Загружаем документ…',
        empty: 'Выберите документ слева, чтобы посмотреть его.',
        /* Доступное имя для <iframe>. Без него скринридер объявляет посреди
           страницы безымянный фрейм, и понять, что в нём, нечем. */
        frameTitle: 'Предпросмотр документа',
      },

      /* Сама форма создания — одна форма на оба экрана. */
      raise: {
        kind: 'Тип документа',
        counterparty: 'Кому выставляем',
        /* Плательщик не выбирается (S-22a): новая дебет-нота выставляется
           только клиенту работы, и название и адрес в печатной форме берутся
           из его карточки (AD-26). */
        counterpartyHint: 'Дебет-нота выставляется клиенту этой работы. Название и адрес в печатной форме берутся из карточки клиента.',
        /* Форма изменения шапки: плательщик записан в дебет-ноте при создании и
           больше не меняется — меняются только дата и их номер. */
        counterpartyFixed: 'Плательщик записан при создании и не меняется.',
        reference: 'Их номер',
        noteDate: 'Дата документа',
        currency: 'Валюта',
        /* Позиции счёта сервер копирует из принятой котировки. Сказано затем,
           чтобы никто не искал в этой форме таблицу начислений, которой здесь
           намеренно нет. */
        submit: 'Создать',
      },

      /* Развилка под кнопкой «Новый документ». Две бумаги — это не два вида
       * одной формы: счёт выставляется КЛИЕНТУ из согласованной с ним ставки
       * (AD-10), дебет-нота перевыставляет расход тому, кто его должен (AD-26).
       * Раньше вопрос не задавался вовсе: первый экран предлагал отметить
       * расходы работы, и любая галочка молча превращала документ в дебет-ноту.
       * Поэтому у каждого пункта своя фраза — из двух слов не видно, к какой
       * стороне сделки обращена бумага и откуда возьмутся её строки. */
      new: {
        invoice: 'Счёт клиенту',
        invoiceHint: 'Из согласованной ставки. Строки подставятся из котировки.',
        debitNote: 'Дебет-нота',
        debitNoteHint: 'Перевыставить расходы работы клиенту.',
      },

      lines: {
        label: 'Позиции',
        /* Дебет-нота — это её позиции: клиенту выставляют то, чего не было в
           котировке, поэтому «за что именно» и есть весь вопрос. */
        empty: 'Позиций пока нет. Дебет-ноте нужна хотя бы одна — клиенту выставляют то, чего не было в котировке.',
        add: 'Добавить позицию',
        description: 'За что начислено',
        descriptionN: 'Позиция {{n}}: описание',
        notePlaceholder: 'Уточнение, напр. 4 контейнера (Open-top) — необязательно',
        noteN: 'Позиция {{n}}: уточнение',
        unitPrice: 'Цена',
        unitPriceN: 'Позиция {{n}}: цена',
        quantity: 'Кол-во',
        quantityN: 'Позиция {{n}}: количество',
        remove: 'Удалить позицию {{n}}',
      },
    },

    /* Рабочее место счёта — `routes/CaseInvoicePage.tsx`.
     *
     * СТРАНИЦА, а не третье окно в цепочке: счёт печатается на A4 и читается
     * поперёк — позиции, потолок ставки, по которому их судят, и проводка,
     * которая переводит их в USD, — это одна картина, а колонка в 440 пикселей
     * вмещает от силы одну её треть.
     *
     * Тексты лежат под `finance`, рядом с `documents` и `invoices`, а не под
     * `cases`, по той же причине, что и блок выше: раздел работы и страница
     * «Деньги» смотрят на одну и ту же бумагу, и второй словарь для одной из
     * них — это место, где два экрана начинают описывать одну запись
     * по-разному. */
    invoiceDesk: {
      /* Путь назад. Называет и работу, и раздел: читатель пришёл из одного из
         двух мест, и оба они — этот раздел. */
      back: 'Счета',
      title: 'Счёт клиенту',
      trancheN: 'Транш {{n}}',
      /* Счёт, который не закрывает ни один платёж по графику. Не «нет» и не
         пустое поле: доплата, перевыставление и работа вообще без графика —
         это обычные случаи, и формулировка должна читаться как осознанный
         ответ, а не как незаполненное поле. */
      offSchedule: 'Вне графика',
      /* Подпись варианта, пока список траншей ещё грузится или его чтение не
         удалось: связь на счёте есть в любом случае, и селект, откатившийся к
         «вне графика», сказал бы обратное тому, что написано в записи. */
      trancheLinked: 'Платёж по графику',
      trancheOption: 'Транш {{n}} · {{amount}}',
      numberOnIssue: 'номер присвоится при выпуске',
      issuedOn: 'Выпущен {{date}}',
      closedTranche: 'закрыл транш {{n}}',

      whatWeBill: 'Что выставляем',
      tranche: 'Платёж по договору',
      /* Сказано до нажатия, а не после. Выпуск счёта на транш убирает этот
         транш из реестра целей — счёт встаёт на его место, — и читатель, не
         знающий этого, воспримет исчезновение как пропажу данных. */
      trancheClosesNote:
        'Выпуск закроет транш {{n}}: он перестанет считаться отдельной ожидаемой суммой, чтобы дебиторка не удвоилась.',
      offScheduleNote:
        'Ни один транш не закроется: график платежей останется как был, а этот счёт встанет в дебиторку сам по себе. Потолок ставки на него всё равно распространяется.',
      /* AD-9: валюта — один из пяти фактов, зафиксированных вместе при
         создании, поэтому её показывают, а не предлагают. */
      currencyFrozen: 'Зафиксирована вместе с курсом. Другая валюта — это новый счёт.',

      lines: 'Строки счёта',
      linesFromQuote:
        'Подставлены из принятой котировки {{quote}}. Правятся прямо здесь, по одной позиции.',
      linesOwn:
        'Работу завели без котировки, поэтому подставлять было нечего — позиции набираются здесь.',
      /* The same two provenance lines for an ISSUED invoice, which cannot be
         edited — the draft wording above ends in an editing instruction (N-26). */
      linesFromQuoteIssued: 'Подставлены из принятой котировки {{quote}}.',
      linesOwnIssued: 'Работу завели без котировки, позиции введены вручную.',
      fromQuote: 'из котировки',

      /* Транш, который закрывает счёт, и сумма его позиций должны сходиться, и
         на сервере это ничем не обеспечено. Плашка называет расхождение, но не
         закрывает его: свести их — это решение (разделить позиции, снять счёт
         с графика, перекроить транш), и ни одно из них не кнопка, которую этот
         экран вправе нажать за человека. */
      mismatchTitle: 'Строки не сходятся с траншем {{n}}',
      mismatchBody:
        'Сумма строк — {{lines}}, а транш {{n}} ожидает {{tranche}}. Выпустив счёт как есть, вы закроете этот платёж документом на другую сумму, и остаток графика останется без покрытия. Поправьте строки или переведите счёт вне графика.',

      ceiling: 'Ставка по работе',
      ceilingApproved: 'Согласовано с клиентом',
      ceilingEarlier: 'Выставлено раньше',
      ceilingThis: 'В этом счёте',
      ceilingRemaining: 'Останется на следующий счёт',
      /* У работы нет согласованной ставки — значит, потолка НЕТ, а не что он
         равен нулю. Счёт всё равно можно выставить: ему просто не с чем
         сверяться. */
      noCeilingTitle: 'Ставка не согласована',
      noCeilingBody:
        'Работу завели напрямую, без котировки, поэтому строки подставлять неоткуда и потолка у счёта нет. Счёт выставить можно — он просто не будет ни с чем сверяться.',

      booking: 'Как считается в USD',
      bookingCurrency: 'Валюта документа',
      bookingRate: 'Курс на {{date}}',
      bookingNote:
        'Курс фиксируется при создании счёта и потом не пересчитывается. Нужна другая валюта — это новый счёт.',

      prints: 'Что напечатается',

      next: 'Что дальше',
      nextOne: 'Черновик правится сколько угодно.',
      nextTwo:
        'PDF собирается и ложится в архив работы. Любая правка черновика отменяет его.',
      nextThree:
        'Выпуск присваивает номер, закрывает транш и создаёт дебиторку. Отменить нельзя.',

      faceValue: 'Сумма счёта',
      received: 'Поступило',
      outstanding: 'Остаток долга',
    },

    /* Счета клиенту по работе (история 5.4, FR-61).
     *
     * «Счёт» здесь — бумага, которая уходит клиенту, а не архивная запись под
     * ней: это две разные записи, и словарь тоже должен оставаться разным. */
    invoices: {
      title: 'Счета клиенту',
      new: 'Новый счёт',
      newTitle: 'Новый счёт по {{number}}',
      raise: 'Создать черновик',

      caseLabel: 'Работа',
      /* Названо правилом, а не серой кнопкой: счёт выставляется по работе, и
         здесь сказано, какой шаг идёт первым. */
      chooseCaseFirst: 'Сначала выберите работу — счёт всегда выставляется по ней.',
      emptyTitle: 'По этой работе счетов нет',
      emptyDescription:
        'Создайте счёт, чтобы выставить его клиенту. По одной работе их может быть несколько — по счёту на каждый транш оплаты.',
      /* У черновика номера нет. Его присваивает история 5.5 при выставлении, а
         всё, что напечатано здесь до этого, не принадлежит ни одной серии
         (AD-12). */
      unnumbered: 'Номера пока нет',
      open: 'Открыть счёт {{invoice}} от {{date}}',

      invoiceDate: 'Дата счёта',
      economicDate: 'Экономическая дата',
      economicDateHint: 'Дата, на которую берётся курс.',
      /* Сказано, а не спрятано: проводка — это пять фактов, зафиксированных
         вместе (AD-9), и поле, тихо исчезнувшее после сохранения, выглядело бы
         ошибкой, а не правилом. */
      economicDateFrozen: 'Зафиксирована вместе с курсом. Чтобы изменить — создайте новый счёт.',
      customerReference: 'Ваш реф.',
      customerReferenceHint: 'Собственный номер клиента — так он и печатается в счёте.',

      currency: 'Валюта документа',
      currencyHint:
        'Выбирается один раз. Вместе с ней фиксируются курс и эквивалент в USD, и больше они не меняются.',

      vat: 'НДС',
      vatApplies: 'НДС применяется',
      /* FR-10: блок либо печатается, либо опускается, но никогда не печатается
         нулём — нулевая строка НДС говорит о необлагаемой поставке. */
      vatHint: 'Налоговый блок печатается только при включённой галочке.',
      vatRate: 'Ставка НДС, %',
      vatRateHint: 'В процентах: 20 означает 20%.',
      vatAmount: 'Налог по этому счёту: {{amount}}',

      paymentTerms: 'Условия оплаты',
      bankDetails: 'Банковские реквизиты',
      /* FR-64/AC-5: на счёте их не набирают. Указание источника — то, что
         останавливает поиск поля для правки. Условия — из работы, теми же
         словами, что в КП (#126, N-05); из юрлица — только реквизиты. */
      termsNote:
        'Условия оплаты берутся из работы — так, как они согласованы с клиентом, и меняются в работе. Из юрлица подставляются только банковские реквизиты — их меняют в настройках. Здесь ни то, ни другое не редактируется.',
      saveHeader: 'Сохранить шапку',

      lines: {
        label: 'Позиции',
        empty:
          'Позиций пока нет. Работа из котировки приходит с позициями, которые принял клиент; работа, заведённая напрямую, начинается с пустого списка.',
        description: 'За что начислено',
        quantity: 'Кол-во',
        unitPrice: 'Цена',
        amount: 'Сумма',
        amountHint: 'Сумма к выставлению, в {{currency}}.',
        amountPositive: 'Введите сумму больше нуля',
        /* Видно, только когда сумма НЕ равна произведению колонок рядом —
           значит её вписали руками поверх расчёта. Это законно (лот, круглая
           цифра, договорённость по телефону), но сказать об этом стоит: так же
           выглядит и наполовину доведённая правка. В обычном случае произведение
           уже стоит в самом поле, и подсказка, повторяющая своё же поле, учит
           не читать подсказки. */
        amountPreview: 'Не совпадает с расчётом: кол-во × цена = {{amount}}',
        add: 'Добавить позицию',
        saveCharge: 'Сохранить позицию',
        editN: 'Изменить позицию {{n}}',
        remove: 'Удалить позицию {{n}}',
        /* AD-10: отказ приходит в момент ДОБАВЛЕНИЯ позиции, а не при
           выставлении. Цифры собираются здесь, из счёта, который уже на
           экране, — сервер намеренно не присылает их внутри ошибки. */
        exceedsApprovedRate:
          'С этой позицией выставленная сумма превысит согласованную ставку {{approved}} на {{excess}}. По этой работе уже выставлено {{billed}}. Сначала нужно согласовать изменение ставки.',
        exceedsShort: 'Выше согласованной ставки',
        exceedsUnknownRate:
          'Эта позиция вывела бы сумму к выставлению за согласованную ставку по этой работе. Сначала нужно согласовать изменение ставки.',
      },
    },
  },

  clients: {
    title: 'Клиенты',
    subtitle: 'Кто сколько должен и какие работы стоят за цифрой.',
    // Ссылка на раздел у роли с «Нет доступа» к клиентам (#129).
    noAccess: {
      title: 'Раздел недоступен',
      description:
        'Клиенты открываются доступом вашей роли к разделу «Клиенты». Если он вам нужен, обратитесь к администратору.',
    },
    new: 'Новый клиент',
    year: { label: 'Год выписки', all: 'Все годы' },
    tiles: {
      billed: 'Начислено',
      received: 'Получено',
      balance: 'Остаток',
      balanceToday: 'Остаток на сегодня',
      opening: 'Остаток прошлого периода',
      cost: 'Себестоимость',
    },
    filters: { withBalance: 'Только с остатком' },
    columns: {
      client: 'Клиент',
      /* Два разных круга людей, и подписи не дают их спутать: менеджер по
         продажам самого клиента и те, кто вёл его работы в этом году. */
      salesContact: 'Менеджер по продажам',
      managers: 'Менеджеры работ',
      cases: 'Работ',
      weight: 'Вес, кг',
      billed: 'Начислено',
      received: 'Получено',
      /* S-22f: возврат клиенту — деньги, ушедшие клиенту обратно. */
      refunded: 'Возврат',
      balance: 'Остаток',
      cost: 'Себестоимость',
      status: 'Статус',
    },
    table: { label: 'Клиенты', emptyTitle: 'Клиентов пока нет', emptyDescription: 'Добавьте первого клиента — с него начинается каждая котировка.' },
    actions: { edit: 'Изменить', merge: 'Слить', open: 'Открыть клиента' },
    /* Кто стоит за клиентом: менеджер по продажам, отвечающий за него сейчас,
       и сотрудник, который его зарегистрировал, — это уже не меняется. */
    people: {
      salesContact: 'Менеджер по продажам',
      registeredBy: 'Зарегистрировал',
    },
    /* Подтверждение после регистрации с номером, который выдал сервер. */
    registered: 'Клиент зарегистрирован под номером {{number}}.',
    owner: {
      action: 'Сменить менеджера',
      title: 'Смена менеджера по продажам',
      colleague: 'Новый менеджер по продажам',
      confirm: 'Сменить',
      hint: 'Переходит только сам клиент. Его текущие котировки и работы остаются у тех, кто их ведёт.',
      notEligible: 'Этот сотрудник не может вести клиентов. Выберите того, кто работает с клиентами.',
    },
    moneyUnavailable: {
      title: 'Цифры временно недоступны',
      body: 'Справочник полный, а деньги сейчас прочитать не удалось. Попробуйте через минуту.',
    },
    statement: {
      title: 'Выписка',
      eyebrow: 'Клиент',
      tableLabel: 'Выписка по клиенту {{name}}',
      openingRow: 'Остаток прошлого периода',
      totalRow: 'Итого',
      emptyTitle: 'Перевозок за этот год нет',
      emptyDescription: 'Выберите другой год или все годы.',
      details: 'Реквизиты и контакты',
      columns: {
        no: '№',
        openedOn: 'Дата приёма',
        number: 'Работа',
        route: 'Маршрут',
        mode: 'Транспорт',
        cargo: 'Груз',
        weight: 'Вес, кг',
        volume: 'Объём, м³',
        billed: 'Начислено',
        received: 'Получено',
        refunded: 'Возврат',
        running: 'Остаток',
        paymentMethod: 'Оплата',
        lastReceivedOn: 'Дата оплаты',
        ata: 'Прибытие',
        transitDays: 'Дней',
        owner: 'Менеджер',
        cost: 'Себестоимость',
        status: 'Статус',
      },
    },
  },

  /* Раздел «Контрагенты» (S-22b): список с категориями и страница одного
     контрагента. Деньги (S-22e) — выписка, плитки, «Средняя ставка» и цифры в
     списке — в USD по курсу проводки и только по справочной записи. */
  counterparties: {
    title: 'Контрагенты',
    subtitle: 'Кто оказывает нам услуги — перевозчики, экспедиторы, брокеры, водители и агенты.',
    new: 'Новый контрагент',
    edit: 'Изменить контрагента',
    /* Фишки над таблицей: множественное число, «Все» — первой. */
    chipsLabel: 'Категории контрагентов',
    chips: {
      all: 'Все',
      carrier: 'Перевозчики',
      forwarder: 'Экспедиторы',
      'customs-broker': 'Брокеры',
      driver: 'Водители',
      agent: 'Агенты',
      other: 'Прочие',
    },
    search: 'Поиск по названию',
    inactive: 'Неактивные',
    table: { label: 'Контрагенты' },
    columns: {
      counterparty: 'Контрагент',
      category: 'Категория',
      country: 'Страна',
    },
    empty: {
      title: 'Контрагентов пока нет',
      description:
        'Добавьте перевозчика, экспедитора или брокера, с которым работаете, — он появится во всех формах, где выбирают контрагента.',
    },
    /* Читатель без права видеть контрагентов: сюда ведёт только старая или
       набранная вручную ссылка — пункта в меню у него нет. */
    forbidden: {
      title: 'Контрагенты недоступны для вашей роли',
      description:
        'Справочник контрагентов открывается правом видеть контрагентов. Если оно нужно, обратитесь к администратору.',
    },
    record: {
      eyebrow: 'Контрагент',
      facts: {
        label: 'Основное о контрагенте',
        country: 'Страна',
        taxId: 'БИН',
        contact: 'Контакт',
        averageRate: 'Средняя ставка',
      },
      details: 'Реквизиты и контакты',
      noContacts: 'Контактов нет',
    },
    /* Деньги контрагента (S-22e): плитки страницы и колонки списка. Всё
       считает сервер; аванс — неразнесённый платёж — показан рядом и не входит
       ни в «Оплачено», ни в «Остаток». */
    money: {
      tiles: {
        units: 'Перевозки',
        billed: 'Начислено',
        paid: 'Оплачено',
        balance: 'Остаток к оплате',
      },
      columns: {
        units: 'Перевозки',
        billed: 'Начислено',
        paid: 'Оплачено',
        balance: 'Остаток к оплате',
      },
      advance: 'Аванс, не разнесено:',
      /* Читатель, чьи деньги ограничены его работами. */
      scoped: 'Цифры по вашим работам',
      unavailable: {
        title: 'Цифры временно недоступны',
        body: 'Работы видны, а деньги сейчас прочитать не удалось. Попробуйте через минуту.',
        listBody: 'Список полный, а деньги сейчас прочитать не удалось. Попробуйте через минуту.',
      },
      forbidden: {
        title: 'Нет доступа к деньгам работ',
        description:
          'Выписка открывается правом видеть деньги работ. Если оно нужно, обратитесь к администратору.',
      },
    },
    statement: {
      title: 'Выписка',
      year: 'Год',
      tableLabel: 'Выписка по контрагенту',
      openingRow: 'Остаток прошлого периода',
      totalRow: 'Итого',
      columns: {
        number: 'Работа',
        openedOn: 'Дата',
        route: 'Маршрут',
        units: 'Перевозки',
        billed: 'Начислено',
        paid: 'Оплачено',
        balance: 'Остаток',
        running: 'Нарастающий',
      },
      /* Кнопка-стрелка у строки: раскрывает, из чего сложилась работа. */
      toggle: 'Состав работы {{number}}',
      items: {
        leg: 'Плечо {{position}}',
        legWhole: 'Плечо {{position}} целиком',
        other: 'Прочие затраты',
        advance: 'Аванс, не разнесено',
        noNumber: 'без номера',
      },
      /* Состояние оплаты строки — строчными, как подпись рядом с суммой. */
      settlement: {
        paid: 'оплачено',
        'partly-paid': 'частично',
        unpaid: 'не оплачено',
      },
      empty: {
        title: 'Работ с этим контрагентом пока нет',
        description: 'Здесь появятся перевозки, затраты и платежи.',
      },
      emptyYear: {
        title: 'За этот год работ нет',
        description: 'Выберите другой год или все годы.',
      },
    },
    form: {
      category: 'Категория',
      taxId: 'БИН / ИНН',
      registrationId: 'Рег. номер',
      address: 'Адрес',
    },
    actions: {
      edit: 'Изменить',
      more: 'Действия с контрагентом',
      deactivate: 'Деактивировать',
      activate: 'Активировать',
      merge: 'Объединить',
      delete: 'Удалить',
    },
    /* Отказы сервера словами (FR-39): номера подставляются, а не склеиваются. */
    refusals: {
      deleteTitle: 'Удалить нельзя',
      mergeTitle: 'Объединить нельзя',
      /* Контрагент в дебет-ноте: ноты — замороженная история, их не
         переносят. Предлагается только деактивация, никогда объединение. */
      mergeDebitNotes:
        'Контрагент указан в дебет-нотах {{list}}, а их не переносят на другую запись. Деактивируйте его — он перестанет предлагаться в новых записях и останется там, где уже указан.',
      categoryMismatched: 'Объединить можно только контрагентов одной категории.',
    },
  },

  directories: {
    title: 'Справочники',
    subtitle: 'Записи, к которым привязаны котировки и работы, в одном месте.',

    /* Отказ в удалении (FR-39, история 7.1 AC 4, история 7.4 AC 5).
     *
     * Номера подставляются, а не склеиваются на месте вызова: в русской фразе
     * список стоит в другом месте, и предложение, собранное из обрывков, его
     * туда не перенесёт (UX-DR-30). */
    referenced: {
      // `more`, а не `count`: i18next считает `count` признаком множественного
      // числа и стал бы искать здесь andMore_one/andMore_other, а рядом — четыре
      // русские формы, которые этой фразе не нужны.
      andMore: '{{named}} и ещё {{more}}',
      mergeInstead:
        'Запись используется в {{list}}, поэтому удаление оставит их без ссылки. Объедините её с другой записью.',
      deactivateInstead:
        'Запись используется в {{list}}, поэтому удалить её нельзя. Деактивируйте её — она перестанет предлагаться в новых записях и останется в тех, где уже указана.',
      // Объединение, которое не может выполниться: в этой установке нечем
      // перенести ссылки. Формулировка отличается намеренно — она не должна
      // отправлять читателя к действию, которое только что отказало.
      mergeUnavailable:
        'Запись используется в {{list}}, и эта установка пока не умеет переносить такие ссылки — объединение оставит их без ссылки. Деактивируйте её.',
    },

    /* Объединение одной записи с другой — действие, которое FR-39 предлагает
     * вместо отклонённого удаления. */
    merge: {
      action: 'Объединить',
      title: 'Объединить с другой записью',
      description:
        'Всё, что ссылается на эту запись, будет ссылаться на выбранную, а эта запись будет удалена. Отменить нельзя.',
      target: 'Объединить с',
      targetPlaceholder: 'Выберите запись, которая останется',
      confirm: 'Объединить и удалить',
      sameRecord: 'Выберите другую запись для объединения.',
    },

    activate: 'Активировать',
    deactivate: 'Деактивировать',
    // Причина отказа — во всплывающей подсказке, а не через disabled: у
    // отключённой кнопки подсказка недоступна при наведении (UX-DR-22).
    needsGrant: 'Для этого списка нужен грант на справочник.',

    /* Блок контактов, общий для клиентов и контрагентов (FR-39, FR-41). */
    contacts: {
      legend: 'Контакты',
      label: 'Контактное лицо',
      labelPlaceholder: 'например, Иванов Иван или отдел логистики',
      add: 'Добавить контакт',
      remove: 'Удалить контакт',
      primary: 'Основной контакт',
      handlePlaceholder: 'ник или ссылка на профиль',
      /* Сказано прямо: поле, похожее на канал доставки, будут использовать
         как канал доставки. */
      messengerNote:
        'Только контактные данные. Платформа не отправляет сюда сообщения — переписка ведётся вне неё.',
      restricted: 'Контактные данные недоступны для вашей роли.',
      /* Сервер не узнал id контакта: список на экране старше сохранённого. */
      stale: 'Контакт изменил кто-то другой — откройте форму заново',
    },

    clients: {
      title: 'Клиенты',
      new: 'Новый клиент',
      delete: 'Удалить',
      edit: 'Изменить клиента',
      name: 'Название',
      country: 'Страна',
      taxId: 'ИНН',
      registrationId: 'ОГРН / рег. номер',
      address: 'Адрес',
      code: 'Код клиента',
      /* Подставляется в номера документов (FR-39, FR-68), поэтому стоит
         знать, где он появится. */
      codeHint: 'Необязательно. Может подставляться в номера документов — держите его коротким.',
      codeInvalid: 'Только буквы, цифры и дефис',
      /* Номер из реестра выдаёт сервер, вручную его не вводят, поэтому форма
         его только показывает. */
      number: 'Номер клиента',
      numberAssigned: 'Присваивается автоматически при сохранении — из единой для всей компании серии номеров клиентов.',
      numberTaken:
        'Следующий номер клиента уже занят. Измените шаблон нумерации клиентов в разделе «Настройки → Нумерация» и сохраните снова.',
      /* Клиент, которому выставлены дебет-ноты, не сливается: выставленные
         ноты — замороженная история, их не переносят. Предлагается только
         деактивация, никогда снова объединение. */
      mergeDebitNotes:
        'Клиенту выставлены дебет-ноты {{list}}, а их не переносят на другую запись. Деактивируйте клиента — он перестанет предлагаться в новых записях и останется там, где уже указан.',
      contact: 'Контакт',
      owner: 'Ответственный',
      open: 'В работе',
      receivable: 'Дебиторка',
      /* Выбор клиента в формах предложения (QW-12). Ищет и по номеру из
         реестра, и по коду: на бумаге клиента называют номером. */
      search: 'Название или номер…',
      /* Пустой список. У продавца в справочнике только свои клиенты, поэтому
         пустой список в первый день — это область видимости, а не сбой, и текст
         называет оба выхода. Второй вариант — для роли, которой создавать
         клиентов нельзя. */
      pickerEmpty:
        'Здесь только ваши клиенты. Если компания уже работает с этим клиентом — попросите руководителя передать его вам; иначе создайте нового.',
      pickerEmptyNoCreate:
        'Здесь только ваши клиенты. Если компания уже работает с этим клиентом — попросите руководителя передать его вам.',
    },

    users: {
      title: 'Пользователи',
      new: 'Новый пользователь',
      edit: 'Изменить пользователя',
      email: 'Email',
      invalidEmail: 'Введите корректный email',
      /* Имя человека, а не «Название», как у компании (S-7, A-2). */
      name: 'Имя',
      role: 'Роль',
      office: 'Офис',
      /* Столбец списка: собственные права сотрудника сверх роли (S-7). */
      extras: 'Дополнительно',
      /* Карточка сотрудника (S-7, A-5): сначала что даёт роль — только для
         чтения, — затем то, что выдано лично ему. */
      roleCapabilities: 'Права роли',
      roleCapabilitiesHint:
        'Задаются в роли и меняются сразу для всех её сотрудников — в разделе «Роли и доступ».',
      /* Тот же смысл без ссылки на раздел — для того, кому вкладка «Роли и доступ»
         не открыта (только admin.users.manage). */
      roleCapabilitiesHintNoEditor:
        'Задаются в роли и меняются сразу для всех её сотрудников.',
      roleCapabilitiesNone: 'Роль не даёт никаких прав.',
      roleCapabilitiesLoading: 'Загрузка прав роли',
      roleCapabilitiesFailed: 'Не удалось загрузить права роли.',
      extraGrants: 'Дополнительно этому сотруднику',
      extraGrantsHint: 'Сверх роли: право остаётся у сотрудника, даже если роль его потеряет.',
      grantedByRole: 'Даётся ролью',
      alsoGrantedByRole: 'Также даётся ролью',
      /* Правило полномочий (S-7, O7): выдать можно только то, что есть у вас
         самих, и действовать — только над тем, у кого прав не больше. */
      roleNotAssignable: 'Эта роль даёт больше прав, чем у вас',
      /* Новая учётная запись, а выдать нельзя ни одну роль из списка. */
      noAssignableRole: 'Нет роли, которую вы можете выдать',
      grantNotYours: 'Вы не можете выдать это право',
      outranked: 'У сотрудника больше прав, чем у вас',
      outrankedRefusal:
        'У сотрудника больше прав, чем у вас: изменить его учётную запись может только супер-админ.',
      /* Отказы сервера по токену в `detail` (S-13, контракт Conflict в
         http-api.md): `email-taken`, `self-demotion`, `self-deactivation`,
         `last-super-admin`. Сам токен на экран не попадает. */
      refusals: {
        emailTaken: 'Сотрудник с таким email уже есть',
        selfDemotion: 'Свою роль поменять нельзя',
        selfDeactivation: 'Свой аккаунт деактивировать нельзя',
        lastSuperAdmin: 'Должен остаться хотя бы один активный супер-админ',
      },
      grantLabels: {
        manageClients: 'Ведение клиентов',
        manageParticipants: 'Ведение контрагентов',
        manageTransport: 'Ведение справочника транспорта',
      },
      status: 'Статус',
      inactive: 'Неактивен',
      language: 'Язык интерфейса',
      /* Адрес — идентификатор учётной записи; его смена переписала бы
         авторство всех прошлых записей (история 1.3). */
      emailFixed: 'Email нельзя изменить после создания учётной записи.',
      awaitingPasswordChange: 'Пароль ещё не сменён',
      resetPassword: 'Сбросить пароль',
      deactivate: 'Деактивировать',
      activate: 'Активировать',
      /* Передача пароля. Пароль показывается здесь ровно один раз — на
         сервере хранится только его хэш. Письмо владельцу уходит не всегда:
         без настроенной почты или вне продакшена оно никуда не попадает, и
         сервер сообщает об этом полем `credentialMailed`. Поэтому ветки две,
         и ни одна не говорит «отправлен»: true значит «поставлено в очередь»,
         а не «доставлено» (QW-5). Смена пароля при первом входе теперь
         действительно обязательна, поэтому эта строка есть в обеих ветках. */
      credentialTitle: 'Пароль для {{name}}',
      credentialIntroMailed:
        'Учётная запись готова к работе. Пароль отправляется письмом на {{email}} вместе со ссылкой для входа и показан здесь на случай, если передать его нужно лично.',
      credentialNotMailed: 'Письмо владельцу не отправлено — передайте пароль сами',
      credentialIntroNotMailed:
        'Учётная запись {{email}} готова к работе. Владельцу пароль письмом не отправлен.',
      credentialCopy: 'Копировать',
      credentialCopied: 'Скопировано',
      credentialOnce: 'Здесь пароль показывается один раз и не восстанавливается.',
      credentialFirstLogin: 'При первом входе система попросит сменить пароль.',
      credentialMailMissing: 'Если письмо не придёт, сбросьте пароль — новый уйдёт письмом.',
      credentialDone: 'Я сохранил пароль',
    },

    transport: {
      title: 'Транспорт',
      modes: 'Виды перевозки',
      equipmentTypes: 'Типы оборудования',
      add: 'Добавить',
      mode: 'вид перевозки',
      equipment: 'тип оборудования',
      addItem: 'Новый — {{item}}',
      editItem: 'Изменить — {{item}}',
      code: 'Код',
      codeHint: 'Короткий машинный код, например FCL или 40HC.',
      delete: 'Удалить',
    },

    editRecord: 'Изменить — {{name}}',
  },

  settings: {
    title: 'Настройки',
    /* The page subtitle names only the tabs the reader holds (N-31): each tab
       contributes one lowercase noun phrase, joined by `settingsSubtitle`. */
    subtitleParts: {
      entities: 'юрлица',
      numbering: 'нумерация',
      templates: 'шаблоны КП',
      directions: 'шаблоны маршрутов',
      users: 'сотрудники',
      access: 'роли',
    },
    sectionsLabel: 'Разделы настроек',

    tabs: {
      entities: 'Юрлица',
      numbering: 'Нумерация',
      /* «Шаблоны КП», а не просто «Шаблоны»: рядом стоят «Шаблоны маршрутов»,
         и голое слово читалось как второе имя того же справочника (F1). */
      templates: 'Шаблоны КП',
      directions: 'Шаблоны маршрутов',
      users: 'Пользователи',
      access: 'Роли и доступ',
    },

    /* Ни одна вкладка не открыта правами читателя — обычно сюда попадают
       по старой ссылке: пункта «Настройки» в меню у такого читателя нет. */
    noTabs: {
      title: 'Нет доступных разделов',
      description:
        'Разделы настроек открываются правами администрирования: учётные записи, шаблоны, роли. Если они вам нужны, обратитесь к администратору.',
    },

    /* Роли и права — редактируемая модель миграции 0021. */
    roles: {
      title: 'Роли',
      intro:
        'Роль — это набор прав, который вы называете сами. У сотрудника одна роль плюс права, выданные ему лично.',
      create: 'Новая роль',
      loading: 'Загрузка ролей',
      edit: 'Изменить «{{name}}»',
      editAction: 'Изменить «{{name}}»',
      deleteAction: 'Удалить «{{name}}»',
      loadFailed: 'Не удалось загрузить роли. Попробуйте ещё раз.',
      /* Отказы сервера по токену в `detail` (S-13, контракт Conflict в
         http-api.md). Встроенная роль, роль супер-админа и последний
         супер-админ достижимы с этого экрана только из устаревшей вкладки:
         кнопок, которые их вызывают, здесь нет. */
      refusals: {
        roleInUse: 'Роль назначена сотрудникам — сначала переназначьте их',
        systemRole: 'Встроенную роль нельзя удалить',
        systemRoleKeyFixed: 'Ключ встроенной роли менять нельзя',
        superuserRole: 'Роль супер-админа нельзя удалить, а её права — изменить',
        lastSuperAdmin: 'Должен остаться хотя бы один активный супер-админ',
      },
      superuserLocked:
        'У этой роли всегда есть все права и доступ ко всем записям — отмечать и выбирать нечего. Именно поэтому она может восстановить остальные.',
      /* Право управлять ролями пока только у супер-админа (S-7, O8): его нельзя
         добавить ни одной роли. */
      superuserOnly: 'Только у супер-админа',
      /* #133: роль, которая меняет плечи, но не видит контрагентов, получает
         каждое сохранённое плечо со скрытыми перевозчиками, и страница плеча
         открывает его только для просмотра. Права подставляются их подписями. */
      warnings: {
        legsNeedCounterparties: {
          title: 'Сохранённые плечи будут открываться только для просмотра',
          body: 'У роли есть право «{{edit}}», но нет права «{{read}}». Без него роль не видит перевозчиков и водителей, поэтому сохранённое плечо открывается ей только для просмотра. Отметьте второе право, чтобы роль могла работать с плечами и фурами.',
        },
      },
      badges: {
        superuser: 'Полный доступ',
        system: 'Встроенная',
      },
      summary: {
        allPermissions: 'Все права',
        counts: 'Прав: {{capabilities}} · Разделов: {{surfaces}}',
        users_one: '{{count}} сотрудник',
        users_few: '{{count}} сотрудника',
        users_many: '{{count}} сотрудников',
      },
      fields: {
        key: 'Идентификатор',
        keyHint: 'Строчные латинские буквы, цифры и дефисы. Изменить позже нельзя.',
        name: 'Название',
        nameSeededHint:
          'Встроенная роль. В интерфейсе она называется «{{label}}» на языке читателя, что бы здесь ни было написано.',
        description: 'Описание',
        descriptionHint: 'Для чего нужна эта роль — своими словами.',
      },
      scopes: {
        title: 'Что видно',
        hint: 'Чьи записи доступны этой роли. Если поставить «Нет доступа», раздел полностью исчезнет из навигации.',
      },
      capabilities: {
        title: 'Что можно делать',
        hint: 'Каждое право выключено, пока вы его не включите.',
      },
      levels: {
        none: 'Нет доступа',
        own: 'Только свои',
        assigned: 'Свои и те, где участвует',
        all: 'Все',
      },
      surfaces: {
        quotes: 'Котировки',
        cases: 'Работы',
        'case-money': 'Затраты и прибыль по работе',
        'cross-case-finance': 'Финансы компании',
        clients: 'Клиенты',
      },
      groups: {
        quote: 'Котировки',
        case: 'Работы',
        directory: 'Справочники',
        counterparty: 'Контрагенты',
        finance: 'Финансы',
        document: 'Документы',
        collab: 'Файлы и чат',
        route: 'Шаблоны маршрутов',
        admin: 'Администрирование',
      },
      /* По одной подписи на каждое право, которое регистрирует сервер. Словарь
         торговли: работа, ставка, КП. Английский текст сервера показывается
         только для права, которого этот список ещё не знает. */
      capabilityLabels: {
        quoteCreate: 'Создавать черновик котировки',
        quoteSend: 'Отправлять КП клиенту',
        quoteTeamAssign: 'Добавлять коллегу в команду котировки',
        quotePricingProvide: 'Считать котировку по запросу и отмечать «Расчёт готов»',
        caseCreate: 'Открывать работу и отменять её',
        caseCloseOperational: 'Закрывать работу операционно (груз доставлен)',
        caseCloseFinancial: 'Закрывать работу финансово (все расчёты завершены)',
        caseRouteEdit: 'Добавлять, менять и удалять плечи работы',
        caseTeamAssign: 'Добавлять коллегу в команду работы',
        caseReopen: 'Переоткрывать финансово закрытую работу (с записью в журнал)',
        caseOperationsLead:
          'Назначаться ответственным от операций, когда котировка становится работой, и видеть в «Моих задачах» работы без плеч, плечи без фуры и работы без фактических затрат',
        directoryClientCreate: 'Добавлять клиента и становиться ответственным за него',
        directoryClientManage: 'Объединять, удалять и править любого клиента, не только своего',
        directoryTransportManage: 'Добавлять, переименовывать и отключать виды транспорта и оборудование',
        directoryContactRead: 'Видеть контакты участников (телефон, почта, адрес)',
        /* S-22b: общий справочник компании, «своих» контрагентов нет. */
        counterpartyRead: 'Видеть контрагентов: перевозчиков, экспедиторов, брокеров, водителей и агентов',
        counterpartyCreate: 'Добавлять контрагента, править и деактивировать любого контрагента',
        counterpartyManage: 'Объединять и удалять контрагентов',
        financeExpenseRecord: 'Вносить и править расчётные и фактические затраты по работе',
        financePaymentRecord: 'Записывать оплату и разносить её по документам',
        financePaymentClient:
          'Вносить платёж клиента по доступной работе и разносить его по счетам, дебет-нотам и траншам работы',
        financeReceivablesRead: 'Видеть долги по срокам, балансы и реестр дебиторки по всем работам',
        financePaymentCarrier:
          'Оплачивать перевозчику затраты по плечу: признать долг, записать и разнести одним действием',
        financePaymentConfirm: 'Подтверждать записанную оплату или удалять неподтверждённую',
        financeRateApprove: 'Согласовывать изменение ставки по работе',
        financeRateSet: 'Устанавливать ставку по работе',
        documentOfferRender: 'Готовить черновик КП в PDF',
        documentOfferIssue: 'Выпускать КП как официальный документ',
        documentInvoiceRender: 'Готовить и открывать счета и их PDF',
        documentInvoiceIssue: 'Выставлять счёт, создавая дебиторскую задолженность',
        documentDebitnoteRender: 'Готовить и открывать дебет-ноты и их PDF',
        documentDebitnoteIssue: 'Выставлять дебет-ноту, создавая дебиторскую задолженность',
        documentArchiveRead: 'Видеть документы по записи; открывать КП и статусы клиенту',
        collabFilesRead: 'Открывать файлы и чат работы или котировки',
        collabFilesUpload: 'Добавлять файлы в работу',
        collabChatPost: 'Писать в чат работы или котировки',
        collabChatDelete: 'Удалять чужие сообщения в чате',
        collabChatReadAll: 'Читать чаты всех работ и котировок, а не только своей команды',
        /* S-10: без остальных настроек; удалять шаблон — по-прежнему право на настройки. */
        routeTemplateManage: 'Создавать, править и скрывать шаблоны маршрутов',
        adminUsersRead: 'Видеть список пользователей',
        adminUsersManage: 'Создавать и править учётные записи, сбрасывать пароли',
        adminRolesRead: 'Видеть роли и права каждой из них',
        adminRolesManage: 'Создавать и править роли и менять их права',
        /* Что право действительно открывает (S-7, O6): юрлица и нумерация
           закрыты правом на учётные записи. */
        adminSettingsManage: 'Менять шаблоны КП, пороги напоминаний и шаблоны маршрутов',
        adminAuditRead: 'Читать журнал изменений',
      },
    },
    access: {
      title: 'Матрица доступа',
      /* N-09: таблица строится из сохранённых ролей — столбец на каждую,
         включая созданные основателем. Слова ячеек — те же, что в редакторе
         ролей (`settings.roles.levels`, `capabilityLabels`), поэтому своих
         уровней здесь больше нет. */
      subtitle:
        'Что каждая роль настроена видеть и делать — по столбцу на роль, включая созданные вами. Сверх роли у человека могут быть личные права в его карточке пользователя.',
      tableCaption: 'Что видит и может делать каждая роль',
      dataGroup: 'Раздел или право',
      /* Для экранного диктора: в ячейке права видна только галочка или прочерк. */
      held: 'Есть',
      notHeld: 'Нет',

      enforcementNote:
        'Проверяется на сервере при каждом запросе. То, что скрывает интерфейс, — это удобство, а не сама граница.',

      grantsTitle: 'Права ведения',
      grantsSubtitle:
        'Дополнение к роли, а не отдельные роли. Право расширяет справочник; доступ к работам и деньгам оно не расширяет никогда.',
      grants: {
        manageClients:
          'Ведение всего списка клиентов, включая чужие записи, — со слиянием и удалением.',
        manageParticipants:
          'Удаление и объединение контрагентов. Действует только вместе с правом видеть контрагентов.',
        manageTransport: 'Ведение видов транспорта и типов оборудования.',
      },
      grantsNote:
        'Выдаётся каждому отдельно в разделе «Настройки → Пользователи». У основателей есть все три.',

      boundariesTitle: 'Границы, которые стоит проговорить',
      boundaries: {
        /* Право названо так, как его называет редактор ролей, а не кодом. */
        agentIdentityScope: {
          title: 'Скрытие агента касается только полей',
          body:
            'Перевозчиков плеч, перевозчика и водителя любой единицы (фуры, контейнера, вагона…), поставщиков в затратах и сторону дебет-ноты видит только роль с правом «Видеть контрагентов»; без него поле показывает «Перевозчик скрыт», «Поставщик скрыт» или «Агент скрыт», а не пустоту. Загруженные файлы и чат по работе не редактируются.',
        },
      },
      externalTag: 'Не пользователи',
      externalNote:
        'Клиенты, отправители, получатели, агенты, владельцы транспорта, диспетчеры и водители не входят в систему. Они получают только письма.',
    },

    entities: {
      title: 'Юрлица',
      new: 'Новое юрлицо',
      edit: 'Изменить юрлицо',
      name: 'Название юрлица',
      legalFooter: 'Юридический футер',
      /* Два коротких кода, которые подставляются в шаблон номера (FR-68). */
      deactivate: 'Деактивировать',
      activate: 'Активировать',
      inactive: 'Деактивировано',
      /* Незаполненный футер или реквизиты уходят клиенту пустой строкой. */
      notSet: 'Не задано',
      initials: 'Инициалы',
      officeCode: 'Код офиса',
      /* Карточка юрлица называет коды словами, а не токенами шаблона: токен
         {INITIALS} понятен тому, кто открыл вкладку нумерации, а не тому, кто
         читает карточку. В подсказке формы токен остаётся — внутри фразы,
         которая его объясняет. */
      initialsTag: 'Инициалы: {{value}}',
      officeTag: 'Офис: {{value}}',
      initialsHint: 'Подставляется в номер вместо {INITIALS}',
      officeCodeHint: 'Подставляется в номер вместо {OFFICE}',
      defaultOffice: 'По умолчанию для офиса',
      defaultOfficeHint:
        'Записи, созданные пользователями этого офиса, выпускаются от этого юрлица. Не более одного юрлица на офис.',
      noDefaultOffice: 'Ни один офис не использует это юрлицо',
      legalFooterHint: 'Печатается внизу каждого документа этого юрлица.',
      bankDetails: 'Банковские реквизиты',
      emailSignature: 'Подпись в письмах',

      /* Загруженный логотип (FR-64, миграция 0041). До него каждый документ
         печатал НАЗВАНИЕ юрлица там, где должен быть логотип. */
      logo: {
        upload: 'Загрузить логотип',
        replace: 'Заменить логотип',
        remove: 'Удалить',
        sending: 'Логотип отправляется…',
        /* Говорит о том, что сделает документ, а не о том, что принимает форма:
           читателя интересует, что получит клиент. */
        hint: 'PNG или JPEG, до 2 МБ. Печатается на каждом предложении, счёте и дебет-ноте этого юрлица. Без него документы несут название юрлица.',
        errorType: 'Файл такого типа нельзя напечатать на документе. Используйте PNG или JPEG.',
        errorSize: 'Логотип больше 2 МБ. Экспортируйте его меньшего размера и попробуйте снова.',
        errorUpload: 'Не удалось отправить логотип в хранилище. Проверьте соединение и попробуйте снова.',
        errorGeneric: 'Не удалось сохранить логотип.',
      },
    },

    numbering: {
      title: 'Схемы нумерации',
      new: 'Новая схема',
      edit: 'Изменить схему',
      entity: 'Юрлицо',
      document: 'Документ',
      pattern: 'Шаблон',
      kinds: {
        quote: 'Котировка',
        case: 'Работа',
        invoice: 'Инвойс',
        'debit-note': 'Дебет-нота',
        client: 'Клиент',
      },
      /* Серии, которые принадлежат всей компании, а не одному юрлицу, — пока
         это только реестр клиентов. */
      company: {
        title: 'Общие для компании',
        hint: 'Номера клиентов — одна серия на всю компанию, какой бы офис ни регистрировал клиента. Серия никогда не сбрасывается.',
        changeNote:
          'Изменение шаблона действует на клиентов, зарегистрированных с этого момента. Уже выданные номера не меняются, а счёт продолжается, а не начинается заново.',
      },
      /* Показывается, когда шаблон не задан и действует значение по умолчанию. */
      usingDefault: 'По умолчанию',
      /* Сервер отклоняет шаблон стабильным кодом; здесь его переводы (AD-27). */
      errors: {
        missingSeq: 'В шаблоне нужен {seq}, иначе все записи получат один номер.',
        unknownToken:
          'Неизвестный токен. Используйте {INITIALS}, {OFFICE}, {YY}, {YYYY}, {MM} или {seq}.',
        seqTooWide: 'Слишком широкое дополнение нулями.',
        periodTokenNotAllowed:
          'Серия номеров клиентов никогда не сбрасывается, поэтому в ней нельзя использовать {YY}, {YYYY} и {MM}.',
        entityTokenNotAllowed:
          'Серия номеров клиентов не принадлежит ни одному юрлицу, поэтому в ней нельзя использовать {INITIALS} и {OFFICE}.',
        invalid: 'Такой шаблон использовать нельзя.',
      },
      liveExample: 'Живой пример',
      next: 'Следующий',
      changeNote:
        'Изменение шаблона действует на документы, выпущенные с этого момента. Уже выпущенные номера сохраняют свою форму.',
    },

    templates: {
      /* Четыре группы, и печатаются только первые три. Формулировки эту разницу
         сохраняют: три печатных — текст в документе, заготовки — список, с
         которого начинается расчёт себестоимости. */
      included: 'Включено',
      notIncluded: 'Не включено',
      disclaimers: 'Оговорки',
      costItems: 'Заготовки статей затрат',
      costItemsHint:
        'Статьи затрат, с которых начинается расчёт. В КП не печатаются и сумм не содержат — деньги вносятся в каждой котировке отдельно.',
      /* Текст, с которого начинается каждая новая котировка (N-19). Он зашит в
         продукт, а не лежит в списке ниже, поэтому раньше карточки «Включено»,
         «Не включено» и «Оговорки» выглядели пустыми, хотя в каждом КП текст был. */
      standing: {
        title: 'Стандартный текст',
        note: 'Встроен в продукт и подставляется в каждую новую котировку на языке её автора. Здесь не редактируется.',
        none: 'Стандартного текста для этого раздела нет.',
        loadFailed: 'Не удалось загрузить стандартный текст.',
      },
      library: {
        title: 'Ваши шаблоны',
        empty: 'Своих шаблонов пока нет',
      },
      new: 'Новый шаблон',
      edit: 'Изменить шаблон',
      label: 'Название',
      body: 'Текст',
      bodyHint: 'Попадает в КП дословно. Пишите так, как это прочитает клиент.',
      bodyHintCostItem:
        'Необязательно. Пометка для того, кто считает цену, — за пределы компании не выходит.',
      /* Почему шаблон удаляется полностью, тогда как записи справочников только
         деактивируются: миграция 0033 копирует текст в саму котировку, поэтому
         удаление не меняет ни один уже отправленный документ. */
      removeHint: 'Удаление шаблона не меняет уже отправленные предложения.',
    },

    /* История 2.9, критерий 4. Каждая подсказка называет того, кто читает
       значение, включая два, которые пока не читает никто, — настройка, молча
       ни на что не влияющая, учит основателя, что элементы управления
       декоративны. */
    thresholds: {
      title: 'Пороги напоминаний и внимания',
      configured: 'Значения заданы для этой компании.',
      usingDefaults: 'Значения ещё не сохранены, поэтому действуют значения по умолчанию.',
      followUp: 'Напоминание о повторном обращении',
      followUpHint: 'Дней после отправки предложения без ответа',
      validityWarning: 'Предупреждение об истечении срока',
      validityWarningHint: 'Дней до даты окончания действия котировки',
      stuckShipment: 'Задержанная перевозка',
      stuckShipmentHint: 'Дней, которые работа может простоять без движения',
      defaultIs: 'по умолчанию {{days}}',
      range: 'От {{min}} до {{max}} дней',
      wholeDays: 'Введите целое число дней.',
      outOfRange: 'Вне диапазона, который принимает эта настройка.',
      awaitingReminderJob: 'пока не читается ни одной задачей напоминаний',
      /* Кто читает порог «Задержанная перевозка». Сохранённое значение читают
         дашборд и метка в списке работ (`stuckDaysFor` в readmodel.go); пока
         ничего не сохранено, сервер берёт своё значение по умолчанию. Имя
         переменной окружения сюда не попадает — оно для того, кто разворачивает
         сервер, а не для того, кто правит настройку. */
      stuckReadBy: 'Используется дашбордом и меткой «Нет движения» в списке работ.',
      stuckUsingDefault: 'Пока не сохранено — действует значение по умолчанию сервера.',
    },

    /* Каталог шаблонов маршрутов (issue #35, история 1; ключ остался
       `directions`). Шаблон — это упорядоченный список точек, у каждой страна
       и норматив перехода в днях.
       Issue #35 назвал записи «направлениями», как и плечо, которое они
       заполняют. Глоссарий QW-2 (решение владельца, 2026-10-08) это отменил:
       применение КОПИРУЕТ точки в плечо и ничего не связывает, так что это две
       разные вещи с двумя разными словами. Копирование по-прежнему прямо
       названо в подзаголовке, а не оставлено слову. Род мужской: «шаблон …
       выключен». */
    directions: {
      title: 'Шаблоны маршрутов',
      subtitle:
        'Готовые маршруты, из которых заполняется плечо работы. При применении точки копируются в плечо, поэтому правка шаблона не сдвигает уже идущую перевозку.',
      empty: 'Шаблонов маршрутов пока нет.',
      new: 'Новый шаблон маршрута',
      edit: 'Изменить шаблон маршрута',
      inactive: 'выключен',
      activate: 'Включить',
      deactivate: 'Выключить',
      pointCount_one: '{{count}} точка',
      pointCount_few: '{{count}} точки',
      pointCount_many: '{{count}} точек',
      normDays_one: 'норматив {{count}} день',
      normDays_few: 'норматив {{count}} дня',
      normDays_many: 'норматив {{count}} дней',
      name: 'Название',
      nameHint: 'Как его называют на месте — «Хоргос → Ашхабад (через Сарахс)».',
      points: 'Точки маршрута',
      pointsHint: 'В порядке следования. Первая точка — там, где груз начинает путь.',
      pointPlaceholder: 'Хоргос',
      pointName: 'Точка {{n}}',
      pointCountry: 'Страна точки {{n}}',
      pointDays: 'Срок до точки {{n}}, дней',
      daysPlaceholder: 'дней',
      firstPointStart: 'старт',
      addPoint: 'Добавить точку',
      removePoint: 'Удалить точку {{n}}',
      maxPoints: 'В маршруте не больше {{max}} точек.',
      daysNote:
        'Срок в днях задаёт переход от предыдущей точки. По его истечении позиция груза в работе передвигается автоматически.',
      delete: 'Удалить шаблон маршрута',
      deleteConfirm:
        'Удалить? Плечи, заполненные из него, хранят свою копию и не изменятся.',
      deleteConfirmAction: 'Удалить',
      errors: {
        minPoints: 'В шаблоне маршрута должно быть не меньше двух точек.',
        wholeDays: 'Введите целое число дней.',
      },
    },
  },

  auth: {
    /* Вход в систему (FR-57, Story 1.2). Почта и пароль, без 2FA и SSO в v1.
     *
     * Текст ошибки намеренно ОБЩИЙ: сервер отвечает одинаково независимо от
     * того, существует ли такая почта, чтобы по форме входа нельзя было
     * узнать, у кого есть учётная запись. Отдельной строки «такой почты нет»
     * здесь нет и быть не должно — она вернула бы ровно то, что сервер скрыл. */
    signIn: {
      title: 'Вход',
      subtitle: 'Расчёты, работы и финансы команды ASA.',
      email: 'Почта',
      emailPlaceholder: 'you@asagateway.com',
      password: 'Пароль',
      submit: 'Войти',
      pending: 'Выполняется вход',
      showPassword: 'Показать пароль',
      hidePassword: 'Скрыть пароль',
      failed: 'Почта и пароль не совпадают',
      failedDetail: 'Проверьте оба поля и повторите.',
      throttled: 'Слишком много попыток',
      throttledDetail: 'Вход для этой почты приостановлен на 15 минут.',
      expired: 'Сеанс завершён',
      expiredDetail: 'Войдите снова, чтобы продолжить работу.',
      /* Самостоятельного сброса пароля в v1 нет — его делает Супер-админ. */
      forgotten: 'Забыли пароль? Супер-админ сбросит его для вас.',
    },
    validation: {
      emailRequired: 'Введите почту',
      emailInvalid: 'Введите корректный адрес почты',
      passwordRequired: 'Введите пароль',
    },
    signOut: 'Выйти',

    /* Смена пароля (QW-5). Пароль, выданный администратором, обязательно
     * меняется при первом входе: до этого сервер отказывает во всём, кроме
     * чтения сессии и самой смены. Правило сервера — не короче 8 символов и
     * не совпадает с текущим; подсказка говорит ровно это и ничего больше. */
    changePassword: {
      title: 'Смена пароля',
      subtitleForced:
        'Этот пароль выдал администратор. Задайте свой, чтобы продолжить работу.',
      subtitleVoluntary: 'Задайте новый пароль для своей учётной записи.',
      current: 'Текущий пароль',
      next: 'Новый пароль',
      repeat: 'Повторите новый пароль',
      policy: 'Не короче 8 символов и не совпадает с текущим.',
      submit: 'Сменить пароль',
      pending: 'Сохраняем…',
      errors: {
        currentRequired: 'Введите текущий пароль',
        nextRequired: 'Введите новый пароль',
        repeatRequired: 'Повторите новый пароль',
        mismatch: 'Пароли не совпадают',
        incorrect: 'Текущий пароль неверен',
        tooShort: 'Пароль короче 8 символов',
        tooLong: 'Пароль слишком длинный',
        unchanged: 'Новый пароль совпадает с текущим',
      },
      /* 409: пока человек менял пароль, администратор его сбросил. */
      changedElsewhere: {
        title: 'Пароль изменён в другом месте',
        detail:
          'Пока вы его меняли, администратор выдал новый. Войдите с паролем, который он передал.',
      },
    },
  },

  profile: {
    title: 'Профиль',
    changePassword: 'Сменить пароль',
    subtitle: 'Ваша учётная запись, роль и язык, на котором с вами говорит интерфейс.',
    loading: 'Загрузка профиля',

    photo: {
      change: 'Изменить фото',
      remove: 'Убрать',
      hint: 'JPEG, PNG, WebP или GIF, до 5 МБ.',
      uploading: 'Отправляем фото…',
      failed: 'Фото не сохранено. Повторите.',
      removeFailed: 'Фото не убрано. Повторите.',
      rejected: {
        type: 'Такой файл нельзя использовать как фото. Выберите JPEG, PNG, WebP или GIF.',
        size: 'Фото больше 5 МБ. Выберите файл поменьше.',
      },
    },

    language: {
      title: 'Язык',
      description:
        'Язык интерфейса — только ваш. Язык КП выбирается в самом КП, письма клиенту о статусе уходят на языке клиента, а счета и дебет-ноты всегда на английском.',
      error: 'Язык не изменён. Повторите.',
    },

    access: {
      title: 'Доступ',
      description:
        'Что доступно вашей учётной записи. Роли и права на справочники назначает суперадмин в вашей карточке пользователя в разделе <0>Справочники</0>.',
      noGrants:
        'У вас нет прав на ведение справочников. На доступ вашей роли это не влияет.',
      grants: {
        manageClients: 'Ведёт справочник клиентов',
        manageParticipants: 'Ведёт справочник контрагентов',
        manageTransport: 'Ведёт справочник транспорта',
      },
    },
  },

  /* Глоссарий интерфейса (QW-2, решение владельца 2026-10-08) — страница
     /glossary, ссылка в подвале бокового меню рядом с профилем. Отдельная
     группа каталога, а не таблица в коде, чтобы parity.ts держал русский и
     английский в шаг. У каждого термина три строки: слово, это же слово на
     другом языке интерфейса и что оно значит. Порядок и группы задаёт
     страница (`routes/GlossaryPage.tsx`).

     Глоссарий — правило ИНТЕРФЕЙСА. Бумага и письма внешним сторонам
     говорят словами получателя («перевозка», «КП»), и серверный текст под
     это правило не подпадает. */
  glossary: {
    title: 'Глоссарий',
    subtitle: 'Одно слово — одна вещь. Так интерфейс называет записи, перевозку и деньги.',
    /* Подписи колонок; на телефоне колонки складываются в карточку, и
       подпись «по-английски» остаётся только для скринридера. */
    columns: {
      term: 'Термин',
      other: 'По-английски',
      meaning: 'Что это',
    },
    groups: {
      records: 'Записи',
      movement: 'Перевозка',
      money: 'Деньги',
      people: 'Люди',
      dates: 'Даты',
    },
    terms: {
      quote: {
        term: 'Котировка',
        other: 'Quote',
        meaning:
          'Запись о расчёте для клиента. Так называются её список, страница, команда, статус, чат и история.',
      },
      offer: {
        term: 'КП',
        other: 'Offer',
        meaning:
          'Документ, который получает клиент: «Отправить КП», предпросмотр, то, что «печатается в КП».',
      },
      /* Вкладка настроек «Шаблоны КП» (F1, решение владельца 2026-10-09). */
      offerTemplate: {
        term: 'Шаблон КП',
        other: 'Offer template',
        meaning:
          'Блоки текста, которые печатаются в КП, и заготовки статей затрат, с которых начинается расчёт котировки.',
      },
      /* Состояние расчёта котировки (S-2a, решение владельца 2026-10-10). */
      requestPricing: {
        term: 'Запросить расчёт',
        other: 'Request price',
        meaning:
          'Попросить коллегу, который может считать котировки, назвать цену. Ему приходит уведомление, а котировка ждёт расчёта.',
      },
      pricingRequested: {
        term: 'Ждёт расчёта',
        other: 'Awaiting price',
        meaning:
          'Расчёт запрошен и ещё не готов. Сюда же котировка возвращается, если после готового расчёта изменили груз, маршрут или условия перевозки.',
      },
      pricingReady: {
        term: 'Расчёт готов',
        other: 'Price ready',
        meaning:
          'Цена посчитана, КП можно отправлять. Отмечает тот, кто считал; запросивший получает уведомление, если он в команде котировки или её владелец.',
      },
      /* «Мои задачи» (S-2b.1, решение владельца 2026-10-11; финансы — S-2b.2). */
      myTasks: {
        term: 'Мои задачи',
        other: 'My tasks',
        meaning:
          'Страница с тем, что ждёт вашего действия: котировки и работы, которые вы ведёте, а у финансов — согласования ставок, платежи к подтверждению и сумма к оплате. Каждый видит только свои разделы — те, что его роль может закрыть. У финансов те же разделы стоят и над дашбордом.',
      },
      awaitingClient: {
        term: 'Ждут ответа клиента',
        other: "Awaiting the client's answer",
        meaning:
          'Отправленные КП, которые ещё действуют и ушли не меньше дней назад, чем задано в настройке «Напоминание о повторном обращении» (по умолчанию 3), а ответ клиента не внесён.',
      },
      case: {
        term: 'Работа',
        other: 'Case',
        meaning: 'Запись о перевозке, которую ведём после того, как клиент принял КП. Клиенту она — «перевозка».',
      },
      /* S-13: движение груза и слово клиента; запись о нём — «Работа». */
      shipment: {
        term: 'Перевозка',
        other: 'Shipment',
        meaning:
          'Само движение груза и слово, которым его называет клиент: письма клиенту и КП говорят «перевозка». Запись о ней в интерфейсе — «Работа».',
      },
      leg: {
        term: 'Плечо',
        other: 'Leg',
        meaning: 'Одно перемещение груза в работе: откуда, куда и каким видом транспорта.',
      },
      route: {
        term: 'Маршрут',
        other: 'Route',
        meaning:
          'Путь груза. В списках и в шапке — откуда и куда, на плече — цепочка его точек («Точки маршрута»). Не путать с вариантом доставки в котировке и с шаблоном маршрута.',
      },
      /* S-13 (решение владельца 2026-10-14): просчитанная альтернатива в
         котировке, раньше «вариант маршрута». */
      deliveryOption: {
        term: 'Вариант доставки',
        other: 'Delivery option',
        meaning:
          'Один из просчитанных для клиента способов довезти груз: маршрут, вид транспорта и срок. Клиент выбирает один, и статьи затрат считаются под выбранный.',
      },
      routeTemplate: {
        term: 'Шаблон маршрута',
        other: 'Route template',
        meaning:
          'Готовая цепочка точек из настроек. При применении точки копируются в плечо, поэтому правка шаблона потом плечо не меняет.',
      },
      truck: {
        term: 'Фура',
        other: 'Truck',
        meaning: 'Одна машина на плече — со своим госномером, перевозчиком и ставкой.',
      },
      plate: {
        term: 'Госномер',
        other: 'Plate',
        meaning: 'Регистрационный номер фуры.',
      },
      /* S-18b: что везёт плечо, вид за видом — у каждой единицы свой номер,
         свой перевозчик и своя ставка. */
      unit: {
        term: 'Единица плеча',
        other: 'Unit',
        meaning:
          'То, что везёт плечо и за что платят перевозчику: фура, контейнер, вагон, авианакладная, коносамент или другое. У каждой свой номер, свой перевозчик, своя ставка и свой остаток.',
      },
      container: {
        term: 'Контейнер',
        other: 'Container',
        meaning: 'Единица плеча с номером контейнера, пломбой и типом (например 40HC). Не путать с номером контейнера у фуры.',
      },
      wagon: {
        term: 'Вагон',
        other: 'Wagon',
        meaning: 'Единица ж/д-плеча с номером вагона; ЗПУ (пломба) и тип — по желанию.',
      },
      awb: {
        term: 'Авианакладная',
        other: 'Air waybill',
        meaning: 'Единица авиаплеча — накладная с номером и весом груза.',
      },
      bl: {
        term: 'Коносамент',
        other: 'Bill of lading',
        meaning: 'Единица морского плеча — коносамент или букинг, по номеру.',
      },
      carrier: {
        term: 'Перевозчик',
        other: 'Carrier',
        meaning: 'Компания, которая везёт груз на плече и которой мы за это платим.',
      },
      rate: {
        term: 'Ставка',
        other: 'Rate',
        meaning: 'Сумма, согласованная с клиентом по работе. Стоит в шапке работы.',
      },
      carrierRate: {
        term: 'Ставка перевозчика',
        other: 'Carrier rate',
        meaning: 'Сумма, согласованная с перевозчиком за фуру, контейнер или другую единицу плеча.',
      },
      clientPrice: {
        term: 'Цена клиенту',
        other: 'Client price',
        meaning: 'Сумма, которую рассчитала котировка: себестоимость плюс наценка.',
      },
      /* S-5 (решения владельца O1, O6): одна наценка на котировку и пара
         слов, которые расчёт показывает вместе. */
      markup: {
        term: 'Наценка',
        other: 'Markup',
        meaning:
          'Процент, который котировка добавляет к себестоимости каждой строки: цена клиенту — это себестоимость, умноженная на единицу плюс наценку. Одна на котировку, задаётся кнопкой «Применить» в расчёте; строка, добавленная или изменённая потом, получает её же.',
      },
      margin: {
        term: 'Маржа',
        other: 'Margin',
        meaning:
          'Сколько котировка зарабатывает: цена клиенту минус себестоимость. В процентах считается от цены клиенту, поэтому наценка 15% даёт маржу 13.04%.',
      },
      expense: {
        term: 'Затрата',
        other: 'Expense',
        meaning:
          'Деньги, которые работа тратит: плановые (расчётные) и фактические. «Затраты · прогноз» в шапке работы — фактические затраты там, где они уже есть, и плановые по строкам, где факта ещё нет.',
      },
      /* Одно слово для трёх цифр (S-6, решение владельца 2026-10-12): вид
         цифры называет уточнение, а не отдельное название. */
      profit: {
        term: 'Прибыль',
        other: 'Profit',
        meaning:
          'Сколько зарабатывает работа. План — ставка минус плановые затраты. Прогноз — ставка вместе с доп. счетами и дебет-нотами минус затраты по факту и плановые затраты по строкам, где факта ещё нет. Итог — полученные и разнесённые деньги минус оплаченные; он заменяет прогноз, когда работа финансово закрыта или отменена.',
      },
      /* Плитка дашборда (Story B7). Определение — docs/llm-wiki/components/
         money-and-fx.md#turnover. */
      turnover: {
        term: 'Оборот за месяц',
        other: 'Turnover this month',
        meaning:
          'Сколько выставлено клиентам с начала месяца: выпущенные счета и дебет-ноты клиенту, в долларах, без НДС. С «К получению» не сходится — там НДС включён.',
      },
      payment: {
        term: 'Платёж',
        other: 'Payment',
        meaning:
          'Деньги, которые пришли от клиента или ушли перевозчику и другим сторонам: платёж бывает входящим или исходящим.',
      },
      team: {
        term: 'Команда',
        other: 'Team',
        meaning:
          'Коллеги, которые ведут котировку или работу. Участие в команде открывает доступ к записи.',
      },
      /* S-1 (2026-10-10): обязательный выбор при создании работы из котировки. */
      operationsOwner: {
        term: 'Ответственный (Операции)',
        other: 'Operations owner',
        meaning:
          'Сотрудник операций, который ведёт работу. Выбирается при создании работы из котировки и входит в её команду.',
      },
      /* Выведенный термин (S-22a): у работы и котировки больше нет списка
         участников. Строка остаётся, чтобы старое слово в чате или в старой
         записи истории находило объяснение. */
      participants: {
        term: 'Участники',
        other: 'Participants',
        meaning:
          'Больше не используется: у работы и котировки нет списка участников. Компания или человек, оказывающие услугу, — это «Контрагент»; он указывается там, где действует: на плече, фуре, строке затрат или платеже.',
      },
      counterAgent: {
        term: 'Контрагент',
        other: 'Counter-agent',
        meaning:
          'Компания или человек, оказывающие нам услугу: перевозчик, экспедитор, таможенный брокер, водитель, агент или прочее. Все контрагенты — в разделе «Контрагенты». В финансах так же называют вторую сторону расчёта — клиента или контрагента.',
      },
      etd: {
        term: 'ETD',
        other: 'ETD',
        meaning: 'Плановая дата отправления.',
      },
      eta: {
        term: 'ETA',
        other: 'ETA',
        meaning: 'Плановая дата прибытия.',
      },
    },
    /* Ссылка в подвале бокового меню. */
    navLabel: 'Глоссарий',
  },

  notFound: {
    title: 'Не найдено',
    heading: 'Страница недоступна',
    description:
      'Ссылка могла устареть, либо запись принадлежит другой команде.',
  },

  errors: {
    forbidden: {
      title: 'Раздел недоступен для вашей роли',
      detail: 'Обратитесь к суперадмину, если нужен доступ.',
    },
    notFound: {
      title: 'Запись недоступна',
      detail: 'Она могла быть удалена либо принадлежит другой команде.',
    },
    server: {
      title: 'Сервер не ответил',
      detail: 'Данные не изменены. Повторите через минуту.',
    },
    network: {
      title: 'Не удалось загрузить данные',
      detail: 'Проверьте соединение и повторите.',
    },
    /* Сбой самого интерфейса, а не запроса. В отличие от `server` выше, здесь
       НЕЛЬЗЯ писать «данные не изменены»: упавший рендер ничего не знает о
       том, что дошло до сервера, а обещание, которое мы не можем подтвердить,
       хуже молчания. Говорим, что делать. */
    crash: {
      title: 'Что-то пошло не так',
      heading: 'Не удалось показать этот экран',
      detail:
        'Повторите попытку или обновите страницу. Если повторяется — передайте это администратору и опишите, что вы делали.',
      reload: 'Обновить страницу',
      details: 'Технические детали',
    },
    validation: {
      detail: 'Проверьте значения и повторите.',
    },
    /* Отказ по FR-64, в двух видах, для трёх листов, которым он может прийти:
       КП, счёт или дебет-нота по делу и счёт клиенту. Одна формулировка, а не
       три: подлежащее фразы — ЮРЛИЦО и экран, где это чинится, а не бумага, в
       которой отказали. Назвать документ — значит написать три фразы ради
       одного действия, и читатель и так смотрит на тот лист, который не прошёл.
       Сервер называет поля, которые у юрлица не заполнены, поэтому обычная
       фраза — та, что их перечисляет: «банковские реквизиты» и «юридический
       футер» — это два разных вечера в настройках, а одна фраза на оба
       случая называла футер там, где с футером всё было в порядке.
       Фраза без перечисления остаётся на случай, когда полей в отказе не
       было: старый сервер или ключ поля, слов для которого в этом каталоге
       нет. Она всё равно ведёт в настройки — чинить всё равно там, — и
       намеренно ничего не называет. */
    branding: {
      incomplete: 'У юрлица не заполнены реквизиты документов — без них PDF не выпускается. Супер-админ может заполнить их в Настройках → Юрлица.',
      incompleteFields: 'У юрлица не заполнено: {{fields}}. Без этого PDF не выпускается — заполнить может супер-админ в Настройках → Юрлица.',
      /* Имена полей приходят машинными ключами (`legal-footer`), слова — наши
         (AD-27). Именительный падеж: из них собирается перечисление после
         двоеточия, и в любом другом падеже список пришлось бы согласовывать с
         фразой, которая его держит. */
      field: {
        name: 'название',
        logo: 'логотип',
        legalFooter: 'юридический футер',
        bankDetails: 'банковские реквизиты',
      },
    },
    /* Отказ `rate-missing` с именами (S-4.1, доработки): сервер называет
       валюту и день, на который курса нет. Сегодняшний курс подтянется сам в
       течение часа; курс за прошлую дату сам не появится никогда — его вносит
       администратор. Две разные подсказки, поэтому две фразы. */
    rateMissing: {
      today:
        'Курс {{currency}} на сегодня ещё не загружен — курсы загружаются автоматически в течение часа. Повторите позже.',
      past: 'Нет курса {{currency}} на {{date}} — без него сумму не пересчитать в USD. Обратитесь к администратору: курс за прошлую дату вносится вручную.',
    },
    /* По одной записи на каждый суффикс из реестра сервера
       (platform/httpx/problem.go). Суффикс — это контракт; формулировки наши,
       потому что title и detail в problem-документе написаны для разработчика,
       читающего лог (AD-22, AD-27). */
    problems: {
      validationFailed: {
        title: 'Некоторые значения требуют внимания',
        detail: 'Выделенные поля не приняты.',
      },
      authRequired: {
        title: 'Сессия завершена',
        detail: 'Войдите снова, чтобы продолжить. Введённое не отправлено.',
      },
      forbidden: {
        title: 'Недоступно для вашей роли',
        detail: 'Обратитесь к супер-администратору, если нужен доступ.',
      },
      notFound: {
        title: 'Запись недоступна',
        detail: 'Возможно, она удалена или принадлежит другой команде.',
      },
      conflict: {
        title: 'Изменение конфликтует с записью',
        detail: 'Обновите страницу, чтобы увидеть текущее состояние, и повторите.',
      },
      idempotencyConflict: {
        title: 'Это уже было отправлено',
        detail: 'Действует предыдущая отправка. Обновите страницу.',
      },
      /* Сегодня этот отказ приходит только при создании работы по котировке. */
      issuingEntityInactive: {
        title: 'Юрлицо отключено',
        detail:
          'Юрлицо этой котировки отключено — включите его в настройках, чтобы создать работу.',
      },
      /* Без валюты и даты в отказе — общая фраза; с ними — errors.rateMissing. */
      rateMissing: {
        title: 'Нет курса валюты',
        detail: 'Нет курса валюты на нужную дату — без него сумму не пересчитать в USD.',
      },
      /* S-10: фуры плеча сохранил кто-то другой после того, как страница их
         прочитала; ничего не записано. */
      legUnitsChanged: {
        title: 'Единицы плеча уже изменены',
        detail: 'Единицы плеча уже изменил кто-то другой — обновите страницу',
      },
      /* S-21: разнесение упёрлось в сумму платежа или в остаток документа;
         с полями отказа его называет лист платежа клиента. */
      allocationOverCeiling: {
        title: 'Сумма больше остатка',
        detail: 'Разнесено больше, чем остаётся по платежу или по документу. Ничего не записано.',
      },
      /* S-21: транш уже заменён выставленным счётом. */
      allocationTargetClosed: {
        title: 'Транш заменён счётом',
        detail: 'Этот транш уже выставлен счётом — разнесите платёж на счёт.',
      },
      /* S-21: у работы не указан клиент. */
      caseHasNoClient: {
        title: 'У работы нет клиента',
        detail: 'Укажите клиента в карточке работы, чтобы внести его платёж.',
      },
      /* QW-5: учётная запись ещё держит пароль, выданный администратором.
         RequireSession сразу переводит на страницу смены, поэтому этот текст
         виден только мгновение. */
      passwordChangeRequired: {
        title: 'Сначала смените пароль',
        detail: 'Задайте свой пароль, чтобы продолжить работу.',
      },
      rateLimited: {
        title: 'Слишком много попыток',
        detail: 'Подождите немного и повторите.',
      },
      payloadTooLarge: {
        title: 'Слишком большой объём',
        detail: 'Уменьшите размер и повторите.',
      },
      unsupportedMediaType: {
        title: 'Такой тип файла не принимается',
        detail: 'Используйте один из поддерживаемых форматов.',
      },
      unavailable: {
        title: 'Здесь это недоступно',
        detail: 'Функции нужен сервис, который не настроен. Обратитесь к администратору.',
      },
      internal: {
        title: 'Сервер не смог ответить',
        detail: 'Ничего не изменено. Повторите через минуту.',
      },
      unknown: {
        title: 'Что-то пошло не так',
        detail: 'Повторите попытку. Если повторяется — сообщите супер-администратору.',
      },
    },

    /* Строка состояния соединения. Постоянная, не всплывающая: уведомление,
       которое исчезает при потере связи, хуже, чем ничего (UX-DR-18). */
    connection: {
      offline: 'Нет соединения. Введённое сохранено на экране и не отправлено.',
      restored: 'Соединение восстановлено.',
      retry: 'Повторить',
    },
    requestFailed: 'Запрос не выполнен',
  },
} as const
